import './style.css'
import './styles/tokens.css'
import './styles/base.css'
import './styles/components/button.css'
import './styles/components/field.css'
import './styles/components/page.css'
import './styles/components/theme-toggle.css'
import { initTheme, resolveTheme } from './lib/theme'

// Tema claro/oscuro (el primer paint lo resuelve el script inline del <head>)
initTheme()

// ===== Field system: mensajes de error accesibles =====
function fieldMessage(inputId: string, msg: string) {
  const input = document.getElementById(inputId) as HTMLInputElement | null
  if (!input) return
  const field = input.closest('.field') as HTMLElement | null
  if (field) field.classList.toggle('field--invalid', !!msg)
  if (msg) input.setAttribute('aria-invalid', 'true')
  else input.removeAttribute('aria-invalid')
  const el = document.getElementById(inputId + 'Msg')
  if (el) el.textContent = msg
}

function clearFormErrors(form: HTMLFormElement | null) {
  if (!form) return
  form.querySelectorAll<HTMLElement>('.field--invalid').forEach(f => f.classList.remove('field--invalid'))
  form.querySelectorAll('[aria-invalid="true"]').forEach(el => el.removeAttribute('aria-invalid'))
  form.querySelectorAll<HTMLElement>('.field__msg').forEach(el => {
    if (el.dataset.hint === undefined) el.dataset.hint = el.textContent || ''
    el.textContent = el.dataset.hint
  })
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/

// Enlaces internos de vuelta (páginas de detalle): no recargan el documento
document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('[data-back]') as HTMLAnchorElement | null
  if (!a) return
  e.preventDefault()
  go('/' + (a.dataset.back || 'dashboard'))
})

// Mostrar/ocultar contraseña en cualquier campo con data-pw-toggle
document.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest('[data-pw-toggle]') as HTMLElement | null
  if (!btn) return
  const input = document.getElementById(btn.dataset.pwToggle || '') as HTMLInputElement | null
  if (!input) return
  const show = input.type === 'password'
  input.type = show ? 'text' : 'password'
  const icon = btn.querySelector('i')
  if (icon) icon.className = show ? 'fas fa-eye-slash' : 'fas fa-eye'
  btn.setAttribute('aria-label', show ? 'Ocultar contraseña' : 'Mostrar contraseña')
})

if (import.meta.env.PROD && !String(import.meta.env.VITE_API_URL || '').includes('-dev')) {
  const noop = () => {}
  console.log = noop
  console.info = noop
  console.debug = noop
  console.warn = noop
}

// ===== CONFIG =====
const API_BASE = import.meta.env.VITE_API_URL || ''
let token = ''

declare const __APP_BUILD__: string

// ===== PWA Auto-update =====
;(() => {
  if (sessionStorage.getItem('pwa_updated')) {
    sessionStorage.removeItem('pwa_updated')
    setTimeout(() => toast('App actualizada a la última versión', 'ok'), 1000)
  }
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      sessionStorage.setItem('pwa_updated', '1')
    })
  }
})()
function setToken(t: string) { token = t; localStorage.setItem('sp_token', t) }
function clearToken() { token = ''; localStorage.removeItem('sp_token'); localStorage.removeItem('sp_refresh'); localStorage.removeItem('sp_token_local'); localStorage.removeItem('sp_user') }

let _refreshing: Promise<string | null> | null = null
async function refreshAccessToken(): Promise<string | null> {
  if (_refreshing) return _refreshing
  _refreshing = _doRefresh()
  try { return await _refreshing } finally { _refreshing = null }
}
// true cuando el último refresh falló por red/servidor (no por credenciales):
// en ese caso NO se cierra la sesión, se conserva la cacheada.
let _refreshOffline = false
async function _doRefresh(): Promise<string | null> {
  _refreshOffline = false
  const rt = localStorage.getItem('sp_refresh')
  if (!rt) return null
  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: rt }) })
    if (res.status >= 500) { _refreshOffline = true; return null }
    const data = await res.json()
    if (res.ok && data.success) {
      token = data.token
      localStorage.setItem('sp_token', data.token)
      localStorage.setItem('sp_refresh', data.refreshToken)
      return data.token
    }
  } catch { _refreshOffline = true }
  return null
}

// true si el JWT de acceso ya venció (o vence en <30 s). Sin red: se decide localmente.
function tokenExpired(t: string): boolean {
  try {
    const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return typeof p.exp === 'number' && p.exp * 1000 < Date.now() + 30_000
  } catch { return false }
}

async function req(method: string, path: string, body?: any) {
  const url = `${API_BASE}${path}`
  // Si hay un refresh en curso (p. ej. el del arranque), esperar el token nuevo
  // en vez de disparar la petición con el vencido y pagar un 401 + reintento.
  if (_refreshing && path !== '/api/v1/auth/refresh') await _refreshing.catch(() => null)
  const headers: Record<string,string> = { 'Content-Type':'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const opts: RequestInit = { method, headers }
  if (body) opts.body = JSON.stringify(body)
  try {
    const res = await fetch(url, opts)
    if (res.status === 401 && path !== '/api/v1/auth/refresh' && path !== '/api/v1/auth/login' && path !== '/api/v1/auth/register/verify-otp' && !path.includes('/forgot-password/')) {
      const newToken = await refreshAccessToken()
      if (newToken) {
        const retryOpts: RequestInit = { method, headers: { 'Content-Type':'application/json', 'Authorization': `Bearer ${newToken}` } }
        if (body) retryOpts.body = JSON.stringify(body)
        const retry = await fetch(url, retryOpts)
        const data = await retry.json()
        return { ok: retry.ok, status: retry.status, data }
      }
      // Sin red / servidor caído: no expulsar al usuario (botón de pánico)
      if (_refreshOffline) return { ok: false, status: 0, data: null, error: 'Network error: refresh' }
      // Refresh rechazado — tokens inválidos, forzar relogin
      clearToken(); localStorage.removeItem('sp_user'); CU=null
      disconnectAlertHub()
      document.getElementById('APP')!.style.display='none'
      showLanding()
      return { ok: false, status: 401, data: null }
    }
    const data = await res.json()
    return { ok: res.ok, status: res.status, data }
  } catch (e: any) {
    return { ok: false, status: 0, data: null, error: 'Network error: ' + (e?.message || '') }
  }
}
const api = {
  login: (e:string,p:string,turnstile_token?:string)=> req('POST','/api/v1/auth/login',{email:e,password:p,turnstile_token}),
  register: (payload:any)=> req('POST','/api/v1/auth/register',payload),
  registerSendOtp: (phone:string, email:string, turnstile_token?:string)=> req('POST','/api/v1/auth/register/send-otp',{phone, email, turnstile_token}),
  registerVerifyOtp: (email:string, code:string, phone:string)=> req('POST','/api/v1/auth/register/verify-otp',{email, code, phone}),
  me: ()=> req('POST','/api/v1/auth/me'),
  refresh: (refreshToken:string)=> req('POST','/api/v1/auth/refresh',{refreshToken}),
  getUsers: ()=> req('GET','/api/v1/users'),
  createUser: (p:any)=> req('POST','/api/v1/users',p),
  updateUser: (id:string,p:any)=> req('PUT',`/api/v1/users/${id}`,p),
  toggleUser: (id:string)=> req('PATCH',`/api/v1/users/${id}/toggle`),
  deleteUser: (id:string)=> req('DELETE',`/api/v1/users/${id}`),
  getAlerts: (qs='')=> req('GET',`/api/v1/alerts${qs}`),
  createAlert: (p:any)=> req('POST','/api/v1/alerts',p),
  resolveAlert: (id:string)=> req('POST',`/api/v1/alerts/${id}/resolve`),
  cancelAlert: (id:string)=> req('POST',`/api/v1/alerts/${id}/cancel`),
  getDevices: ()=> req('GET','/api/v1/devices'),
  getContacts: ()=> req('GET','/api/v1/contacts'),
  createContact: (p:any)=> req('POST','/api/v1/contacts',p),
  updateContact: (id:string,p:any)=> req('PUT',`/api/v1/contacts/${id}`,p),
  toggleContact: (id:string)=> req('PATCH',`/api/v1/contacts/${id}/toggle`),
  deleteContact: (id:string)=> req('DELETE',`/api/v1/contacts/${id}`),
  createDevice: (p:any)=> req('POST','/api/v1/devices',p),
  deleteDevice: (id:string)=> req('DELETE',`/api/v1/devices/${id}`),
  patchDevice: (id:string,p:any)=> req('PATCH',`/api/v1/devices/${id}/status`,p),
  updateDevice: (id:string,p:any)=> req('PUT',`/api/v1/devices/${id}`,p),
  regenerateToken: (id:string)=> req('POST',`/api/v1/iot/devices/${id}/regenerate-token`),
  sendDeviceCommand: (id:string,action:string,params?:any)=> req('POST',`/api/v1/iot/devices/${id}/command`,{action,params}),
  getGroupDevices: (gid:string)=> req('GET',`/api/v1/devices/group/${gid}`),
  getNotifications: ()=> req('GET','/api/v1/notifications'),
  updateNotifications: (p:any)=> req('PUT','/api/v1/notifications',p),
  getAnalytics: ()=> req('GET','/api/v1/analytics/dashboard'),
  getAdminStats: ()=> req('GET','/api/v1/analytics/admin'),
  getEventLog: (qs?:string)=> req('GET',`/api/v1/analytics/events${qs||''}`),
  logEvent: (event:string, entityType:string, entityId:string|null, description:string, metadata?:any)=> req('POST','/api/v1/analytics/events',{event,entity_type:entityType,entity_id:entityId,description,metadata}),
  getMapData: (qs?:string)=> req('GET',`/api/v1/map/data${qs||''}`),
  getPlans: ()=> req('GET','/api/v1/plans'),
  createPlan: (p:any)=> req('POST','/api/v1/plans',p),
  updatePlan: (id:string,p:any)=> req('PUT',`/api/v1/plans/${id}`,p),
  deletePlan: (id:string)=> req('DELETE',`/api/v1/plans/${id}`),
  getSiteContent: (section?: string)=> req('GET', `/api/v1/site-content${section ? `?section=${encodeURIComponent(section)}` : ''}`),
  createSiteContent: (p:any)=> req('POST','/api/v1/site-content',p),
  updateSiteContent: (id:string,p:any)=> req('PUT',`/api/v1/site-content/${id}`,p),
  deleteSiteContent: (id:string)=> req('DELETE',`/api/v1/site-content/${id}`),
  getProducts: (category?: string, type?: string)=> {
    const params = new URLSearchParams()
    if (category) params.set('category', category)
    if (type) params.set('type', type)
    const qs = params.toString()
    return req('GET', `/api/v1/products${qs ? `?${qs}` : ''}`)
  },
  getProductsAdmin: ()=> req('GET', '/api/v1/products/admin'),
  getProductPrice: (code:string)=> req('GET', `/api/v1/products/${code}/price`),
  createProduct: (p:any)=> req('POST','/api/v1/products',p),
  updateProduct: (id:string,p:any)=> req('PUT',`/api/v1/products/${id}`,p),
  deleteProduct: (id:string)=> req('DELETE',`/api/v1/products/${id}`),
  uploadProductImage: (productId:string, file:File)=> {
    const url = `${API_BASE}/api/v1/products/${productId}/images`
    const fd = new FormData()
    fd.append('image', file)
    const h: Record<string,string> = {}
    if(token) h['Authorization'] = `Bearer ${token}`
    return fetch(url, { method:'POST', headers:h, body:fd }).then(async r => { const d = await r.json(); return { ok: r.ok, status: r.status, data:d } })
  },
  deleteProductImage: (productId:string, imageId:string)=> req('DELETE', `/api/v1/products/${productId}/images/${imageId}`),
  reorderProductImages: (productId:string, imageIds:string[])=> req('PUT', `/api/v1/products/${productId}/images/reorder`, { imageIds }),
  quotePlan: (id:string,p:any)=> req('POST',`/api/v1/plans/${id}/quote`,p),
  getAlertNotifications: (id:string)=> req('GET',`/api/v1/alerts/${id}/notifications`),
  exportAlerts: (qs:string)=> { const u=`${API_BASE}/api/v1/alerts/export/csv${qs}`; window.open(u,'_blank') },
   // Billing / Mercado Pago
   getBillingStatus: ()=> req('GET','/api/v1/billing/status'),
   getBillingHistory: ()=> req('GET','/api/v1/billing/history'),
   subscribeToPlan: (planId:string, successUrl?:string)=> req('POST','/api/v1/billing/subscribe',{plan_id:planId, success_url:successUrl}),
   cancelSubscription: ()=> req('POST','/api/v1/billing/cancel'),
   testEvo: (p:any)=> req('POST','/api/v1/notifications/test-evolution',p),
  deleteAccount: ()=> req('DELETE','/api/v1/auth/me'),
  logoutAll: ()=> req('POST','/api/v1/auth/logout-all'),
  sendOtp: ()=> req('POST','/api/v1/auth/send-otp'),
  verifyOtp: (code:string,phone?:string)=> req('POST','/api/v1/auth/verify-otp',{code,phone}),
  forgotPasswordSendOtp: (email:string, turnstile_token?:string)=> req('POST','/api/v1/auth/forgot-password/send-otp',{email, turnstile_token}),
  forgotPasswordReset: (email:string, code:string, password:string)=> req('POST','/api/v1/auth/forgot-password/reset',{email, code, password}),
  forceReset: (token:string, password:string)=> req('POST','/api/v1/auth/force-reset',{token, password}),
  sendWelcome: (id:string)=> req('POST',`/api/v1/users/${id}/send-welcome`),
  get2faSetup: ()=> req('GET','/api/v1/2fa/setup'),
  verify2fa: (code:string)=> req('POST','/api/v1/2fa/verify',{code}),
  disable2fa: (code:string)=> req('POST','/api/v1/2fa/disable',{code}),
  get2faBackupCodes: ()=> req('GET','/api/v1/2fa/backup-codes'),
  get2faStatus: ()=> req('GET','/api/v1/2fa/status'),
  check2faLogin: (code:string)=> req('POST','/api/v1/2fa/login-check',{code}),
  getGroups: ()=> req('GET','/api/v1/groups'),
  getGroup: (id:string)=> req('GET',`/api/v1/groups/${id}`),
  createGroup: (p:any)=> req('POST','/api/v1/groups',p),
  updateGroup: (id:string,p:any)=> req('PUT',`/api/v1/groups/${id}`,p),
  deleteGroup: (id:string)=> req('DELETE',`/api/v1/groups/${id}`),
  addGroupMember: (id:string,uid:string)=> req('POST',`/api/v1/groups/${id}/members`,{user_id:uid}),
  removeGroupMember: (id:string,uid:string)=> req('DELETE',`/api/v1/groups/${id}/members/${uid}`),
  getExtContacts: (gid?:string)=> req('GET','/api/v1/external-contacts'+(gid?`?group_id=${gid}`:'')),
  createExtContact: (p:any)=> req('POST','/api/v1/external-contacts',p),
  updateExtContact: (id:string,p:any)=> req('PUT',`/api/v1/external-contacts/${id}`,p),
  deleteExtContact: (id:string)=> req('DELETE',`/api/v1/external-contacts/${id}`),
  getEmergencyNumbers: (groupId?:string)=> req('GET','/api/v1/emergency-numbers'+(groupId?`?group_id=${groupId}`:'')),
  createEmergencyNumber: (p:any)=> req('POST','/api/v1/emergency-numbers',p),
  updateEmergencyNumber: (id:string,p:any)=> req('PUT',`/api/v1/emergency-numbers/${id}`,p),
  deleteEmergencyNumber: (id:string)=> req('DELETE',`/api/v1/emergency-numbers/${id}`),
  bulkDeleteAlerts: (p:any)=> req('POST','/api/v1/alerts/bulk-delete',p),
  resendExtConfirm: (id:string)=> req('POST',`/api/v1/external-contacts/${id}/resend`),
  getSharedAlert: (token:string)=> req('GET',`/api/v1/alerts/shared/${token}`),
    getRetentionPolicy: ()=> req('GET','/api/v1/retention/policy'),
  updateRetentionPolicy: (p:any)=> req('PUT','/api/v1/retention/policy',p),
  purgeData: ()=> req('POST','/api/v1/retention/purge'),
  getComplianceReport: ()=> req('GET','/api/v1/retention/compliance'),
  generateGroupInvite: (id:string, role?:string, inviterName?:string)=> req('POST',`/api/v1/groups/${id}/invite`,{role,inviter_name:inviterName}),
  getGroupInvite: (id:string)=> req('GET',`/api/v1/groups/${id}/invite`),
  revokeGroupInvite: (id:string)=> req('DELETE',`/api/v1/groups/${id}/invite`),
  resolveInvite: (token:string)=> req('GET',`/api/v1/invite/${token}`),
  submitQuote: (payload:any)=> req('POST','/api/v1/quotes',payload),
  getQuotes: (status?:string)=> req('GET',`/api/v1/quotes${status?`?status=${encodeURIComponent(status)}`:''}`),
  getQuote: (id:string)=> req('GET',`/api/v1/quotes/${id}`),
  updateQuote: (id:string,payload:any)=> req('PATCH',`/api/v1/quotes/${id}`,payload),
  // Orders
  checkout: (payload:any)=> req('POST','/api/v1/orders/checkout',payload),
  getOrders: ()=> req('GET','/api/v1/orders'),
  getOrder: (id:string)=> req('GET',`/api/v1/orders/${id}`),
  googleLogin: (credential:string)=> req('POST','/api/v1/auth/google',{credential}),
}

// ===== LOCAL FALLBACK DB =====
const DBK='domus-secura_v2'
function db(){try{return JSON.parse(localStorage.getItem(DBK)!)}catch(e){return null}}
function dbSave(d:any){localStorage.setItem(DBK,JSON.stringify(d))}
function initDB(){
  if(localStorage.getItem(DBK))return
  dbSave({users:[],alerts:[],devices:[],ncfg:{whatsapp:true,waNums:'',call:true,callNum:'',sound:true,vibration:true,cooldown:30,evoUrl:'',evoKey:''}})
}

// ===== WEBSOCKET LIVE DATA =====
let wsConn: WebSocket | null = null
let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null
let wsGroupId: string | null = null
let wsGeneration = 0
const WS_RECONNECT_MS = 5000

function _updateDeviceStatus(deviceId: string, status: string) {
  const card = document.querySelector(`[data-iot-device-id="${deviceId}"]`) as HTMLElement | null
  if (!card) return
  const badge = card.querySelector('.ds') as HTMLElement | null
  if (badge) {
    badge.className = `ds ${status}`
    badge.textContent = status === 'online' ? 'En linea' : 'Desconectado'
  }
}

// Indicador de conexión del dispositivo (dashboard): punto + texto
function devStatusInner(status: string) {
  return `<span class="dh-dot" aria-hidden="true"></span>${status === 'online' ? 'En línea' : 'Desconectado'}`
}
function _updateDashboardDeviceStatus(deviceId: string, status: string) {
  // Update status badge
  const badge = document.querySelector(`.dh-status[data-device-id="${deviceId}"]`) as HTMLElement | null
  if (badge) {
    badge.className = `dh-status ${status}`
    badge.innerHTML = devStatusInner(status)
  }
  // Update relay button disabled state
  const relayBtns = document.querySelectorAll(`.relay-btn[data-device="${deviceId}"]`)
  relayBtns.forEach((btn) => {
    (btn as HTMLButtonElement).disabled = status !== 'online'
  })
}

function connectAlertHub(groupId: string) {
  if (!groupId || !API_BASE) return
  // Idempotente: si ya hay conexion viva (o en curso) al mismo grupo, no reconectar.
  // Sin esto, el boot (enterApp + me() + selector) abria 2-3 sockets y cerraba
  // los anteriores a mitad del handshake ("closed before the connection...").
  if (wsConn && wsGroupId === groupId &&
      (wsConn.readyState === WebSocket.OPEN || wsConn.readyState === WebSocket.CONNECTING)) return
  // Cerrar conexion anterior (de otro grupo o muerta)
  if (wsConn) { try { wsConn.close() } catch {} wsConn = null }
  if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null }

  const myGeneration = ++wsGeneration
  wsGroupId = groupId
  const wsUrl = API_BASE.replace(/^http/, 'ws') + `/ws/alerts?groupId=${encodeURIComponent(groupId)}`
  console.log('[WS] connecting to', wsUrl)
  const ws = new WebSocket(wsUrl)
  wsConn = ws

  ws.onopen = () => {
    console.log('[WS] connected to AlertHub')
    toast('Conectado a live data','ok')
  }
  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data)
      console.log('[WS] message:', msg)
      if (msg.type === 'new_alert') {
        handleLiveAlert(msg.alert)
      } else if (msg.type === 'device_status') {
        console.log('[WS] device status:', msg.device_id, msg.status, msg.name)
        if (CP === 'dashboard') _updateDashboardDeviceStatus(msg.device_id, msg.status)
        if (CP === 'iot') _updateDeviceStatus(msg.device_id, msg.status)
      }
    } catch (e) {
      console.error('[WS] parse error:', e)
    }
  }
  ws.onclose = () => {
    // Ignorar cierres de sockets viejos ya reemplazados (evita reconexiones
    // fantasma que mataban la conexion vigente cada 5s)
    if (myGeneration !== wsGeneration) return
    if (wsConn === ws) wsConn = null
    // Cierre intencional (logout / disconnect): no reconectar
    if (wsGroupId === null) return
    console.log('[WS] closed, reconnecting in', WS_RECONNECT_MS, 'ms')
    wsReconnectTimer = setTimeout(() => connectAlertHub(groupId), WS_RECONNECT_MS)
  }
  ws.onerror = (err) => {
    console.error('[WS] error:', err)
  }
}

function disconnectAlertHub() {
  wsGeneration++ // invalida los onclose pendientes de sockets viejos
  wsGroupId = null // marca el cierre como intencional (onclose no reconecta)
  if (wsConn) { try { wsConn.close() } catch {} wsConn = null }
  if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null }
}

function handleLiveAlert(alert: any) {
  // Actualizar badge de alertas
  const navBdg = document.getElementById('navBdg')
  if (navBdg) {
    const current = parseInt(navBdg.textContent || '0')
    navBdg.textContent = String(current + 1)
    navBdg.style.display = 'inline-block'
  }
  // Toast inmediato
  const typeNames: Record<string,string> = { security: 'Seguridad', medical: 'Alerta Medica', fire: 'Bomberos' }
  toast(`🚨 Nueva alerta ${typeNames[alert.type] || alert.type} — ${alert.user_name}`, 'er')
  // Refrescar dashboard/alerts si estamos en esas vistas
  if (CP === 'dashboard') renderDash()
  if (CP === 'alerts') renderAlerts()
  if (CP === 'map') renderMap()
}

// ===== STATE =====
let CU: any = null // current user
let CP = 'dashboard'
let MAP_FILTER = 'all'
let MAP_PERIOD = 'all'
let MAP_SELECTED: Set<string> = new Set()
let mapInst: any = null
let mapMarkers: any[] = []
let _heatLayer: any = null
let AT: any = null, ASec = 0, AId: string|null = null
let ULoc = {lat:10.4912,lng:-66.8798,accuracy:0}
let API_MODE = true
let _regPhoneVerified = false

// ===== CURRENT GROUP =====
let _curGroupId: string | null = null
function userGroupIds(): string[] {
  if (CU?.group_ids?.length) return CU.group_ids
  if (CU?.group_id) return [CU.group_id]
  return []
}
function currentGroupId(): string {
  const ids = userGroupIds()
  if (!ids.length){ _curGroupId = null; return '' }
  if (_curGroupId && ids.includes(_curGroupId)) return _curGroupId
  const saved = localStorage.getItem('sp_current_group')
  if (saved && ids.includes(saved)){ _curGroupId = saved; return saved }
  const def = CU?.default_group_id && ids.includes(CU.default_group_id) ? CU.default_group_id : ids[0]
  _curGroupId = def
  localStorage.setItem('sp_current_group', def)
  return def
}
async function initGroupSelector(){
  const wrap = document.getElementById('curGroupWrap') as HTMLElement | null
  const sel = document.getElementById('curGroupSel') as HTMLSelectElement | null
  if(!wrap || !sel) return
  const ids = userGroupIds()
  if(!ids.length){ wrap.style.display = 'none'; return }
  if(API_MODE && !_groupsCache.length) await loadGroupsCache().catch(() => {})
  sel.innerHTML = ids.map((id) => {
    const g = _groupsCache.find((x:any)=>x.id===id)
    return `<option value="${esc(id)}">${esc(g?.name || id)}</option>`
  }).join('')
  sel.value = currentGroupId()
  wrap.style.display = ''
}
function setCurrentGroup(gid: string){
  const ids = userGroupIds()
  if(!gid || !ids.includes(gid)) return
  _curGroupId = gid
  localStorage.setItem('sp_current_group', gid)
  const sel = document.getElementById('curGroupSel') as HTMLSelectElement | null
  if(sel && sel.value !== gid) sel.value = gid
  if(API_MODE && gid) connectAlertHub(gid)
  if(CP === 'dashboard') renderDash()
  const g = _groupsCache.find((x:any)=>x.id===gid)
  toast('Grupo actual: ' + (g?.name || gid), 'ok')
}
function bindGroupSelector(){
  const sel = document.getElementById('curGroupSel') as HTMLSelectElement | null
  if(!sel || sel.dataset.bound) return
  sel.dataset.bound = '1'
  sel.addEventListener('change', () => setCurrentGroup(sel.value))
}

// ===== TOAST =====
function toast(msg: string, type='in'){
  const icons: Record<string,string>={ok:'fa-check-circle',er:'fa-circle-xmark',wa:'fa-triangle-exclamation',in:'fa-circle-info'}
  const tc=document.getElementById('toastC')!
  const t=document.createElement('div')
  t.className='toast '+type
  t.innerHTML=`<i class="fas ${icons[type]||icons.in}"></i><span class="tm">${esc(msg)}</span>`
  tc.appendChild(t)
  setTimeout(()=>{t.classList.add('rm');setTimeout(()=>t.remove(),300)},3500)
}
function esc(s:string){const e=document.createElement('div');e.textContent=s;return e.innerHTML}

// ===== NAVIGATION =====
// Route map: internal page key <-> URL path
const ROUTES: Record<string,string> = {
  dashboard: '/dashboard',
  map: '/map',
  alerts: '/alerts',
  users: '/users',
  groups: '/groups',
  empresas: '/admin/empresas',
  emergency: '/emergency',
  notifications: '/notifications',
  'external-contacts': '/external-contacts',
  subscription: '/plans',
  suscripciones: '/suscripciones',
  billing: '/billing',
  iot: '/iot',
  'products-admin': '/products-admin',
  'quotes-admin': '/quotes-admin',
  shopping: '/shopping',
  orders: '/orders',
  'faq-admin': '/faq-admin',
  profile: '/profile',
  settings: '/settings',
}
const ROUTE_TO_PAGE: Record<string,string> = Object.fromEntries(
  Object.entries(ROUTES).map(([k,v])=>[v,k])
)

function showScr(s: string, plan?: string){
  const path = s==='login' ? '/login' : s==='reg' ? '/register' : s==='forgot' ? '/forgot-password' : s==='reset' ? '/reset-password' : s==='forceReset' ? '/force-reset' : '/'
  if(window.location.pathname !== path) history.replaceState({screen:s}, '', path)
  document.getElementById('landing')!.style.display='none'
  document.getElementById('scrLogin')!.style.display=s==='login'?'flex':'none'
  document.getElementById('scrReg')!.style.display=s==='reg'?'flex':'none'
  document.getElementById('scrForgot')!.style.display=s==='forgot'?'flex':'none'
  document.getElementById('scrReset')!.style.display=s==='reset'?'flex':'none'
  document.getElementById('scrForceReset')!.style.display=s==='forceReset'?'flex':'none'
  document.getElementById('publicAlert')!.style.display='none'
  document.getElementById('confirmedPage')!.style.display='none'
  document.getElementById('APP')!.style.display='none'
  document.body.dataset.screen='public'
  const fabAuth = document.getElementById('themeFab') as HTMLElement | null
  if(fabAuth) fabAuth.hidden = false
  if(s==='login'){
    setTimeout(() => renderTurnstile('turnstileLogin'), 100)
    setTimeout(() => renderGoogleButton(), 200)
  }
  if(s==='forgot'){
    (document.getElementById('frmForgot') as HTMLFormElement).reset()
    document.getElementById('fMsg')!.style.display='none'
    const btn=document.getElementById('btnForgotSend') as HTMLButtonElement
    btn.innerHTML='<i class="fas fa-paper-plane"></i> Enviar Codigo'; btn.disabled=false
    setTimeout(() => renderTurnstile('turnstileForgot'), 100)
  }
  if(s==='reset'){
    (document.getElementById('frmReset') as HTMLFormElement).reset()
    document.getElementById('rMsg')!.style.display='none'
  }
  if(s==='forceReset'){
    (document.getElementById('frmForceReset') as HTMLFormElement).reset()
    document.getElementById('frMsg')!.style.display='none'
  }
  // Resolve invite token if present
  if(s==='reg'){
    _regPhoneVerified = false
    if(!plan){
      const urlPlan = new URLSearchParams(window.location.search).get('plan')
      if(urlPlan) plan = urlPlan
    }
    const regForm = document.getElementById('frmReg') as HTMLFormElement
    if(regForm) regForm.reset()
    const rOtpSec = document.getElementById('rOtpSection') as HTMLElement
    if(rOtpSec) rOtpSec.style.display = 'none'
    const btnOtp = document.getElementById('btnRegSendOtp') as HTMLButtonElement | null
    if(btnOtp){ btnOtp.innerHTML = '<i class="fas fa-paper-plane"></i> Enviar'; btnOtp.disabled = false; delete btnOtp.dataset.mode }
    const rOtp = document.getElementById('rOtp') as HTMLInputElement | null
    if(rOtp){ rOtp.value = ''; rOtp.readOnly = false }
    const rOtpMsg = document.getElementById('rOtpMsg')
    if(rOtpMsg) rOtpMsg.style.display = 'none'
    const rPass = document.getElementById('rPass') as HTMLInputElement | null
    if(rPass) rPass.value = ''
    setTimeout(() => renderTurnstile('turnstileReg'), 100)
    const token = sessionStorage.getItem('invite_token')
    const infoEl = document.getElementById('rInviteInfo')
    const planFg = document.getElementById('rPlanFg') as HTMLElement
    if(token && infoEl){
      api.resolveInvite(token).then(r => {
        if(r.ok && r.data?.success){
          const roleLabel = r.data.group.invite_role === 'admin' ? 'Admin del grupo' : 'Miembro'
          infoEl.innerHTML = `<i class="fas fa-link"></i> Unirse a <strong>${esc(r.data.group.name)}</strong> como <span class="rb ${r.data.group.invite_role}">${roleLabel}</span>`
          infoEl.style.display = 'flex'
          if(planFg) planFg.style.display = 'none'
        }
      })
    } else if(infoEl){
      infoEl.style.display = 'none'
      if(planFg) planFg.style.display = ''
    }
    // Cargar planes desde API
    const planSel = document.getElementById('rPlan') as HTMLSelectElement
    if(planSel && API_MODE){
      api.getPlans().then(r => {
        if(r.ok && r.data?.plans?.length){
          planSel.innerHTML = '<option value="">Selecciona un plan...</option>'
          r.data.plans.forEach((p:any) => {
            if(p.active !== false){
              const priceLabel = p.price > 0 ? ' - $' + Number(p.price).toLocaleString('es-CL') + (p.period==='year'?'/anual':'/mes') : ''
              const opt = document.createElement('option')
              opt.value = p.id
              opt.textContent = p.name + priceLabel
              planSel.appendChild(opt)
            }
          })
          if(plan) planSel.value = plan
          updatePlanHighlight()
        }
      }).catch(() => {})
    }
  }
}

function updatePlanHighlight(){
  const sel = document.getElementById('rPlan') as HTMLSelectElement
  const el = document.getElementById('rPlanHighlight')
  if(!sel || !el) return
  const v = sel.value
  if(!v){ el.textContent = ''; return }
  const labels: Record<string,string> = { familiar:'tu Familia', condominio:'tu Condominio', gratis:'tu Hogar' }
  el.textContent = 'Te estás registrando como administrador de ' + (labels[v] || v)
}

function showLanding(){
  destroyTurnstile()
  document.body.dataset.screen='public'
  const fabLanding = document.getElementById('themeFab') as HTMLElement | null
  if(fabLanding) fabLanding.hidden = true
  history.replaceState({screen:'landing'}, '', '/')
  document.getElementById('landing')!.style.display='block'
  document.getElementById('scrLogin')!.style.display='none'
  document.getElementById('scrReg')!.style.display='none'
  document.getElementById('publicAlert')!.style.display='none'
  document.getElementById('confirmedPage')!.style.display='none'
  document.getElementById('APP')!.style.display='none'
  renderLandingPlans()
  renderLandingContent()
  renderLandingQuoteCalculator()
  renderProductCatalog()
  loadLandingWhatsApp()
  // NOTA: no se invoca One Tap automatico. El prompt de Google (FedCM)
  // abortaba en background (AbortError + warning COOP) en cada vista del
  // landing y tras logout. El boton "Continuar con Google" sigue disponible.
}

async function loadLandingWhatsApp(){
  try {
    const res = await fetch(`${API_BASE}/api/v1/landing/config`)
    const data = await res.json()
    if(data.success && data.whatsapp){
      const link = document.getElementById('landingWaLink') as HTMLAnchorElement | null
      if(link){
        const num = data.whatsapp.replace(/[^\d]/g, '')
        link.href = `https://wa.me/${num}?text=Hola%2C%20quiero%20comenzar%20a%20usar%20Domus%20Secura%20gratuitamente`
        link.style.opacity = '1'
      }
    }
  } catch {}
}

async function showPublicAlert(token: string){
  history.replaceState({screen:'publicAlert', token}, '/alerta/'+token)
  document.getElementById('landing')!.style.display='none'
  document.getElementById('scrLogin')!.style.display='none'
  document.getElementById('scrReg')!.style.display='none'
  document.getElementById('publicAlert')!.style.display='block'
  document.getElementById('APP')!.style.display='none'

  const loading = document.getElementById('paLoading')!
  const error = document.getElementById('paError')!
  const content = document.getElementById('paContent')!

  loading.style.display = ''
  error.style.display = 'none'
  content.style.display = 'none'

  const res = await api.getSharedAlert(token)
  loading.style.display = 'none'

  if (!res.ok || !res.data?.success) {
    error.style.display = ''
    error.innerHTML = '<i class="fas fa-triangle-exclamation" style="font-size:32px;display:block;margin-bottom:12px"></i><p>' + esc(res.data?.error || 'Alerta no encontrada o expirada') + '</p>'
    return
  }

  const a = res.data.alert
  content.style.display = ''

  const typeColors: Record<string, string> = { security: 'var(--red)', medical: 'var(--amber)', fire: 'var(--orange)' }
  const typeIcons: Record<string, string> = { security: 'fa-shield-halved', medical: 'fa-heart-pulse', fire: 'fa-fire-extinguisher' }
  const typeBg: Record<string, string> = { security: 'var(--red-g)', medical: 'var(--amber-g)', fire: 'var(--orange-g)' }
  const statusColors: Record<string, string> = { active: 'var(--red)', resolved: 'var(--green)', cancelled: 'var(--fg3)' }

  const badge = document.getElementById('paTypeBadge')!
  badge.style.background = typeBg[a.type] || 'var(--red-g)'
  badge.style.color = typeColors[a.type] || 'var(--red)'
  badge.innerHTML = '<i class="fas ' + (typeIcons[a.type] || 'fa-shield-halved') + '"></i> <span>' + esc(a.type_label || a.type) + '</span>'

  const statusEl = document.getElementById('paStatus')!
  statusEl.style.background = (statusColors[a.status] || 'var(--fg3)') + '22'
  statusEl.style.color = statusColors[a.status] || 'var(--fg3)'
  statusEl.textContent = a.status === 'active' ? 'Activa' : a.status === 'resolved' ? 'Resuelta' : 'Cancelada'

  document.getElementById('paUser')!.textContent = esc(a.user_name || 'Usuario')
  document.getElementById('paTime')!.textContent = new Date(a.created_at).toLocaleString('es-CL')

  const addr = document.getElementById('paAddr')!
  if (a.address) {
    addr.style.display = ''
    document.getElementById('paAddress')!.textContent = esc(a.address)
  } else {
    addr.style.display = 'none'
  }

  // Show map if coordinates available
  if (a.lat && a.lng) {
    const mapDiv = document.getElementById('paMap')!
    mapDiv.style.display = ''
    // Initialize leaflet map
    if (mapInst) mapInst.remove()
    try { await ensureLeaflet() } catch { mapDiv.style.display = 'none'; return }
    mapInst = L.map(mapDiv, { zoomControl: true, attributionControl: false }).setView([a.lat, a.lng], 15)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(mapInst)
    L.circle([a.lat, a.lng], { radius: a.accuracy || 50, color: typeColors[a.type] || '#ef4444', fillColor: typeColors[a.type] || '#ef4444', fillOpacity: 0.2 }).addTo(mapInst)
    L.marker([a.lat, a.lng]).addTo(mapInst)
    setTimeout(() => mapInst.invalidateSize(), 500)
  } else {
    document.getElementById('paMap')!.style.display = 'none'
  }
}

async function renderConfirmedPage(token: string){
  history.replaceState({screen:'confirmed'}, '/confirmed?token='+token)
  document.getElementById('landing')!.style.display='none'
  document.getElementById('scrLogin')!.style.display='none'
  document.getElementById('scrReg')!.style.display='none'
  document.getElementById('publicAlert')!.style.display='none'
  document.getElementById('APP')!.style.display='none'
  document.getElementById('confirmedPage')!.style.display='block'

  const loading = document.getElementById('confLoading')!
  const error = document.getElementById('confError')!
  const content = document.getElementById('confContent')!

  loading.style.display = ''
  error.style.display = 'none'
  content.style.display = 'none'

  const res = await req('GET', '/api/v1/external-contacts/confirmed?token='+encodeURIComponent(token))
  loading.style.display = 'none'

  if (!res.ok || !res.data?.success) {
    error.style.display = ''
    error.innerHTML = '<i class="fas fa-triangle-exclamation" style="font-size:32px;display:block;margin-bottom:12px"></i><p>' + esc(res.data?.error || 'Enlace invalido o expirado') + '</p>'
    return
  }

  content.style.display = ''
  const d = res.data
  document.getElementById('confInvitedBy')!.textContent = esc(d.invited_by || 'Un usuario')
  document.getElementById('confName')!.textContent = esc(d.contact?.name || '-')
  document.getElementById('confPhone')!.textContent = esc(d.contact?.phone || '-')
  document.getElementById('confDate')!.textContent = d.contact?.consent_at ? new Date(d.contact.consent_at).toLocaleString('es-CL') : 'Ahora'
}

type RouteParams = Record<string, string>

const PARAM_ROUTES: Array<{ re: RegExp; page: string; keys: string[] }> = [
  { re: /^\/users\/new\/?$/, page: 'user-edit', keys: [] },
  { re: /^\/users\/([^/]+)\/?$/, page: 'user-edit', keys: ['id'] },
  { re: /^\/groups\/new\/?$/, page: 'group-edit', keys: [] },
  { re: /^\/groups\/([^/]+)\/?$/, page: 'group-edit', keys: ['id'] },
  { re: /^\/groups\/([^/]+)\/miembros\/?$/, page: 'group-members', keys: ['id'] },
  { re: /^\/iot\/([^/]+)\/?$/, page: 'iot-device', keys: ['id'] },
  { re: /^\/emergency\/([^/]+)\/numeros\/?$/, page: 'emergency-numbers', keys: ['gid'] },
  { re: /^\/contacts\/new\/?$/, page: 'contact-edit', keys: [] },
  { re: /^\/contacts\/([^/]+)\/?$/, page: 'contact-edit', keys: ['id'] },
  { re: /^\/external-contacts\/new\/?$/, page: 'ext-contact-edit', keys: [] },
  { re: /^\/external-contacts\/([^/]+)\/?$/, page: 'ext-contact-edit', keys: ['id'] },
  { re: /^\/legal\/([^/]+)\/?$/, page: 'legal-page', keys: ['doc'] },
  { re: /^\/legal\/quote\/([^/]+)\/?$/, page: 'legal-quote', keys: ['quoteId'] },
  { re: /^\/plans\/new\/?$/, page: 'plan-edit', keys: [] },
  { re: /^\/plans\/([^/]+)\/?$/, page: 'plan-edit', keys: ['id'] },
  { re: /^\/products-admin\/new\/?$/, page: 'product-edit', keys: [] },
  { re: /^\/products-admin\/([^/]+)\/?$/, page: 'product-edit', keys: ['id'] },
]

function resolvePath(path: string): { page: string; params: RouteParams } | null {
  for (const r of PARAM_ROUTES) {
    const m = path.match(r.re)
    if (m) {
      const params: RouteParams = {}
      r.keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1] || '') })
      return { page: r.page, params }
    }
  }
  const page = ROUTE_TO_PAGE[path]
  return page ? { page, params: {} } : null
}

// Navegación por ruta (soporta parámetros: /users/:id, /groups/:id)
function go(path: string, push = true) {
  const match = resolvePath(path)
  if (!match) { if (path !== '/dashboard') go('/dashboard', push); return }
  if (push) history.pushState({ page: match.page, path }, '', path)
  renderPage(match.page, match.params)
}

// Compatibilidad: navegación por nombre de página
function nav(page: string, push = true) {
  go(ROUTES[page] || '/dashboard', push)
}

function renderPage(page: string, params: RouteParams = {}) {
  CP = page
  document.getElementById('SIDEBAR')!.classList.remove('open')
  document.getElementById('sbOv')!.classList.remove('show')
  const navKey = page === 'user-edit' ? 'users'
    : (page === 'group-edit' || page === 'group-members') ? 'groups'
    : page === 'iot-device' ? 'iot'
    : page === 'emergency-numbers' ? 'emergency'
    : page === 'contact-edit' ? 'notifications'
    : page === 'ext-contact-edit' ? 'external-contacts'
    : page === 'legal-page' || page === 'legal-quote' ? 'notifications'
    : page === 'plan-edit' ? 'plans'
    : page === 'product-edit' ? 'products-admin'
    : page;
  document.querySelectorAll('.ni').forEach((n) => { n.classList.toggle('act', (n as HTMLElement).dataset.p === navKey) })
  document.querySelectorAll('.page').forEach((p) => { p.classList.toggle('act', p.id === 'pg-' + page) })
  const titles: Record<string, string> = { dashboard: 'Dashboard', map: 'Mapa en Tiempo Real', alerts: 'Historial de Alertas', users: 'Gestion de Usuarios', groups: 'Gestion de Grupos', empresas: 'Mantenedor de Empresas', emergency: 'Numeros de Emergencia', 'external-contacts': 'Mis Contactos Externos', notifications: 'Configuracion de Notificaciones', 'control-center': 'Control Center', subscription: 'Planes de Suscripcion', 'products-admin': 'Productos y Precios', 'faq-admin': 'Preguntas Frecuentes', billing: 'Facturacion', suscripciones: 'Mi Suscripcion', iot: 'Modulo IoT', profile: 'Mi Perfil', settings: '2FA y Privacidad', orders: 'Mis Ordenes', 'user-edit': 'Usuario', 'group-edit': 'Grupo', 'group-members': 'Miembros del grupo', 'iot-device': 'Dispositivo', 'emergency-numbers': 'Números de emergencia', 'contact-edit': 'Contacto', 'ext-contact-edit': 'Contacto Externo', 'legal-page': 'Documentos Legales', 'legal-quote': 'Detalle de Cotización', 'plan-edit': 'Plan', 'product-edit': 'Producto' }
  document.getElementById('pgTitle')!.textContent = titles[page] || 'Dashboard'
  switch (page) {
    case 'dashboard': renderDash(); break
    case 'map': renderMap(); break
    case 'alerts': renderAlerts(); break
    case 'users': renderUsers(); break
    case 'groups': renderGroups(); break
    case 'empresas': renderEmpresas(); break
    case 'emergency': renderEmergencyPage(); break
    case 'external-contacts': renderExtContacts(); break
    case 'notifications': renderNotif(); renderContacts(); break
    case 'control-center': renderControlCenter(); break
    case 'subscription': renderPlans(); break
    case 'products-admin': renderProductsAdmin(); break
    case 'quotes-admin': renderQuotesAdmin(); break
    case 'shopping': renderShopping(); break
    case 'orders': renderOrders(); break
    case 'faq-admin': renderFaqAdmin(); break
    case 'billing': renderBilling(); break
    case 'suscripciones': renderSuscripciones(); break
    case 'iot': renderIoT(); break
    case 'profile': renderProfile(); break
    case 'settings': renderSettings(); break
    case 'user-edit': renderUserPage(params.id); break
    case 'group-edit': renderGroupPage(params.id); break
    case 'group-members': renderGroupMembersPage(params.id); break
    case 'iot-device': renderIotDevicePage(params.id); break
    case 'emergency-numbers': renderEmergencyNumbersPage(params.gid); break
    case 'contact-edit': renderContactEditPage(params.id); break
    case 'ext-contact-edit': renderExtContactEditPage(params.id); break
    case 'legal-page': renderLegalPage(params.doc); break
    case 'legal-quote': renderLegalQuotePage(params.quoteId); break
    case 'plan-edit': renderPlanEditPage(params.id); break
    case 'product-edit': renderProductEditPage(params.id); break
  }
}

function showConfirmedPage(){
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  if(!token) showLanding()
  else renderConfirmedPage(token)
}

// Handle browser back/forward
window.addEventListener('popstate', (e: PopStateEvent) => {
  if(!CU){
    const path = window.location.pathname
    const alertaMatch = path.match(/^\/alerta\/([a-zA-Z0-9]+)$/)
    if(alertaMatch) { showPublicAlert(alertaMatch[1]); return }
    if(path === '/confirmed') { showConfirmedPage(); return }
    if(path === '/login') showScr('login')
    else if(path === '/register') showScr('reg')
    else if(path === '/forgot-password') showScr('forgot')
    else if(path === '/reset-password') showScr('reset')
    else showLanding()
    return
  }
  const match = resolvePath(window.location.pathname)
  if (match) renderPage(match.page, match.params)
  else nav('dashboard', false)
})

function buildNav(){
  const r=CU.role
  let h=''
  h+=`<div class="ns"><div class="ns-t">Principal</div>`
  h+=`<div class="ni act" data-p="dashboard"><i class="fas fa-gauge-high"></i> Dashboard</div>`
  h+=`<div class="ni" data-p="alerts"><i class="fas fa-bell"></i> Alertas <span class="nb" id="navBdg" style="display:none">0</span></div>`
    if(r==='admin'||r==='superadmin') h+=`<div class="ni" data-p="map"><i class="fas fa-map"></i> Mapa</div>`
  h+=`<div class="ni" data-p="shopping"><i class="fas fa-cart-shopping"></i> Tienda</div>`
  h+=`<div class="ni" data-p="orders"><i class="fas fa-truck"></i> Mis Ordenes</div>`
  h+=`</div>`
  if(r==='admin'||r==='superadmin'){
    h+=`<div class="ns"><div class="ns-t">Gestion</div>`
    h+=`<div class="ni" data-p="users"><i class="fas fa-users"></i> Usuarios</div>`
    h+=`<div class="ni" data-p="groups"><i class="fas fa-people-group"></i> Grupos</div>`
    h+=`<div class="ni" data-p="empresas"><i class="fas fa-building"></i> Empresas</div>`
    h+=`<div class="ni" data-p="emergency"><i class="fas fa-phone-volume"></i> Numeros de Emergencia</div>`
    h+=`<div class="ni" data-p="external-contacts"><i class="fas fa-address-book"></i> Mis Contactos Externos</div>`
    h+=`<div class="ni" data-p="notifications"><i class="fas fa-paper-plane"></i> Notificaciones</div>`
    h+=`</div>`
  }
  if(r==='superadmin'){
    h+=`<div class="ns"><div class="ns-t">Plataforma</div>`
    h+=`<div class="ni" data-p="control-center"><i class="fas fa-chart-pie"></i> Control Center</div>`
    h+=`<div class="ni" data-p="iot"><i class="fas fa-microchip"></i> Modulo IoT</div>`
    h+=`<div class="ni" data-p="subscription"><i class="fas fa-credit-card"></i> Planes</div>`
    h+=`<div class="ni" data-p="products-admin"><i class="fas fa-tags"></i> Productos y Precios</div>`
    h+=`<div class="ni" data-p="quotes-admin"><i class="fas fa-file-invoice"></i> Cotizaciones</div>`
    h+=`<div class="ni" data-p="faq-admin"><i class="fas fa-circle-question"></i> Preguntas Frecuentes</div>`
    h+=`<div class="ni" data-p="suscripciones"><i class="fas fa-receipt"></i> Suscripcion</div>`
    h+=`<div class="ni" data-p="billing"><i class="fas fa-receipt"></i> Facturacion</div>`
    h+=`</div>`
  }
  if(r==='user'){
    h+=`<div class="ns"><div class="ns-t">Mi Cuenta</div>`
    h+=`<div class="ni" data-p="iot"><i class="fas fa-microchip"></i> Mis Dispositivos</div>`
    h+=`<div class="ni" data-p="notifications"><i class="fas fa-paper-plane"></i> Mis Notificaciones</div>`
    h+=`<div class="ni" data-p="external-contacts"><i class="fas fa-address-book"></i> Mis Contactos Externos</div>`
    h+=`<div class="ni" data-p="subscription"><i class="fas fa-credit-card"></i> Planes</div>`
    h+=`<div class="ni" data-p="suscripciones"><i class="fas fa-receipt"></i> Mi Suscripcion</div>`
    h+=`</div>`
  }
  h+=`<div class="ns"><div class="ni" data-p="profile"><i class="fas fa-user-gear"></i> Mi Perfil</div>`
    h+=`<div class="ni" data-p="settings"><i class="fas fa-shield-halved"></i> 2FA y Privacidad</div></div>`
  h+=`<div class="ns" style="margin-top:auto;border-top:1px solid var(--bdr);padding-top:6px">`
    h+=`<div class="ni" style="font-size:11px;cursor:default;opacity:.5;pointer-events:none"><i class="fas fa-code-branch"></i> Build <span id="sbBuildId">${typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : 'dev'}</span></div>`
    h+=`<div class="ni" id="btnForceUpdate" style="font-size:12px"><i class="fas fa-rotate"></i> Buscar actualizacion</div>`
  h+=`</div>`
  document.getElementById('SBNAV')!.innerHTML=h
  document.getElementById('SBNAV')!.onclick=(e)=>{
    const ni=(e.target as HTMLElement).closest('.ni') as HTMLElement|null
    if(ni&&ni.dataset.p) nav(ni.dataset.p)
  }
  document.getElementById('btnForceUpdate')?.addEventListener('click', forceAppUpdate)
}
async function forceAppUpdate(){
  const btn = document.getElementById('btnForceUpdate')
  if(btn){ btn.style.opacity='.5'; btn.style.pointerEvents='none' }
  const restore = () => { if(btn){ btn.style.opacity='1'; btn.style.pointerEvents='' } }
  try {
    if('serviceWorker' in navigator && window.location.protocol.startsWith('http')){
      const reg = await navigator.serviceWorker.getRegistration()
      if(reg){
        const prev = reg.active
        await reg.update()
        const updated = reg.waiting || reg.installing || (reg.active && reg.active !== prev)
        if(updated){
          toast('Actualizando...','in')
          await new Promise<void>((resolve) => {
            const t = reg.waiting || reg.installing
            if(!t || t.state === 'activated') return resolve()
            t.addEventListener('statechange', () => { if(t.state === 'activated') resolve() })
            setTimeout(resolve, 4000)
          })
        }
        window.location.reload()
      } else {
        window.location.reload()
      }
    } else {
      window.location.reload()
    }
  } catch(e: any){
    toast('Error al buscar actualizacion','er')
    restore()
  }
}
function updateSbUser(){
  document.getElementById('sbAv')!.textContent=((CU.name?.[0]||'')+(CU.lastname?.[0]||'')).toUpperCase()
  document.getElementById('sbNm')!.textContent=CU.name+' '+CU.lastname
  document.getElementById('sbRl')!.textContent=CU.role==='superadmin'?'Super Administrador':CU.role==='admin'?'Administrador':'Usuario'
}
function toggleSb(){
  document.getElementById('SIDEBAR')!.classList.toggle('open')
  document.getElementById('sbOv')!.classList.toggle('show')
}

function destroyTurnstile(){
  Object.values(tsWidgets).forEach(id => {
    try { (turnstile as any).remove(id) } catch {}
  })
  tsWidgets = {}; tsTokens = {}; tsFailed = {}
}

function enterApp(){
  destroyTurnstile()
  document.getElementById('landing')!.style.display='none'
  document.getElementById('scrLogin')!.style.display='none'
  document.getElementById('scrReg')!.style.display='none'
  document.getElementById('APP')!.style.display='block'
  document.body.dataset.screen='app'
  buildNav();updateSbUser()
  initGroupSelector().then(() => bindGroupSelector())
  // Check for pending subscription — redirect to payment
  if(CU.role !== 'superadmin' && API_MODE){
    api.getBillingStatus().then(r => {
      if(r.ok && r.data?.subscription?.status === 'pending'){
        nav('suscripciones')
        toast('Tienes un pago pendiente. Completa la suscripcion para acceder a todas las funciones.','wa')
      }
    }).catch(() => {})
  }
  // Navegar a la ruta actual (soporta rutas con parámetros como /users/:id)
  const deep = resolvePath(window.location.pathname)
  if (deep) renderPage(deep.page, deep.params)
  else nav('dashboard', false)
  getGeo()
  _cartUpdateBadges()
  setTimeout(() => toast('Bienvenido, '+CU.name,'ok'), 100)
  // Conectar WebSocket live data al grupo actual
  const wsGroup = currentGroupId()
  if (wsGroup) {
    connectAlertHub(wsGroup)
  }
}
function logout(){
  disconnectAlertHub()
  _curGroupId = null
  localStorage.removeItem('sp_current_group')
  try{ Object.keys(localStorage).filter((k)=>k.indexOf('sp_cache_')===0).forEach((k)=>localStorage.removeItem(k)) }catch{}
  CU=null;clearToken();document.getElementById('APP')!.style.display='none';showLanding()
}

function gid(){return 'id_'+Date.now().toString(36)+Math.random().toString(36).slice(2,7)}
function fmtDate(iso:string){
  const d=new Date(iso)
  return d.toLocaleDateString('es')+' '+d.toLocaleTimeString('es',{hour:'2-digit',minute:'2-digit'})
}
const typeLbl: Record<string,string>={security:'Seguridad',medical:'Alerta Medica',fire:'Bomberos'}
const typeIco: Record<string,string>={security:'fa-shield-halved',medical:'fa-heart-pulse',fire:'fa-fire-extinguisher'}

function statusLabel(st:string){ return st==='active'?'Activa':st==='resolved'?'Resuelta':st==='cancelled'?'Cancelada':st }

// Turnstile helpers
declare const turnstile: { render: (container: string | HTMLElement, opts: any) => string; getResponse: (widgetId: string | HTMLElement) => string; reset: (widgetId: string | HTMLElement) => void; execute: (widgetId: string | HTMLElement) => void }
const TURNSTILE_SITE_KEY = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

// Google Sign-In / One Tap
const GOOGLE_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID || ''
const _GOOGLE_ALLOWED_ORIGINS: string[] = [
  'http://localhost:5173',
  'http://localhost:8788',
  'https://app.domus-secura.com',
  'https://domus-secura.pages.dev',
  'https://develop.domus-secura.pages.dev',
  'https://domus-secura-dev.pages.dev',
  'https://app-dev.domus-secura.com',
]
// En desarrollo el puerto de Vite cambia si el 5173 está ocupado (5174, 5199…):
// se acepta cualquier localhost. El origen igual debe estar autorizado en el
// cliente OAuth de Google Cloud, o Google rechaza el botón.
const _googleAllowed = import.meta.env.DEV
  ? /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
  : _GOOGLE_ALLOWED_ORIGINS.includes(window.location.origin)
if (import.meta.env.DEV && GOOGLE_CLIENT_ID && !_GOOGLE_ALLOWED_ORIGINS.includes(window.location.origin)) {
  console.warn(`[GOOGLE] Origen ${window.location.origin} no está en la lista conocida: si el botón falla, agrégalo en "Orígenes autorizados de JavaScript" del cliente OAuth.`)
}
let googleInitialized = false
declare const google: any;
function initGoogleSignIn(){
  if(googleInitialized || !GOOGLE_CLIENT_ID || typeof google === 'undefined' || !_googleAllowed) return
  googleInitialized = true
  google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: handleGoogleCredential,
    cancel_on_tap_outside: false,
    use_fedcm_for_prompt: false,
  })
}
function renderGoogleButton(){
  const el = document.getElementById('googleBtn')
  if(!el || !GOOGLE_CLIENT_ID || typeof google === 'undefined' || !_googleAllowed) return
  initGoogleSignIn()
  google.accounts.id.renderButton(el, { type: 'standard', shape: 'pill', theme: 'outline', size: 'large', text: 'continue_with', logo_alignment: 'left' })
}
async function handleGoogleCredential(response: any){
  if(!response?.credential) return
  console.log('[GOOGLE] credential received, length:', response.credential.length)
  const r = await api.googleLogin(response.credential)
  if(r.ok && r.data?.success){
    CU = r.data.user
    setToken(r.data.token)
    localStorage.setItem('sp_refresh', r.data.refreshToken)
    localStorage.setItem('sp_user', JSON.stringify(r.data.user))
    toast('Inicio de sesion con Google exitoso', 'ok')
    enterApp()
  } else {
    toast(r.data?.error || 'Error al iniciar sesion con Google', 'er')
  }
}
// Reservado para uso explicito futuro (actualmente sin auto-llamadas:
// el prompt automatico generaba AbortError de FedCM + warnings COOP).
function promptGoogleOneTap(){
  try {
    if(!GOOGLE_CLIENT_ID || typeof google === 'undefined' || !_googleAllowed) return
    if(document.hidden) return
    initGoogleSignIn()
    // Cancela un prompt pendiente antes de abrir otro (evita aborts superpuestos)
    try { google.accounts.id.cancel() } catch {}
    google.accounts.id.prompt()
  } catch {}
}
let tsWidgets: Record<string, string> = {}
let tsTokens: Record<string, string> = {}
let tsFailed: Record<string, boolean> = {}
// Cada widget invisible (execution:'execute') solo genera token tras turnstile.execute().
// Mapea widget -> boton que debe re-dispararse cuando el callback entrega el token.
function tsSubmitFor(id: string): string {
  if(id === 'turnstileLogin') return 'btnLogin'
  if(id === 'turnstileForgot') return 'btnForgotSend'
  const sendBtn = document.getElementById('btnRegSendOtp') as HTMLButtonElement | null
  if(sendBtn && sendBtn.dataset.mode !== 'verify') return 'btnRegSendOtp'
  return 'btnReg'
}
function renderTurnstile(id: string){
  if(typeof turnstile === 'undefined' || !TURNSTILE_SITE_KEY) return
  if((import.meta as any).env?.VITE_SKIP_TURNSTILE){ tsTokens[id] = 'bypassed'; return }
  const el = document.getElementById(id)
  if(!el) return
  delete tsTokens[id]
  delete tsFailed[id]
  el.innerHTML = ''
  try {
    tsWidgets[id] = turnstile.render('#' + id, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: resolveTheme(),
      execution: 'execute',
      callback: (token: string) => {
        tsTokens[id] = token
        const btn = document.getElementById(tsSubmitFor(id)) as HTMLButtonElement | null
        if(btn){ btn.disabled = false; btn.click() }
      },
      'error-callback': () => {
        tsFailed[id] = true
        const btn = document.getElementById(tsSubmitFor(id)) as HTMLButtonElement | null
        if(btn) btn.disabled = false
      },
      'expired-callback': () => {
        delete tsTokens[id]
        const widgetId = tsWidgets[id]
        if(widgetId) turnstile.reset(widgetId)
      },
    })
  } catch(e) {
    tsFailed[id] = true
    console.warn('[Turnstile] render error:', e)
  }
}
function getTurnstileToken(id: string): string {
  return tsTokens[id] || ''
}
function alertHtml(a: any, userName=''){
  const acc = a.accuracy ? ` ±${Math.round(a.accuracy)}m` : ''
  const loc = (a.lat!=null && a.lng!=null) ? `${a.lat.toFixed(4)}, ${a.lng.toFixed(4)}${acc}` : 'Sin ubicacion'
  const notifBtn = a.id ? `<button class="btn btn-g btn-sm" data-al-not="${a.id}" style="margin-top:6px"><i class="fas fa-paper-plane"></i> Notificaciones</button>` : ''
  const resolveBtn = a.status === 'active' ? `<button class="btn btn-o btn-sm" data-al-resolve="${a.id}" style="margin-top:6px;margin-left:4px"><i class="fas fa-check"></i> Resolver</button>` : ''
  return `<div class="ai"><div class="ai-i ${a.type}"><i class="fas ${typeIco[a.type]}"></i></div><div class="ai-b"><div class="ai-t">${typeLbl[a.type]}</div><div class="ai-d">${esc(userName||'')} — ${loc}<br>${esc(a.address||'')}</div>${notifBtn}${resolveBtn}</div><div class="ai-m"><div class="ai-time">${fmtDate(a.created_at||a.at)}</div><span class="st ${a.status}">${statusLabel(a.status)}</span></div></div>`
}

function alertSmallHtml(a: any){
  const loc = (a.lat!=null && a.lng!=null) ? `${a.lat.toFixed(4)}, ${a.lng.toFixed(4)}` : ''
  return `<div class="ai sm" data-al-detail="${a.id}" style="cursor:pointer">
    <div class="ai-i ${a.type}"><i class="fas ${typeIco[a.type]}" style="font-size:12px"></i></div>
    <div class="ai-b"><div class="ai-t" style="font-size:12px">${typeLbl[a.type]}</div>
    <div class="ai-d" style="font-size:11px;opacity:.7">${loc ? loc+' · ':''}${esc(a.address||'')}</div></div>
    <div class="ai-m"><div class="ai-time" style="font-size:10px">${fmtDate(a.created_at||a.at)}</div>
    <span class="st ${a.status}" style="font-size:10px">${statusLabel(a.status)}</span></div>
  </div>`
}

// ===== GEO =====
function getGeo(){
  const el=document.getElementById('geoC')
  if(!navigator.geolocation){if(el)el.textContent='Geolocalizacion no soportada';return}
  navigator.geolocation.getCurrentPosition((pos)=>{
    ULoc={lat:pos.coords.latitude,lng:pos.coords.longitude,accuracy:pos.coords.accuracy}
    if(el)el.textContent=`${ULoc.lat.toFixed(6)}, ${ULoc.lng.toFixed(6)} | ${Math.round(ULoc.accuracy)}m`
  },()=>{
    if(el)el.textContent=`${ULoc.lat.toFixed(6)}, ${ULoc.lng.toFixed(6)} | Simuladas`
  },{enableHighAccuracy:true,timeout:10000})
}

// ===== LEAFLET (carga diferida) =====
// Leaflet solo se usa en mapas; cargarlo en el <head> bloqueaba el primer
// render (y con él el botón de pánico). Se inyecta bajo demanda una sola vez.
let _leafletP: Promise<void> | null = null
function ensureLeaflet(): Promise<void> {
  if ((window as any).L?.heatLayer) return Promise.resolve()
  if (_leafletP) return _leafletP
  const add = (tag: 'link' | 'script', attrs: Record<string, string>) => new Promise<void>((res, rej) => {
    const el = document.createElement(tag) as any
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
    el.onload = () => res(); el.onerror = () => rej(new Error('leaflet: ' + (attrs.src || attrs.href)))
    document.head.appendChild(el)
  })
  const CDN = 'https://cdnjs.cloudflare.com/ajax/libs/'
  _leafletP = Promise.all([
    add('link', { rel: 'stylesheet', href: CDN + 'leaflet/1.9.4/leaflet.css', integrity: 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=', crossorigin: '' }),
    add('script', { src: CDN + 'leaflet/1.9.4/leaflet.js', integrity: 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=', crossorigin: '' })
      .then(() => add('script', { src: CDN + 'leaflet.heat/0.2.0/leaflet-heat.js', integrity: 'sha512-KhIBJeCI4oTEeqOmRi2gDJ7m+JARImhUYgXWiOTIp9qqySpFUAJs09erGKem4E5IPuxxSTjavuurvBitBmwE0w==', crossorigin: '' })),
  ]).then(() => {}).catch((e) => { _leafletP = null; throw e })
  return _leafletP
}

// ===== MAP =====
async function renderMap(){
  const container = document.getElementById('mapContainer')!
  try { await ensureLeaflet() } catch { container.innerHTML = '<div class="empty"><i class="fas fa-map"></i><p>No se pudo cargar el mapa</p></div>'; return }
  if(!mapInst){
    mapInst = (window as any).L.map(container).setView([ULoc.lat, ULoc.lng], 14)
    ;(window as any).L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 }).addTo(mapInst)
  }
  if(!API_MODE){ container.innerHTML = '<div class="empty"><i class="fas fa-map"></i><p>Mapa no disponible sin conexion</p></div>'; return }
  const mapStatusEl = document.getElementById('mapStatus'); if(mapStatusEl) mapStatusEl.textContent = 'Cargando mapa...'
  const mapListEl = document.getElementById('mapAlertList')
  if(CU?.role === 'superadmin' && mapListEl && !mapListEl.innerHTML.trim()){ mapListEl.style.display = ''; mapListEl.innerHTML = loadingState('Cargando registros...') }
  const qs = `?period=${MAP_PERIOD}&type=${MAP_FILTER}&_t=${Date.now()}`
  const r = await api.getMapData(qs)
  if(!r.ok || !r.data.success){ toast('Error cargando mapa','er'); if(mapStatusEl) mapStatusEl.textContent = 'Error cargando mapa'; return }
  mapMarkers.forEach((m:any)=> mapInst.removeLayer(m))
  mapMarkers = []
  if(_heatLayer){ mapInst.removeLayer(_heatLayer); _heatLayer = null }
  const typeColors: Record<string,string> = {security:'#ef4444', medical:'#f59e0b', fire:'#f97316'}
  const typeLbl: Record<string,string> = {security:'Seguridad', medical:'Medica', fire:'Bomberos'}
  const alerts = (r.data.alerts || []).filter((a:any)=> MAP_FILTER==='all' || a.type===MAP_FILTER)
  let bounds: any[] = []

  // Build heat map points: [lat, lng, intensity]
  const heatPoints: [number,number,number][] = []
  for(const a of alerts){
    if(a.lat==null || a.lng==null) continue
    heatPoints.push([a.lat, a.lng, 1])
    bounds.push([a.lat,a.lng])
  }
  // Render heat layer if we have enough points
  if(heatPoints.length >= 3 && (window as any).L.heatLayer){
    _heatLayer = (window as any).L.heatLayer(heatPoints, {
      radius: 30,
      blur: 20,
      maxZoom: 16,
      max: 1.0,
      gradient: { 0.2: '#3b82f6', 0.4: '#f59e0b', 0.6: '#f97316', 0.8: '#ef4444', 1.0: '#dc2626' }
    }).addTo(mapInst)
  }
  // Individual alert pins on top of heatmap
  for(const a of alerts){
    if(a.lat==null || a.lng==null) continue
    const col = typeColors[a.type] || '#ef4444'
    const circle = (window as any).L.circleMarker([a.lat,a.lng], {radius: 8, color: col, fillColor: col, fillOpacity: 0.9, weight: 2, opacity: 1}).addTo(mapInst)
    const popup = `<strong>${typeLbl[a.type] || a.type}</strong><br>${esc(a.name||'')} ${esc(a.lastname||'')}<br>${esc(a.address||'Sin direccion')}<br>${fmtDate(a.created_at)}<br>Precision: ${a.accuracy ? a.accuracy+'m' : 'N/A'}`
    circle.bindPopup(popup)
    mapMarkers.push(circle)
  }
  const devColors: Record<string,string> = {sirena:'#ef4444', portero:'#06b6d4', puerta:'#10b981', camara:'#a855f7', sensor:'#f59e0b'}
  for(const d of (r.data.devices || [])){
    const lat = d.lat ?? ULoc.lat
    const lng = d.lng ?? ULoc.lng
    const icon = (window as any).L.divIcon({className:'', html:`<div style="background:${devColors[d.type]||'#64748b'};width:12px;height:12px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 8px rgba(0,0,0,.4)" title="${d.name}"></div>`})
    const m = (window as any).L.marker([lat,lng], {icon}).addTo(mapInst)
    m.bindPopup(`<strong>${esc(d.name)}</strong><br>${esc(d.type)}<br>${esc(d.status)}<br>${esc(d.loc||'')}`)
    mapMarkers.push(m)
    bounds.push([lat,lng])
  }
  if(bounds.length) mapInst.fitBounds(bounds, {padding:[20,20], maxZoom:16})
  const periodLabels: Record<string,string> = {all:'todo el historial', today:'hoy', week:'esta semana', month:'este mes', year:'este año'}
  const statusEl = document.getElementById('mapStatus'); if (statusEl) statusEl.textContent = `${alerts.length} alertas (${periodLabels[MAP_PERIOD]||MAP_PERIOD}) · ${r.data.devices.length} dispositivos`
  renderMapAlertList(alerts)
}

function renderMapAlertList(alerts: any[]){
  const bar = document.getElementById('mapDeleteBar') as HTMLElement | null
  const list = document.getElementById('mapAlertList') as HTMLElement | null
  if(!bar || !list) return
  if(CU?.role !== 'superadmin'){
    bar.style.display = 'none'
    list.style.display = 'none'
    list.innerHTML = ''
    return
  }
  bar.style.display = ''
  if(!alerts.length){
    list.style.display = 'none'
    list.innerHTML = ''
    MAP_SELECTED.clear()
    updateMapSelUi()
    return
  }
  const ids = alerts.map((a:any)=>a.id)
  MAP_SELECTED = new Set(Array.from(MAP_SELECTED).filter((id)=>ids.includes(id)))
  const tc: Record<string,string> = {security:'#ef4444', medical:'#f59e0b', fire:'#f97316'}
  list.style.display = ''
  list.innerHTML = `<div class="stit"><i class="fas fa-list-check"></i> Registros (${alerts.length}) — marca para eliminar</div>` + alerts.map((a:any)=>`
    <label class="map-al-row">
      <input type="checkbox" data-al-id="${esc(a.id)}" ${MAP_SELECTED.has(a.id)?'checked':''}>
      <i class="fas ${typeIco[a.type] || 'fa-shield-halved'}" style="color:${tc[a.type] || '#ef4444'}"></i>
      <span class="map-al-name">${esc((a.name && a.lastname ? a.name+' '+a.lastname : a.name || 'Usuario').trim())}</span>
      <span class="map-al-meta">${typeLbl[a.type] || a.type} · ${fmtDate(a.created_at)} · ${a.lat!=null && a.lng!=null ? a.lat.toFixed(4)+', '+a.lng.toFixed(4) : 'Sin ubicacion'}${a.address ? ' · '+esc(a.address) : ''}</span>
    </label>`).join('')
  updateMapSelUi()
}

function updateMapSelUi(){
  const selBtn = document.getElementById('btnMapDelSel') as HTMLButtonElement | null
  const cntEl = document.getElementById('mapDelSelCount')
  if(cntEl) cntEl.textContent = String(MAP_SELECTED.size)
  if(selBtn) selBtn.disabled = MAP_SELECTED.size === 0
}

const mapAlertListEl = document.getElementById('mapAlertList')
if(mapAlertListEl) mapAlertListEl.addEventListener('change', (e) => {
  const cb = e.target as HTMLInputElement
  if(cb?.dataset?.alId){
    if(cb.checked) MAP_SELECTED.add(cb.dataset.alId)
    else MAP_SELECTED.delete(cb.dataset.alId)
    updateMapSelUi()
  }
})

document.getElementById('btnMapSelectAll')?.addEventListener('click', () => {
  const cbs = document.querySelectorAll<HTMLInputElement>('#mapAlertList input[data-al-id]')
  if(!cbs.length) return
  const allSelected = MAP_SELECTED.size === cbs.length
  MAP_SELECTED.clear()
  cbs.forEach((cb) => {
    cb.checked = !allSelected
    if(!allSelected) MAP_SELECTED.add(cb.dataset.alId!)
  })
  updateMapSelUi()
})

async function mapBulkDelete(payload: any, confirmMsg: string){
  if(!confirm(confirmMsg)) return
  const r = await api.bulkDeleteAlerts(payload)
  if(r.ok && r.data?.success){
    toast(`Eliminadas ${r.data.deleted} alerta(s)`,'ok')
    MAP_SELECTED.clear()
    renderMap()
    if(CP === 'alerts') renderAlerts().catch(()=>{})
    renderDash().catch(()=>{})
  } else {
    toast(r.data?.error || 'Error al eliminar','er')
  }
}

document.getElementById('btnMapDelSel')?.addEventListener('click', () => {
  const ids = Array.from(MAP_SELECTED)
  if(!ids.length) return
  mapBulkDelete({ ids }, `Eliminar ${ids.length} alerta(s) seleccionada(s)? Esta accion no se puede deshacer.`)
})

document.getElementById('btnMapDelAll')?.addEventListener('click', () => {
  const periodLabel: Record<string,string> = {all:'todo el historial', today:'hoy', week:'esta semana', month:'este mes', year:'este año'}
  const typeLabel: Record<string,string> = {all:'todos los tipos', security:'Seguridad', medical:'Alerta Medica', fire:'Bomberos'}
  mapBulkDelete({ filter: { period: MAP_PERIOD, type: MAP_FILTER } },
    `Eliminar TODAS las alertas del filtro actual (periodo: ${periodLabel[MAP_PERIOD]||MAP_PERIOD}, tipo: ${typeLabel[MAP_FILTER]||MAP_FILTER})? Esta accion no se puede deshacer.`)
})

const mapFilEl = document.getElementById('mapFil');
if (mapFilEl) mapFilEl.addEventListener('click', (e:any)=>{
  const btn = e.target.closest('[data-mf]')
  if(!btn) return
  document.querySelectorAll('#mapFil .tfb').forEach((b:any)=>b.classList.remove('act'))
  btn.classList.add('act')
  MAP_FILTER = btn.dataset.mf
  renderMap()
})

const mapPeriodEl = document.getElementById('mapPeriodFil');
if (mapPeriodEl) mapPeriodEl.addEventListener('click', (e:any)=>{
  const btn = e.target.closest('[data-mp]')
  if(!btn) return
  document.querySelectorAll('#mapPeriodFil .tfb').forEach((b:any)=>b.classList.remove('act'))
  btn.classList.add('act')
  MAP_PERIOD = btn.dataset.mp
  renderMap()
})

// ===== DASHBOARD =====
const _relayStates = new Map<string, boolean>()
let _dashRefreshTimer: any = null
let _iotRefreshTimer: any = null
async function renderDash(){
  if(_dashRefreshTimer){ clearInterval(_dashRefreshTimer); _dashRefreshTimer = null }
  if(API_MODE){
    // Clear top summary stats
    document.getElementById('statsG')!.innerHTML = ''
    let s: any = { active: 0, month: 0, resolved: 0, users: 0 }
    const paintDashStatus = () => {
      updBdg(s.active)
      const banner = document.getElementById('alertBanner')!
      if(s.active > 0){
        banner.style.display = 'flex'
        document.getElementById('abType')!.textContent = 'activa'
        banner.dataset.activeCount = String(s.active)
      } else {
        banner.style.display = 'none'
      }
    }
    // Primer paint desde cache (PWA movil), luego se revalida contra la red
    const cachedAn = dsCacheGet('analytics')
    if(cachedAn?.data?.stats){ s = cachedAn.data.stats; paintDashStatus() }
    // Analytics y dispositivos en paralelo (antes iban en serie)
    await Promise.all([
      api.getAnalytics().then(({ok,data}) => {
        if(ok&&data?.success){
          s=data.stats
          dsCacheSet('analytics', data)
          paintDashStatus()
        }
      }),
      renderDeviceBtns().catch(() => {}),
    ])
    // Auto-refresh device status cada 20s
    _dashRefreshTimer = setInterval(() => {
      if(CP === 'dashboard') renderDeviceBtns().catch(() => {})
      else if(_dashRefreshTimer){ clearInterval(_dashRefreshTimer); _dashRefreshTimer = null }
    }, 20000)
  } else {
    // Local fallback render
    const d=db();if(!d)return
    const ids=visIds(d)
    const ua=d.alerts.filter((a:any)=>ids.includes(a.userId))
    const act=ua.filter((a:any)=>a.status==='active')
    const now=new Date()
    const mo=ua.filter((a:any)=>{const dd=new Date(a.at);return dd.getMonth()===now.getMonth()&&dd.getFullYear()===now.getFullYear()})
    const tu=d.users.filter((u:any)=>ids.includes(u.id)).length
    document.getElementById('statsG')!.innerHTML = ''
    // Active alert banner
    const banner = document.getElementById('alertBanner')!
    if(act.length > 0){
      banner.style.display = 'flex'
      document.getElementById('abType')!.textContent = 'activa'
      banner.dataset.activeCount = String(act.length)
    } else {
      banner.style.display = 'none'
    }
    updBdg(act.length)
  }
}
async function renderDeviceBtns(){
  const container = document.getElementById('deviceBtns')
  if(!container) return
  const fnLabels: Record<string,string> = { panic:'Pánico', gate:'Vehícular', pedestrian:'Peatonal', free:'Libre' }
  const col: Record<string,string> = { sirena:'#ef4444', portero:'#06b6d4', puerta:'#10b981', camara:'#a855f7', sensor:'#f59e0b' }
  const paint = (allDevices: any[]) => {
    if(!allDevices.length){ container.innerHTML = ''; return }
    let h = ''
    allDevices.forEach((dev: any) => {
      const cfg = dev.config_json ? (typeof dev.config_json === 'string' ? JSON.parse(dev.config_json) : dev.config_json) : {}
      const relays: any[] = cfg.relays || []
      const visibleRelays = relays.filter((r:any) => r.show_dashboard !== false && r.label)
      if(!visibleRelays.length) return
      h += `<section class="device-block">
        <div class="device-header">
          <div class="dh-name">${esc(cfg.display_name || dev.name)}</div>
          <div class="dh-status ${dev.status}" data-device-id="${dev.id}" role="status">${devStatusInner(dev.status)}</div>
        </div>
        <div class="relay-grid" data-count="${visibleRelays.length}">`
      visibleRelays.forEach((r:any) => {
        const action = r.duration ? 'pulse' : 'toggle'
        h += `<button class="relay-btn" data-device="${dev.id}" data-relay-idx="${r.id-1}" data-relay-action="${action}" data-relay-duration="${r.duration||''}" data-relay-icon="${r.icon||'fa-plug'}" data-relay-fn="${r.function||'free'}" data-relay-siren="${r.trigger_siren?'1':'0'}" ${dev.status!=='online'?'disabled':''}>
          <i class="fas ${r.icon||'fa-plug'} r-icon" style="color:${col[dev.type]||'var(--fg3)'}"></i>
          <span>${esc(r.label)}</span>
        </button>`
      })
      h += `</div></section>`
    })
    container.innerHTML = h
  }
  const devCacheKey = 'devices_' + (CU?.id || 'x')
  // Paint instantaneo desde cache + indicador mientras revalida (sin parpadeo en auto-refresh)
  if(!container.innerHTML.trim()){
    const cachedDv = dsCacheGet(devCacheKey)?.data
    container.innerHTML = cachedDv?.length ? '' : loadingState('Cargando dispositivos...')
    if(cachedDv?.length) paint(cachedDv)
  }
  const groupIds = CU?.group_ids || (CU?.group_id ? [CU.group_id] : [])
  let allDevices: any[] = []
  let failed = false
  if(['superadmin','admin'].includes(CU?.role)){
    try {
      const ra = await api.getDevices()
      if(ra.ok && ra.data?.devices) allDevices = ra.data.devices
      else failed = true
    } catch { failed = true }
  } else {
    const rs = await Promise.all(groupIds.map((gid:string) => api.getGroupDevices(gid)))
    rs.forEach((r) => { if(r.ok && r.data?.devices) allDevices = allDevices.concat(r.data.devices); else failed = true })
  }
  // Sin red: conservar lo pintado desde cache en vez de vaciar los botones
  if(failed && !allDevices.length && dsCacheGet(devCacheKey)?.data?.length) return
  if(!failed) dsCacheSet(devCacheKey, allDevices)
  paint(allDevices)
}

async function handleRelayAction(relayBtn: HTMLElement, deviceId: string, relayId: number, action: string){
  const relayFn = relayBtn.dataset.relayFn || 'free'
  const triggerSiren = relayBtn.dataset.relaySiren === '1'
  const actionLabel = relayFn === 'gate' ? 'Apertura de portón' : relayFn === 'pedestrian' ? 'Apertura de puerta' : ''
  if(actionLabel){
    const eventType = relayFn === 'gate' ? 'gate.opened' : 'door.opened'
    await api.logEvent(eventType, 'device', deviceId, actionLabel, { relay_id: relayId, action }).catch(() => {})
  }
  if(triggerSiren){
    const dr = await api.getDevices().catch(() => null)
    if(dr?.ok){
      for(const d of dr.data.devices.filter((x:any)=>x.panic_relay==1)){
        const cfg = d.config_json ? (typeof d.config_json === 'string' ? JSON.parse(d.config_json) : d.config_json) : {}
        const panicRelay = (cfg.relays||[]).find((r:any)=>r.function==='panic')
        const dur = panicRelay?.duration ?? 1
        await api.sendDeviceCommand(d.id, 'relay_pulse', {relay_id:0, duration: dur})
      }
    }
  }
}

// Click handler for device buttons (dashboard)
document.addEventListener('click', async (e) => {
  const relayBtn = (e.target as HTMLElement).closest('[data-relay-action]') as HTMLElement | null
  if(relayBtn){
    if((relayBtn as HTMLButtonElement).disabled) return
    const deviceId = relayBtn.dataset.device!
    const relayIdx = parseInt(relayBtn.dataset.relayIdx!)
    const action = relayBtn.dataset.relayAction || 'toggle'
    const duration = relayBtn.dataset.relayDuration ? parseFloat(relayBtn.dataset.relayDuration) : undefined
    // Animación: spinning
    relayBtn.classList.add('sending')
    const iconEl = relayBtn.querySelector('.r-icon') as HTMLElement
    if(iconEl) iconEl.className = 'fas fa-spinner r-icon'
    const params: any = { relay_id: relayIdx }
    if(duration !== undefined) params.duration = duration
    const r = await api.sendDeviceCommand(deviceId, 'relay_'+action, params)
    relayBtn.classList.remove('sending')
    if(r.ok && r.data?.sent > 0){
      relayBtn.classList.add('sent')
      if(iconEl) iconEl.className = `fas fa-check r-icon`
      setTimeout(() => { relayBtn.classList.remove('sent'); if(iconEl) iconEl.className = `fas ${relayBtn.dataset.relayIcon||'fa-plug'} r-icon` }, 1200)
      handleRelayAction(relayBtn, deviceId, relayIdx, action)
    } else {
      relayBtn.classList.add('error')
      if(iconEl) iconEl.className = 'fas fa-xmark r-icon'
      setTimeout(() => { relayBtn.classList.remove('error'); if(iconEl) iconEl.className = `fas ${relayBtn.dataset.relayIcon||'fa-plug'} r-icon` }, 1200)
    }
    return
  }
  const dashRelay = (e.target as HTMLElement).closest('[data-dash-relay]') as HTMLElement | null
  if(dashRelay){
    const id = dashRelay.dataset.dashRelay!
    const relayId = parseInt(dashRelay.dataset.relayId!)
    const action = dashRelay.dataset.relayAction || 'toggle'
    const duration = dashRelay.dataset.relayDuration ? parseInt(dashRelay.dataset.relayDuration) : undefined
    const params: any = { relay_id: relayId }
    if(duration !== undefined) params.duration = duration
    const r = await api.sendDeviceCommand(id, 'relay_'+action, params)
    if(r.ok && r.data?.sent > 0) toast('Comando enviado al rele','ok')
    else toast(r.data?.error || 'Dispositivo desconectado','wa')
    return
  }
  const btn = (e.target as HTMLElement).closest('[data-dash-cmd]') as HTMLElement | null
  if(!btn) return
  const id = btn.dataset.dashCmd!
  const action = btn.dataset.dashAction || 'activate'
  const r = await api.sendDeviceCommand(id, action)
  if(r.ok && r.data?.sent > 0) toast(`Comando "${action}" enviado al dispositivo`,'ok')
  else toast(r.data?.error || 'Dispositivo desconectado','wa')
})

function localRecentFallback(){ return emptyState('Sin alertas recientes','fa-bell-slash') }
function visIds(d:any){
  if(CU.role==='superadmin') return d.users.map((u:any)=>u.id)
  if(CU.role==='admin') return d.users.filter((u:any)=>u.group===CU.group||u.id===CU.id).map((u:any)=>u.id)
  return [CU.id]
}
function updBdg(count:number){
  const b1=document.getElementById('topBadge')!
  const b2=document.getElementById('navBdg')!
  if(count>0){ b1.style.display='inline'; b1.textContent=String(count); b2.style.display='inline'; b2.textContent=String(count); }
  else { b1.style.display='none'; b2.style.display='none'; }
}
function emptyState(text:string,icon:string){return `<div class="empty"><i class="fas ${icon}"></i><p>${text}</p></div>`}
function loadingState(text='Cargando...'){return `<div class="empty ds-loading"><i class="fas fa-circle-notch fa-spin"></i><p>${text}</p></div>`}
function skelGrid(n=6){let h='<div class="skel-grid">';for(let i=0;i<n;i++)h+='<div class="skel"></div>';return h+'</div>'}
// Cache local stale-while-revalidate para el dashboard (PWA movil: primer paint desde cache)
const DS_CACHE_TTL = 5*60*1000
function dsCacheGet(key:string){try{const raw=localStorage.getItem('sp_cache_'+key);if(!raw)return null;return JSON.parse(raw)}catch{return null}}
function dsCacheSet(key:string,data:any){try{localStorage.setItem('sp_cache_'+key,JSON.stringify({ts:Date.now(),data}))}catch{}}
function dsCacheFresh(entry:any){return !!entry && (Date.now()-entry.ts) < DS_CACHE_TTL}

// ===== ALERTS PAGE =====
async function renderAlerts(){
  const list = document.getElementById('allAl')!
  if(API_MODE){
      const qs = buildAlertQs()
    list.innerHTML = loadingState('Cargando alertas...')
    const r=await api.getAlerts(`?limit=100${qs}&_t=${Date.now()}`)
    if(r.ok&&r.data.success&&r.data.alerts.length){
      list.innerHTML=r.data.alerts.map((a:any)=>alertHtml(a,(a.name && a.lastname ? a.name + ' ' + a.lastname : '')||'')).join('')
    } else {
      list.innerHTML=emptyState('No hay alertas','fa-bell-slash')
    }
  } else {
    const d=db(); if(!d) return
    const ids=visIds(d)
    const al=d.alerts.filter((a:any)=>ids.includes(a.userId))
    al.sort((a:any,b:any)=>new Date(b.at).getTime()-new Date(a.at).getTime())
    list.innerHTML=al.length?al.map((a:any)=>alertHtml(a)).join(''):emptyState('No hay alertas','fa-bell-slash')
  }
}

function buildAlertQs(){
  let q=''
  const activeBtn = document.querySelector('#alertFil .tfb.act') as HTMLElement|null
  const f = activeBtn?.dataset.f
  if(f && f!=='all'){
    if(f==='active') q+='&status=active'
    else q+=`&type=${f}`
  }
  const fromV=(document.getElementById('afFrom') as HTMLInputElement|null)?.value
  const toV=(document.getElementById('afTo') as HTMLInputElement|null)?.value
  if(fromV) q+=`&from=${fromV}`
  if(toV) q+=`&to=${toV}`
  return q
}
(document.getElementById('alertFil') as HTMLElement|null)?.addEventListener('click',(e)=>{
  const btn=(e.target as HTMLElement).closest('.tfb') as HTMLElement|null
  if(!btn)return
  document.querySelectorAll('#alertFil .tfb').forEach((b)=>b.classList.remove('act'))
  btn.classList.add('act')
  const f=btn.dataset.f
  if(API_MODE){
    document.getElementById('allAl')!.innerHTML = loadingState('Cargando alertas...')
    api.getAlerts(`?limit=100${f&&f!=='all'?'type='+f:f==='active'?'&status=active':''}`).then((r:any)=>{
      if(r.ok&&r.data.success) document.getElementById('allAl')!.innerHTML=r.data.alerts.length?r.data.alerts.map((a:any)=>alertHtml(a,(a.name && a.lastname ? a.name + ' ' + a.lastname : '')||'')).join(''):emptyState('No hay alertas','fa-bell-slash')
    })
  }
})

// ===== PANIC =====
document.getElementById('btnPanic')!.onclick=()=>{
  document.getElementById('panicExp')!.classList.add('show')
  loadEmergencyNumbers()
}
document.getElementById('peClose')!.onclick=()=>document.getElementById('panicExp')!.classList.remove('show')
document.querySelectorAll('.po').forEach((el)=>{
  const node=el as HTMLElement
  node.onclick=()=>triggerAlert(node.dataset.type!)
  node.onkeydown=(e:KeyboardEvent)=>{if(e.key==='Enter') triggerAlert(node.dataset.type!)}
})

async function loadEmergencyNumbers(){
  const list = document.getElementById('peEmergencyList')!
  if(!API_MODE){
    list.innerHTML = `<a class="pe-emergency-btn" href="tel:133"><i class="fas fa-shield-halved" style="color:#3b82f6"></i><div><div class="en-name">Carabineros</div><div class="en-phone">133</div></div></a><a class="pe-emergency-btn" href="tel:132"><i class="fas fa-fire-extinguisher" style="color:#f97316"></i><div><div class="en-name">Bomberos</div><div class="en-phone">132</div></div></a><a class="pe-emergency-btn" href="tel:131"><i class="fas fa-heart-pulse" style="color:#10b981"></i><div><div class="en-name">Ambulancia</div><div class="en-phone">131</div></div></a>`
    return
  }
  try {
    const groupId = currentGroupId()
    const r = await api.getEmergencyNumbers(groupId)
    if(r.ok && r.data?.success && r.data.numbers?.length){
      list.innerHTML = r.data.numbers.map((n:any)=>
        `<a class="pe-emergency-btn" href="tel:${n.phone}"><i class="fas ${n.icon||'fa-phone'}" style="color:${n.color||'#ef4444'}"></i><div><div class="en-name">${esc(n.name)}</div><div class="en-phone">${esc(n.phone)}</div></div></a>`
      ).join('')
    } else {
      list.innerHTML = '<div style="font-size:12px;color:var(--fg3);text-align:center">Sin numeros de emergencia configurados</div>'
    }
  } catch(e){
    list.innerHTML = '<div style="font-size:12px;color:var(--fg3);text-align:center">Error cargando numeros</div>'
  }
}

async function triggerAlert(type:string){
  document.getElementById('panicExp')!.classList.remove('show')
  const cfg:any={security:{ico:'fa-shield-halved',txt:'Alerta de Seguridad',col:'var(--red)',bg:'linear-gradient(135deg,#ef4444,#991b1b)'},medical:{ico:'fa-heart-pulse',txt:'Alerta Medica',col:'var(--amber)',bg:'linear-gradient(135deg,#f59e0b,#92400e)'},fire:{ico:'fa-fire-extinguisher',txt:'Alerta de Bomberos',col:'#f97316',bg:'linear-gradient(135deg,#f97316,#9a3412)'}}[type]
  const ov=document.getElementById('alertOv')!
  const iconEl=document.getElementById('aaIcon') as HTMLElement
  iconEl.style.background=cfg.bg
  iconEl.innerHTML=`<i class="fas ${cfg.ico}"></i>`
  document.getElementById('aaText')!.textContent=cfg.txt+' Activa'
  ;(document.getElementById('aaText') as HTMLElement).style.color=cfg.col
  document.getElementById('aaSub')!.textContent='Enviando notificaciones via WhatsApp y llamada automatica...'
  ov.classList.add('show')

  ASec=0;AId=null
  if(AT){clearInterval(AT);AT=null}
  AT=window.setInterval(()=>{
    ASec++
    document.getElementById('aaTimer')!.textContent=String(Math.floor(ASec/60)).padStart(2,'0')+':'+String(ASec%60).padStart(2,'0')
  },1000)

  if(API_MODE){
    const payload = {type,lat:ULoc.lat,lng:ULoc.lng,accuracy:ULoc.accuracy}
    console.log('[PANIC] Enviando alerta:', payload)
    const r=await api.createAlert(payload)
    console.log('[PANIC] Respuesta:', r)
    if(r.ok&&r.data.success){
      AId=r.data.alert.id
      toast('Alerta activada y enviada','ok')
      // Enviar pulso directo a dispositivos panic_relay
      const dr = await api.getDevices().catch(() => null)
      if(dr?.ok) {
        for(const d of dr.data.devices.filter((x:any)=>x.panic_relay==1 && x.status==='online')){
          const cfg = d.config_json ? (typeof d.config_json === 'string' ? JSON.parse(d.config_json) : d.config_json) : {}
          const panicRelay = (cfg.relays||[]).find((r:any)=>r.function==='panic')
          const dur = panicRelay?.duration ?? 1
          await api.sendDeviceCommand(d.id, 'relay_pulse', {relay_id:0, duration: dur})
          console.log('[PANIC] Pulso enviado a', d.name, `duracion=${dur}s`)
        }
      }
    } else {
      toast('Error al activar alerta: ' + (r.data?.error || r.data?.reason || 'desconocido'),'er')
      console.error('[PANIC] Error:', r)
    }
  } else {
    const d=db()!
    const alert={id:gid(),userId:CU.id,type,status:'active',lat:ULoc.lat,lng:ULoc.lng,at:new Date().toISOString(),rt:null}
    d.alerts.push(alert);dbSave(d);AId=alert.id
  }
}

document.getElementById('aaCancel')!.onclick=cancelAlert
document.getElementById('aaBack')!.onclick=()=>{
  document.getElementById('alertOv')!.classList.remove('show')
  nav('dashboard')
}

// Alert banner: ver detalles
document.getElementById('abView')!.onclick = async () => {
  go('/legal/alerts')
}

async function showAlertNotifications(alertId: string) {
  const r = await api.getAlertNotifications(alertId)
  if(!r.ok || !r.data?.success){ toast('Error cargando notificaciones','er'); return }
  const list = (r.data.notifications || []) as any[]
  if(!list.length){ toast('Sin notificaciones registradas','in'); return }
  const html = list.map(n => `<tr><td>${esc(n.channel||'')}</td><td>${esc(n.recipient||'')}</td><td>${esc(n.status||'')}</td><td>${esc(n.sent_at||'')}</td></tr>`).join('')
  document.getElementById('legalTitle')!.textContent = 'Notificaciones de alerta'
  document.getElementById('legalBody')!.innerHTML = `<table style='width:100%;font-size:13px'><thead><tr><th>Canal</th><th>Destinatario</th><th>Estado</th><th>Enviado</th></tr></thead><tbody>${html}</tbody></table>`
  document.getElementById('modLegal')!.classList.add('show')
}
document.addEventListener('click', async (e: Event) => {
  const t = e.target as HTMLElement
  const notBtn = t.closest('[data-al-not]') as HTMLElement | null
  if(notBtn && notBtn.dataset.alNot){ showAlertNotifications(notBtn.dataset.alNot); return }
  const resolveBtn = t.closest('[data-al-resolve]') as HTMLElement | null
  if(resolveBtn && resolveBtn.dataset.alResolve){
    const id = resolveBtn.dataset.alResolve
    if(!confirm('Resolver esta alerta como solucionada?')) return
    if(API_MODE){
      const res = await api.resolveAlert(id)
      console.log('[RESOLVE] response:', res)
      if(!res.ok || !res.data?.success){ toast(res.data?.error || 'Error al resolver','er'); return }
      // Enviar pulso a dispositivos panic_relay
      const dr = await api.getDevices().catch(() => null)
      if(dr?.ok) for(const d of dr.data.devices.filter((x:any)=>x.panic_relay==1)){
        const cfg = d.config_json ? (typeof d.config_json === 'string' ? JSON.parse(d.config_json) : d.config_json) : {}
        const panicRelay = (cfg.relays||[]).find((r:any)=>r.function==='panic')
        const dur = panicRelay?.duration ?? 1
        await api.sendDeviceCommand(d.id,'relay_pulse',{relay_id:0, duration: dur})
        console.log('[RESOLVE] Pulso enviado a', d.name, `duracion=${dur}s`)
      }
    }
    toast('Alerta resuelta','ok')
    // Refrescar solo según la página actual
    if(CP === 'alerts'){
      await renderAlerts().catch(() => {})
    } else if(CP === 'dashboard'){
      await renderDash().catch(() => {})
    }
    // Recalcular badge
    const ar = await api.getAlerts('?limit=1&status=active&_t='+Date.now()).catch(() => null)
    if(ar?.ok) updBdg(parseInt(ar.data?.total || '0'))
    // Refrescar modal de alertas activas si está abierto
    const legalModal = document.getElementById('modLegal')!
    if(legalModal.classList.contains('show')){
      const rr = await api.getAlerts('?limit=5&status=active&_t='+Date.now()).catch(() => null)
      if(rr?.ok && rr.data?.alerts?.length){
        document.getElementById('legalTitle')!.textContent = 'Alertas Activas (' + rr.data.alerts.length + ')'
        document.getElementById('legalBody')!.innerHTML = rr.data.alerts.map((a:any) => alertHtml(a, a.name && a.lastname ? a.name+' '+a.lastname : '')).join('')
      } else {
        legalModal.classList.remove('show')
        const banner = document.getElementById('alertBanner')!
        if(banner) banner.style.display = 'none'
      }
    }
  }
})
async function cancelAlert(){
  console.log('[CANCEL] Cancelando alerta...', {AId})
  if(AT){clearInterval(AT);AT=null}
  document.getElementById('alertOv')!.classList.remove('show')
  if(AId){
    if(API_MODE){
      const res = await api.cancelAlert(AId)
      console.log('[CANCEL] Respuesta:', res)
      // Enviar pulso a dispositivos panic_relay
      const dr = await api.getDevices().catch(() => null)
      if(dr?.ok) for(const d of dr.data.devices.filter((x:any)=>x.panic_relay==1)){
        const cfg = d.config_json ? (typeof d.config_json === 'string' ? JSON.parse(d.config_json) : d.config_json) : {}
        const panicRelay = (cfg.relays||[]).find((r:any)=>r.function==='panic')
        const dur = panicRelay?.duration ?? 1
        await api.sendDeviceCommand(d.id,'relay_pulse',{relay_id:0, duration: dur})
        console.log('[CANCEL] Pulso enviado a', d.name, `duracion=${dur}s`)
      }
    } else {
      const d=db()!; const a=d.alerts.find((x:any)=>x.id===AId); if(a){a.status='cancelled';a.rt=new Date().toISOString();dbSave(d);}
    }
    AId=null
  }
  toast('Alerta cancelada y registrada','ok')
  if(CP==='dashboard') await renderDash().catch(() => {}); else if(CP==='alerts') await renderAlerts().catch(() => {})
  // Recalcular badge
  const ar = await api.getAlerts('?limit=1&status=active&_t='+Date.now()).catch(() => null)
  if(ar?.ok) updBdg(parseInt(ar.data?.total || '0'))
}

// ===== USERS =====
let _usersCache: any[] = []
async function loadUsersCache(){
  if(!API_MODE) return []
  const r = await api.getUsers()
  if(r.ok && r.data?.success){ _usersCache = r.data.users || [] }
  return _usersCache
}

async function renderUsers(){
  if(API_MODE){
    if(!document.getElementById('uTbody')!.innerHTML.trim()) document.getElementById('uTbody')!.innerHTML = loadingState('Cargando usuarios...')
    if(!_groupsCache.length) await loadGroupsCache()
    const r=await api.getUsers()
    if(r.ok&&r.data.success){
      const users = r.data.users
      _usersCache = users
      const filter = ((document.getElementById('uSearch') as HTMLInputElement)?.value || '').toLowerCase()
      const filtered = filter ? users.filter((u:any) => (u.name+' '+u.lastname).toLowerCase().includes(filter) || u.email.toLowerCase().includes(filter)) : users
      const colors=['#ef4444','#f59e0b','#10b981','#06b6d4','#a855f7','#ec4899','#f97316']
      let h=''
      filtered.forEach((u:any,i:number)=>{
        const c=colors[i%colors.length]
        const ini=(u.name[0]+u.lastname[0]).toUpperCase()
        h+=`<tr><td data-label="Usuario"><div class="uc"><div class="ua" style="background:${c}">${ini}</div><div><div class="un">${esc(u.name+' '+u.lastname)}</div><div class="ue">${esc(u.email)}</div></div></div></td>`
        h+=`<td data-label="Rol"><span class="rb ${u.role}">${u.role}</span></td>`
        h+=`<td data-label="Plan"><span class="pb2 ${u.plan}">${u.plan}</span></td>`
        const groupNames = u.group_ids?.length
          ? u.group_ids.map((gid:string)=>_groupsCache.find((g:any)=>g.id===gid)).filter(Boolean).map((g:any)=>esc(g.name)).join(', ')
          : esc(u.group_name||'—')
        h+=`<td data-label="Grupos" style="font-size:12px;color:var(--fg3)">${groupNames}</td>`
        h+=`<td data-label="Estado"><span class="st ${u.active?'resolved':'active'}">${u.active?'Activo':'Inactivo'}</span></td>`
        h+=`<td data-label="Acciones"><div class="ac">`
        h+=`<button class="ab ed" data-edit="${u.id}" title="Editar"><i class="fas fa-pen"></i></button>`
        if(u.id!==CU.id) h+=`<button class="ab" data-toggle="${u.id}" title="${u.active?'Desactivar':'Activar'}"><i class="fas fa-${u.active?'ban':'check'}"></i></button>`
        if(u.id!==CU.id&&CU.role==='superadmin') h+=`<button class="ab" data-welcome="${u.id}" title="Enviar WhatsApp"><i class="fab fa-whatsapp" style="color:#25D366"></i></button>`
        if(u.id!==CU.id&&CU.role==='superadmin') h+=`<button class="ab" data-del="${u.id}" title="Eliminar"><i class="fas fa-trash"></i></button>`
        h+=`</div></td></tr>`
      })
      document.getElementById('uTbody')!.innerHTML=h||emptyTr()
    }
  } else {
    const d=db();if(!d)return
    const users=CU.role==='superadmin'?d.users:d.users.filter((u:any)=>u.group===CU.group||u.id===CU.id)
    const filter = ((document.getElementById('uSearch') as HTMLInputElement)?.value || '').toLowerCase()
    const filtered = filter ? users.filter((u:any) => (u.name+' '+u.lastname).toLowerCase().includes(filter) || u.email.toLowerCase().includes(filter)) : users
    const colors=['#ef4444','#f59e0b','#10b981','#06b6d4','#a855f7','#ec4899','#f97316']
    let h=''
    filtered.forEach((u:any,i:number)=>{
      const c=colors[i%colors.length]
      const ini=(u.name[0]+u.lastname[0]).toUpperCase()
      h+=`<tr><td data-label="Usuario"><div class="uc"><div class="ua" style="background:${c}">${ini}</div><div><div class="un">${esc(u.name+' '+u.lastname)}</div><div class="ue">${esc(u.email)}</div></div></div></td>`
      h+=`<td data-label="Rol"><span class="rb ${u.role}">${u.role}</span></td>`
      h+=`<td data-label="Plan"><span class="pb2 ${u.plan}">${u.plan}</span></td>`
      h+=`<td data-label="Grupo" style="font-size:12px;color:var(--fg3)">${esc(u.group||'—')}</td>`
      h+=`<td data-label="Estado"><span class="st ${u.active?'resolved':'active'}">${u.active?'Activo':'Inactivo'}</span></td>`
      h+=`<td data-label="Acciones"><div class="ac">`
      h+=`<button class="ab ed" data-edit="${u.id}" title="Editar"><i class="fas fa-pen"></i></button>`
      if(u.id!==CU.id) h+=`<button class="ab" data-toggle="${u.id}" title="${u.active?'Desactivar':'Activar'}"><i class="fas fa-${u.active?'ban':'check'}"></i></button>`
      if(u.id!==CU.id&&CU.role==='superadmin') h+=`<button class="ab" data-welcome="${u.id}" title="Enviar WhatsApp"><i class="fab fa-whatsapp" style="color:#25D366"></i></button>`
      if(u.id!==CU.id&&CU.role==='superadmin') h+=`<button class="ab" data-del="${u.id}" title="Eliminar"><i class="fas fa-trash"></i></button>`
      h+=`</div></td></tr>`
    })
    document.getElementById('uTbody')!.innerHTML=h||emptyTr()
  }
}
function emptyTr(){return `<tr><td colspan="6"><div class="empty"><i class="fas fa-users-slash"></i><p>Sin usuarios</p></div></td></tr>`}

document.getElementById('uTbody')!.onclick=(e)=>{
  const btn=(e.target as HTMLElement).closest('.ab') as HTMLElement|null
  if(!btn)return
  if(btn.dataset.edit) openUserMod(btn.dataset.edit)
  else if(btn.dataset.toggle) toggleUserSt(btn.dataset.toggle)
  else if(btn.dataset.welcome) sendUserWelcome(btn.dataset.welcome)
  else if(btn.dataset.del) delUser(btn.dataset.del)
}

document.getElementById('uSearch')?.addEventListener('input', () => renderUsers())
document.getElementById('btnNewUser')!.onclick=()=>openUserMod()

function renderGroupGrid(selectedIds: string[] = [], filter = ''){
  const grid = document.getElementById('ufGGrid')!
  grid.innerHTML = ''
  _groupsCache.forEach(g => {
    if(g.active === 0 && CU.role !== 'superadmin') return
    if(filter && !g.name.toLowerCase().includes(filter.toLowerCase())) return
    const checked = selectedIds.includes(g.id)
    const label = document.createElement('label')
    label.style.cssText = 'display:flex;align-items:center;gap:4px;padding:4px 8px;border-radius:4px;border:1px solid var(--bdr);cursor:pointer;font-size:12px;background:' + (checked ? 'var(--card)' : 'transparent')
    label.innerHTML = `<input type="checkbox" value="${esc(g.id)}" ${checked?'checked':''} style="accent-color:var(--red)"> ${esc(g.name)}`
    grid.appendChild(label)
  })
}

// Modales convertidos en páginas con ruta: /users/new y /users/:id
function openUserMod(uid?: string){
  go(uid ? `/users/${encodeURIComponent(uid)}` : '/users/new')
}

async function renderUserPage(uid?:string){
  const d=API_MODE ? null : db(); if(!API_MODE && !d) return
  ;(document.getElementById('frmUser') as HTMLFormElement).reset()
  ;(document.getElementById('ufId') as HTMLInputElement).value=''
  ;(document.getElementById('ufPs') as HTMLInputElement).value=''
  ;(document.getElementById('ufGSearch') as HTMLInputElement).value=''
  document.getElementById('muTitle')!.textContent='Nuevo Usuario'
  document.getElementById('ufPh')!.textContent=''
  // Valores intencionales del formulario: se re-aplican tras el autofill del
  // navegador/gestores de contrasenas, para que un campo que debe quedar vacio
  // no aparezca con datos memorizados de otra pantalla.
  const intended: Record<string,string> = { ufN:'', ufL:'', ufE:'', ufP:'', ufPs:'' }

  const rs=document.getElementById('ufR') as HTMLSelectElement
  rs.innerHTML=CU.role==='admin'?'<option value="user">Usuario</option>':'<option value="user">Usuario</option><option value="admin">Admin</option><option value="superadmin">SuperAdmin</option>'

  let selectedGroups: string[] = []
  if(API_MODE){
    await loadGroupsCache()
    if(uid){
      if(!_usersCache.length) await loadUsersCache()
      const u = _usersCache.find((x:any)=>x.id===uid)
      if(u) selectedGroups = u.group_ids || (u.group_id ? [u.group_id] : [])
    }
    renderGroupGrid(selectedGroups)
  } else {
    if(uid && d){
      const u2 = d.users.find((x:any)=>x.id===uid)
      if(u2 && u2.group) selectedGroups = [u2.group]
    }
  }

  let u: any = null
  if(uid){
    if(API_MODE){
      if(!_usersCache.length) await loadUsersCache()
      u = _usersCache.find((x:any)=>x.id===uid)
    } else if(d){
      u = d.users.find((x:any)=>x.id===uid)
    }
    if(!u){ toast('Usuario no encontrado','er'); go('/users'); return }
    document.getElementById('muTitle')!.textContent='Editar Usuario'
    ;(document.getElementById('ufId') as HTMLInputElement).value=u.id
    ;(document.getElementById('ufN') as HTMLInputElement).value=u.name || ''
    ;(document.getElementById('ufL') as HTMLInputElement).value=u.lastname || ''
    ;(document.getElementById('ufE') as HTMLInputElement).value=u.email || ''
    ;(document.getElementById('ufP') as HTMLInputElement).value=u.phone || ''
    ;(document.getElementById('ufR') as HTMLSelectElement).value=u.role || 'user'
    ;(document.getElementById('ufPn') as HTMLSelectElement).value=u.plan || 'free'
    document.getElementById('ufPh')!.textContent='(vacio = sin cambios)'
    intended.ufN = u.name || ''
    intended.ufL = u.lastname || ''
    intended.ufE = u.email || ''
    intended.ufP = u.phone || ''
  }
  // Default group selector (nuevo y editar): todos los grupos visibles para mi rol,
  // no solo los del usuario. Superadmin ve todos; admin ve los que administra (cache filtrada por backend).
  const defSel = document.getElementById('ufGDef') as HTMLSelectElement
  defSel.innerHTML = '<option value="">Sin grupo</option>'
  _groupsCache.forEach((g:any) => {
    if(g.active === 0 && CU.role !== 'superadmin') return
    const o = document.createElement('option')
    o.value = g.id; o.textContent = g.name
    if(u && g.id === u.default_group_id) o.selected = true
    defSel.appendChild(o)
  })
  reassertUserModalFields(intended)
  document.getElementById('ufSubmitLabel')!.textContent = u ? 'Guardar cambios' : 'Crear usuario'
  document.getElementById('muTitle')!.textContent = u ? 'Editar usuario' : 'Nuevo usuario'
}
// Chrome/gestores de contrasenas pueden rellenar inputs del modal de forma
// asincrona (al mostrarlo o al reactivar el campo). Re-aplicamos los valores
// intencionales salvo que el usuario ya haya editado ese campo.
let _userModalTimers: any[] = []
function reassertUserModalFields(intended: Record<string,string>){
  _userModalTimers.forEach(t => clearTimeout(t))
  _userModalTimers = []
  const touched = new Set<string>()
  const ids = Object.keys(intended)
  const handlers: Array<() => void> = []
  ids.forEach(id => {
    const el = document.getElementById(id) as HTMLInputElement | null
    if(!el) return
    // El autofill del navegador rellena sin enfocar ni teclear. Enfocar, teclear,
    // pegar o escribir (input) marca el campo como del usuario y no se re-aplica.
    const h = () => touched.add(id)
    el.addEventListener('focus', h)
    el.addEventListener('keydown', h)
    el.addEventListener('paste', h)
    el.addEventListener('input', h)
    handlers.push(() => {
      el.removeEventListener('focus', h)
      el.removeEventListener('keydown', h)
      el.removeEventListener('paste', h)
      el.removeEventListener('input', h)
    })
  })
  const apply = () => ids.forEach(id => {
    if(touched.has(id)) return
    const el = document.getElementById(id) as HTMLInputElement | null
    if(!el) return
    const want = intended[id] ?? ''
    if(el.value !== want) el.value = want
  })
  apply()
  _userModalTimers = [
    setTimeout(apply, 80),
    setTimeout(apply, 350),
    setTimeout(apply, 900),
    setTimeout(() => { apply(); handlers.forEach(h => h()) }, 1600),
  ]
}
// Group search filter
document.getElementById('ufGSearch')?.addEventListener('input', () => {
  const grid = document.getElementById('ufGGrid')
  if(!grid) return
  const checked: string[] = []
  grid.querySelectorAll('input[type=checkbox]:checked').forEach(cb => checked.push((cb as HTMLInputElement).value))
  renderGroupGrid(checked, (document.getElementById('ufGSearch') as HTMLInputElement).value)
})

document.getElementById('frmUser')!.onsubmit=async (e)=>{
  e.preventDefault()
  const id=(document.getElementById('ufId') as HTMLInputElement).value
  const email=(document.getElementById('ufE') as HTMLInputElement).value.trim().toLowerCase()
  const pass=(document.getElementById('ufPs') as HTMLInputElement).value
  const groupSel = (document.getElementById('ufG') as HTMLSelectElement)
  const payload:any = {
    name:(document.getElementById('ufN') as HTMLInputElement).value.trim(),
    lastname:(document.getElementById('ufL') as HTMLInputElement).value.trim(),
    email,
    phone:(document.getElementById('ufP') as HTMLInputElement).value.trim(),
    role:(document.getElementById('ufR') as HTMLSelectElement).value,
    plan:(document.getElementById('ufPn') as HTMLSelectElement).value,
  }
  clearFormErrors(document.getElementById('frmUser') as HTMLFormElement | null)
  let invalid = false
  if(!payload.name){ fieldMessage('ufN','Ingresa el nombre'); invalid = true }
  if(!payload.lastname){ fieldMessage('ufL','Ingresa el apellido'); invalid = true }
  if(!email){ fieldMessage('ufE','Ingresa el correo'); invalid = true }
  else if(!EMAIL_RE.test(email)){ fieldMessage('ufE','Ingresa un correo válido'); invalid = true }
  if(!payload.phone){ fieldMessage('ufP','Ingresa el teléfono'); invalid = true }
  if(invalid) return
  if(API_MODE){
    const grid = document.getElementById('ufGGrid')
    if(grid){
      const gids: string[] = []
      grid.querySelectorAll('input[type=checkbox]:checked').forEach(cb => gids.push((cb as HTMLInputElement).value))
      payload.group_ids = gids
      payload.group_id = gids[0] || null
      payload.default_group_id = (document.getElementById('ufGDef') as HTMLSelectElement).value || null
      if(gids.length){
        const g = _groupsCache.find((x:any) => x.id === gids[0])
        if(g) payload.group_name = g.name
      } else {
        payload.group_name = ''
      }
    }
  } else {
    payload.group = groupSel.value.trim()
  }
  if (pass) payload.password = pass
  if(API_MODE){
    const r = id ? await api.updateUser(id, payload) : await api.createUser(payload)
    if(!r.ok || !r.data?.success){ toast(r.data?.error || 'Error al guardar usuario', 'er'); return }
    toast(id?'Usuario actualizado':'Usuario creado','ok')
  } else {
    const d=db()!
    if(d.users.some((u:any)=>u.email===email&&u.id!==id)){toast('Correo ya en uso','er');return}
    if(id){
      const u=d.users.find((x:any)=>x.id===id)!
      u.name=payload.name;u.lastname=payload.lastname;u.email=email;u.phone=payload.phone;
      u.role=payload.role;u.plan=payload.plan;u.group=payload.group
      if(pass) u.password=pass
      dbSave(d); toast('Usuario actualizado','ok')
    } else {
      if(!pass){toast('Contrasena obligatoria','er');return}
      d.users.push({id:gid(),name:payload.name,lastname:payload.lastname,email,phone:payload.phone,password:pass,role:payload.role,plan:payload.plan,group:payload.group,active:true,created:new Date().toISOString().split('T')[0]})
      dbSave(d); toast('Usuario creado','ok')
    }
  }
  go('/users')
}

// Enviar WhatsApp con link de acceso y token para cambio de contrasena
async function sendUserWelcome(id:string){
  if(!confirm('Enviar WhatsApp a este usuario con el link de acceso y token para cambio de contrasena?')) return
  const btn = document.querySelector(`[data-welcome="${id}"]`) as HTMLElement|null
  if(btn) btn.style.opacity = '0.5'
  const r = await api.sendWelcome(id)
  if(btn) btn.style.opacity = '1'
  if(r.ok && r.data?.success){
    if(r.data.dev_token) console.log('[WELCOME] Token:', r.data.dev_token)
    toast(r.data.message || 'Mensaje enviado', 'ok')
  } else {
    toast(r.data?.error || 'Error al enviar', 'er')
  }
}

async function toggleUserSt(id:string){
  if(API_MODE){
    const r = await api.toggleUser(id)
    if(!r.ok || !r.data?.success){ toast('Error al cambiar estado','er'); return }
    toast(r.data.active ? 'Usuario activado' : 'Usuario desactivado', 'ok')
  } else {
    const d=db()!; const u=d.users.find((x:any)=>x.id===id)!; u.active=!u.active; dbSave(d)
    toast('Usuario '+(u.active?'activado':'desactivado'),'ok')
  }
  renderUsers()
}
async function delUser(id:string){
  if(!confirm('Eliminar este usuario permanentemente?')) return
  if(API_MODE){
    const r = await api.deleteUser(id)
    if(!r.ok || !r.data?.success){ toast(r.data?.error || 'Error al eliminar','er'); return }
    toast('Usuario eliminado','ok')
  } else {
    const d=db()!; d.users=d.users.filter((u:any)=>u.id!==id); d.alerts=d.alerts.filter((a:any)=>a.userId!==id); dbSave(d); toast('Usuario eliminado','ok')
  }
  renderUsers()
}

// ===== NOTIFICATIONS =====
async function renderNotif(){
  const notifCards = document.getElementById('notifCards')!
  notifCards.innerHTML = '<div class="empty"><i class="fas fa-circle-notch fa-spin"></i><p>Cargando configuracion...</p></div>'

  const res = API_MODE ? await api.getNotifications() : { ok: true, data: { success: true, config: db()?.ncfg || {} } }
  if (!res.ok || !res.data?.success) {
    notifCards.innerHTML = '<div class="empty"><i class="fas fa-triangle-exclamation"></i><p>Error cargando configuracion</p></div>'
    return
  }
  const cfg = res.data.config || {}

  // Map API field names to UI shorthand
  const c: any = {
    wa_enabled: cfg.wa_enabled ?? 1,
    wa_numbers: cfg.wa_numbers ?? '',
    call_enabled: cfg.call_enabled ?? 1,
    call_number: cfg.call_number ?? '',
    sound: cfg.sound ?? 1,
    vibration: cfg.vibration ?? 1,
    cooldown: cfg.cooldown ?? 30,
    evo_url: cfg.evo_url ?? '',
    evo_key: cfg.evo_key ?? '',
    evo_instance: cfg.evo_instance ?? 'domus-secura-web', // evolution instance default name
    evo_send_path: cfg.evo_send_path ?? '',
    landing_whatsapp: cfg.landing_whatsapp ?? '',
  }

  notifCards.innerHTML = `
    <div class="cc"><h3><i class="fab fa-whatsapp"></i> WhatsApp</h3>
    <div class="tr"><div><div class="tr-l">Enviar alertas via WhatsApp</div><div class="tr-d">Notifica a contactos al activar panico</div></div><button class="tgl ${c.wa_enabled ? 'on' : ''}" id="tglWa" aria-label="WhatsApp"></button></div>
    <div class="fg mt4"><label>Numeros destino (separados por coma)</label><input type="text" class="fi" id="nWaNums" value="${esc(c.wa_numbers)}"></div></div>

    <div class="cc"><h3><i class="fas fa-phone"></i> Llamadas</h3>
    <div class="tr"><div><div class="tr-l">Llamada automatica</div><div class="tr-d">Via Evolution API al activar alerta</div></div><button class="tgl ${c.call_enabled ? 'on' : ''}" id="tglCall" aria-label="Llamada"></button></div>
    <div class="fg mt4"><label>Numero de llamada</label><input type="text" class="fi" id="nCallNum" value="${esc(c.call_number)}"></div></div>

    <div class="cc"><h3><i class="fas fa-mobile-screen"></i> Dispositivo</h3>
    <div class="tr"><div><div class="tr-l">Sonido de alerta</div></div><button class="tgl ${c.sound ? 'on' : ''}" id="tglSnd" aria-label="Sonido"></button></div>
    <div class="tr"><div><div class="tr-l">Vibracion</div></div><button class="tgl ${c.vibration ? 'on' : ''}" id="tglVib" aria-label="Vibracion"></button></div>
    <div class="fg mt4"><label>Cooldown entre alertas (seg)</label><input type="number" class="fi" id="nCd" value="${c.cooldown}" min="5" max="300"></div></div>

    <div class="cc"><h3><i class="fas fa-server"></i> Evolution Manager API</h3>
    <div class="fg"><label>URL Instancia</label><input type="url" class="fi" id="nEvoUrl" value="${esc(c.evo_url)}"></div>
    <div class="fg"><label>API Key</label><input type="password" class="fi" id="nEvoKey" value="${esc(c.evo_key)}"></div>
    <div class="fg"><label>Nombre de instancia Evolution</label><input type="text" class="fi" id="nEvoInstance" value="${esc(c.evo_instance)}" placeholder="domus-secura-web"></div>
    <div class="fg"><label>Path personalizado (opcional, soporta {instance})</label><input type="text" class="fi" id="nEvoSendPath" value="${esc(c.evo_send_path)}" placeholder="auto: prueba /message/sendText, /api/message/sendText, etc"></div>
    <button class="btn btn-s btn-sm mt4" id="btnTestEvo"><i class="fas fa-plug"></i> Probar Conexion</button></div>

    <div class="cc"><h3><i class="fab fa-whatsapp"></i> WhatsApp Landing Page</h3>
    <div class="fg"><label>Numero de contacto WhatsApp en landing</label><input type="tel" class="fi" id="nLandWa" value="${esc(c.landing_whatsapp)}" placeholder="56912345678"></div>
    <p style="font-size:12px;color:var(--fg3);margin-top:6px">Los visitantes de la landing page podran escribirte via WhatsApp a este numero desde el boton "Escribir por WhatsApp".</p></div>`

  const toggleMap: Record<string, string> = {
    tglWa: 'wa_enabled', tglCall: 'call_enabled',
    tglSnd: 'sound', tglVib: 'vibration'
  }

  const sendUpdate = async (updates: Record<string, any>) => {
    if (!API_MODE) {
      const d2 = db()!
      Object.assign(d2.ncfg, updates)
      dbSave(d2)
      toast('Configuracion guardada (local)', 'ok')
      return
    }
    toast('Guardando...', 'in')
    const r = await api.updateNotifications(updates)
    if (r.ok && r.data?.success) {
      toast('Configuracion guardada', 'ok')
    } else {
      toast('Error al guardar', 'er')
    }
  }

  for (const [id, key] of Object.entries(toggleMap)) {
    const el = document.getElementById(id) as HTMLElement | null
    if (!el) continue
    el.onclick = () => {
      const currentlyOn = el.classList.contains('on')
      el.classList.toggle('on')
      sendUpdate({ [key]: currentlyOn ? 0 : 1 })
    }
  }

  const inputMap: Record<string, string> = {
    nWaNums: 'wa_numbers',
    nCallNum: 'call_number',
    nEvoUrl: 'evo_url',
    nEvoKey: 'evo_key',
    nEvoInstance: 'evo_instance',
    nEvoSendPath: 'evo_send_path',
    nLandWa: 'landing_whatsapp',
  }

  for (const [id, key] of Object.entries(inputMap)) {
    const inp = document.getElementById(id) as HTMLInputElement | null
    if (!inp) continue
    inp.addEventListener('change', () => sendUpdate({ [key]: inp.value }))
  }

  const cdInp = document.getElementById('nCd') as HTMLInputElement | null
  if (cdInp) {
    cdInp.addEventListener('change', () => sendUpdate({ cooldown: parseInt(cdInp.value) || 30 }))
  }

  const testBtn = document.getElementById('btnTestEvo')
  if (testBtn) {
    testBtn.onclick = async () => {
      const url = (document.getElementById('nEvoUrl') as HTMLInputElement | null)?.value || ''
      const key = (document.getElementById('nEvoKey') as HTMLInputElement | null)?.value || ''
      if (!url || !key) { toast('Configura URL y API Key', 'er'); return }
      toast('Probando conexion con Evolution (multi-variante)...', 'in')
      const r = await api.testEvo({ evo_url: url, evo_key: key })
      if (r.ok && r.data.success) {
        const usedUrl = r.data.used_url || '(?)'
        const usedHeader = r.data.used_header || '(?)'
        const detected = r.data.detected_instance
        toast(`OK: ${usedHeader} @ ${usedUrl}` + (detected ? ` (instancia: ${detected})` : ''), 'ok')
      } else {
        const attempts = r.data?.attempts || []
        const msg = r.data?.error || 'Error de conexion'
        toast(msg + (attempts.length ? ' (' + attempts.length + ' variantes probadas)' : ''), 'er')
        // Imprimir detalles en consola para debugging
        console.warn('[test-evo]', { error: msg, attempts })
      }
    }
  }
}

// ===== CURRENCY (CLP) =====
function fmtCLP(val:number|string):string{
  const n = typeof val === 'string' ? parseFloat(val) : (val || 0)
  if(n === 0) return 'Gratis'
  return '$' + Math.round(n).toLocaleString('es-CL')
}
function fmtPrice(val:number|string):string{
  const n = typeof val === 'string' ? parseFloat(val) : (val || 0)
  if(n === 0) return 'Gratis'
  return '$' + Math.round(n).toLocaleString('es-CL')
}

async function renderLandingPlans(){
  if(!API_MODE) return
  const container = document.getElementById('landingPlans')
  if(!container) return
  const r = await api.getPlans()
  if(!r.ok || !r.data?.success) return
  const plans = (r.data.plans || []).filter((p:any) => p.active)
  if(!plans.length) return

  const baseInstall: Record<string, string> = {
    gratis: 'Sin tarjeta de credito. Registrate y listo.',
    familiar: 'Instalacion $15.990 o $0 con contrato 24 meses',
    condominio: 'Instalacion $0 con contrato 12 meses + 3 meses gracia'
  }

  let h = ''
  plans.forEach((p: any, i: number) => {
    const nameLower = p.name.toLowerCase()
    const isFree = p.price_type === 'free'
    const feats = (p.features || '').split(',').filter(Boolean)
    let popClass = ''
    let btnClass = 'secondary'
    if(nameLower === 'familiar') { popClass = 'pop'; btnClass = 'accent' }
    else if(isFree) btnClass = 'primary'
    const rvDelay = i === 0 ? '' : (i === 1 ? ' rv-d1' : ' rv-d2')
    const periodLabel = p.period === 'year' ? '/ano' : '/mes'
    const priceHtml = isFree
      ? '$0<span></span>'
      : fmtPrice(p.price) + '<span>' + periodLabel + '</span>'
    const periodHtml = isFree
      ? 'Para siempre'
      : (p.max_users ? 'Por ' + p.max_users + ' usuarios' : 'Plan ' + (p.period === 'year' ? 'anual' : 'mensual'))
    const btnText = isFree ? 'Comenzar Gratuitamente' : 'Comenzar y Escalar'
    const isQuote = p.price_type === 'quote'
    const dataAction = isQuote ? 'quote' : 'register'
    const btnLabel = isQuote ? esc(p.quote_button_text || 'Cotizar') : btnText

    h += `<div class="price-card${popClass ? ' pop' : ''} rv${rvDelay}">
      <div class="pc-label">${esc(p.description ? p.description.split(',').slice(0,1).join(',') : (isFree ? 'Para empezar' : (isQuote ? 'Cotizacion personalizada' : 'Para tu ' + (nameLower === 'condominio' ? 'comunidad' : 'familia'))))}</div>
      <div class="pc-name">${esc(p.name)}</div>
      <div class="pc-desc">${esc(p.description || '')}</div>
      <div class="pc-price${isFree ? ' free-price' : ''}">${priceHtml}</div>
      <div class="pc-period">${periodHtml}</div>
      ${isQuote ? '' : `<div class="pc-install"><i class="fas fa-check-circle"></i> ${esc(baseInstall[nameLower] || '')}</div>`}
      <ul>`
    feats.forEach((f: string) => {
      h += `<li><i class="fas fa-check"></i> ${esc(f.trim())}</li>`
    })
    h += `</ul>
      <button class="pc-btn ${btnClass}" data-action="${dataAction}" data-plan="${p.id}" data-plan-name="${esc(p.name)}">${btnLabel}</button>
    </div>`
  })
  container.innerHTML = h
}

let _currentQuotePlanId: string | null = null

async function openQuoteModal(planId: string, planName: string){
  _currentQuotePlanId = planId
  const f = document.getElementById('frmQuote') as HTMLFormElement
  f.reset()
  ;(document.getElementById('qfPlanId') as HTMLInputElement).value = planId
  ;(document.getElementById('mqTitle') as HTMLElement).textContent = 'Cotizar: ' + planName
  document.getElementById('qfResult')!.innerHTML = 'Ingresa cantidades para ver tu cotizacion.'
  document.getElementById('modQuote')!.classList.add('show')
}

document.getElementById('btnCalcQuote')!.addEventListener('click', async () => {
  if(!_currentQuotePlanId) return
  const payload = {
    users: parseInt((document.getElementById('qfUsers') as HTMLInputElement).value || '0') || 0,
    gateways: parseInt((document.getElementById('qfGateways') as HTMLInputElement).value || '0') || 0,
    sirens: parseInt((document.getElementById('qfSirens') as HTMLInputElement).value || '0') || 0
  }
  const r = await api.quotePlan(_currentQuotePlanId, payload)
  const res = document.getElementById('qfResult')!
  if(!r.ok || !r.data?.success){
    res.innerHTML = `<span style="color:var(--red)">${esc(r.data?.error || 'Error al calcular')}</span>`
    return
  }
  const q = r.data
  let html = '<div style="background:var(--bg);border:1px solid var(--bdr);border-radius:var(--rl);padding:12px">'
  if(q.base > 0){
    html += `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>Precio base</span><strong>${fmtPrice(q.base)} CLP</strong></div>`
  }
  const allLines = [...(q.services||[]), ...(q.physical||[])]
  allLines.forEach((line: any) => {
    html += `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>${esc(line.label)} x${line.qty}</span><strong>${fmtPrice(line.total)} CLP</strong></div>`
  })
  html += `<div style="border-top:1px solid var(--bdr);margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;font-size:15px"><span>Total</span><strong>${fmtPrice(q.grand_total)} CLP</strong></div>`
  html += `<p style="font-size:11px;color:var(--fg3);margin:8px 0 0">Precios vigentes al ${new Date(q.valid_at).toLocaleString('es-CL')}</p>`
  html += '</div>'
  res.innerHTML = html
})

document.getElementById('frmQuote')!.onsubmit = async function(e){
  e.preventDefault()
  const planId = (document.getElementById('qfPlanId') as HTMLInputElement).value
  const payload = {
    users: parseInt((document.getElementById('qfUsers') as HTMLInputElement).value || '0') || 0,
    gateways: parseInt((document.getElementById('qfGateways') as HTMLInputElement).value || '0') || 0,
    sirens: parseInt((document.getElementById('qfSirens') as HTMLInputElement).value || '0') || 0
  }
  const r = await api.quotePlan(planId, payload)
  if(!r.ok || !r.data?.success){
    toast(r.data?.error || 'Error al calcular', 'er')
    return
  }
  const subR = await api.subscribeToPlan(planId, String(window.location.origin || '') + '/billing')
  if(subR.ok && subR.data?.success){
    toast('Cotizacion recibida. Te contactaremos en 24 horas.', 'ok')
    document.getElementById('modQuote')!.classList.remove('show')
  } else {
    toast(subR.data?.error || 'Error al guardar solicitud', 'er')
  }
}

let _qcPlanId: string | null = null

async function renderLandingQuoteCalculator(){
  const container = document.getElementById('quoteCalculator')
  if(!container) return
  if(!API_MODE){ container.innerHTML = ''; return }
  const r = await api.getPlans()
  if(!r.ok || !r.data?.success) return
  const allPlans = (r.data.plans || []).filter((p: any) =>
    p.active && p.id !== 'free' &&
    (p.base_product_code || p.base_price != null || (p.product_codes && p.product_codes !== '{}'))
  )
  if(!allPlans.length){ container.innerHTML = ''; return }

  _qcPlanId = allPlans[0].id

  // Plan cards selector
  let h = '<div class="qc-plan-grid" id="qcPlanGrid">'
  allPlans.forEach((p: any) => {
    const incl: string[] = []
    if (p.max_users != null) incl.push(`Hasta ${p.max_users} usuarios`)
    if (p.max_contacts != null) incl.push(p.max_contacts === -1 ? 'Contactos ilimitados' : `Hasta ${p.max_contacts} contactos`)
    if (p.max_devices != null) incl.push(`Hasta ${p.max_devices} dispositivos`)
    if (p.iot_enabled) incl.push('IoT incluido')
    const selected = p.id === allPlans[0].id ? ' qc-plan-card-sel' : ''
    h += `<div class="qc-plan-card${selected}" data-qc-plan="${esc(p.id)}">
      <div class="qc-pc-name">${esc(p.name)}</div>
      <div class="qc-pc-price">${fmtPrice(p.price)} <span>/${p.period || 'mes'}</span></div>
      ${incl.length ? `<div class="qc-pc-incl">${incl.join(' · ')}</div>` : ''}
    </div>`
  })
  h += '</div>'

  h += `<div class="qc-form">
    <div class="fg"><label for="qcUsers">Usuarios totales</label><input type="number" class="fi" id="qcUsers" value="10" min="0" step="1"></div>
    <div class="fg"><label for="qcGateways">Domus Gateway</label><input type="number" class="fi" id="qcGateways" value="1" min="0" step="1"></div>
    <div class="fg"><label for="qcSirens">Sirenas</label><input type="number" class="fi" id="qcSirens" value="1" min="0" step="1"></div>
  </div>
  <div class="qc-actions">
    <button class="btn btn-p" id="qcCalcBtn"><i class="fas fa-calculator"></i> Calcular</button>
    <span id="qcSpinner" style="display:none"><i class="fas fa-spinner fa-spin"></i></span>
  </div>
  <div class="qc-result" id="qcResult"></div>`

  container.innerHTML = h

  // Plan card click -> select
  container.addEventListener('click', (e: MouseEvent) => {
    const card = (e.target as HTMLElement).closest('[data-qc-plan]') as HTMLElement | null
    if (!card) return
    _qcPlanId = card.dataset.qcPlan || ''
    document.querySelectorAll('.qc-plan-card').forEach(c => c.classList.remove('qc-plan-card-sel'))
    card.classList.add('qc-plan-card-sel')
    document.getElementById('qcResult')!.innerHTML = ''
  })

  document.getElementById('qcCalcBtn')!.addEventListener('click', async () => {
    if(!_qcPlanId) return
    const spinner = document.getElementById('qcSpinner')!
    spinner.style.display = 'inline-block'
    const nUsers = parseInt((document.getElementById('qcUsers') as HTMLInputElement).value || '0') || 0
    const nGateways = parseInt((document.getElementById('qcGateways') as HTMLInputElement).value || '0') || 0
    const nSirens = parseInt((document.getElementById('qcSirens') as HTMLInputElement).value || '0') || 0

    // Auto-suggest best plan based on total users
    const sorted = [...allPlans].sort((a: any, b: any) => (a.max_users || 999) - (b.max_users || 999))
    const suggestedPlan = sorted.find((p: any) => (p.max_users || 0) >= nUsers) || sorted[sorted.length - 1]
    if (suggestedPlan && suggestedPlan.id !== _qcPlanId) {
      _qcPlanId = suggestedPlan.id
      document.querySelectorAll('.qc-plan-card').forEach(c => {
        c.classList.toggle('qc-plan-card-sel', (c as HTMLElement).dataset.qcPlan === _qcPlanId)
      })
    }

    const payload = { users: nUsers, gateways: nGateways, sirens: nSirens }
    if (!_qcPlanId) return
    const res = await api.quotePlan(_qcPlanId, payload)
    spinner.style.display = 'none'
    const resultEl = document.getElementById('qcResult')!
    if(!res.ok || !res.data?.success){
      resultEl.innerHTML = `<div class="qc-result-inner" style="color:var(--red)">${esc(res.data?.error || 'Error al calcular')}</div>`
      return
    }
    const q = res.data
    const planName = allPlans.find((p: any) => p.id === _qcPlanId)?.name || ''

    let html = '<div class="qc-result-inner">'
    html += `<div class="qc-suggestion"><i class="fas fa-star"></i> <strong>${esc(planName)}</strong> — plan recomendado para ${nUsers} usuario(s) y ${nGateways + nSirens} dispositivo(s)</div>`

    // Services section (recurring)
    if (q.services?.length) {
      html += '<div class="qc-section-label"><i class="fas fa-repeat"></i> Servicios (cargo mensual)</div>'
      q.services.forEach((line: any) => {
        html += `<div class="qc-line"><span>${esc(line.label)}${line.qty > 1 ? ' x' + line.qty : ''}</span><strong>${fmtPrice(line.total)} CLP/mes</strong></div>`
      })
      html += `<div class="qc-subtotal"><span>Total mensual</span><strong>${fmtPrice(q.total_services)} CLP/mes</strong></div>`
    }

    // Physical section (one-time)
    if (q.physical?.length) {
      html += '<div class="qc-section-label" style="margin-top:12px"><i class="fas fa-box"></i> Dispositivos (pago único)</div>'
      q.physical.forEach((line: any) => {
        html += `<div class="qc-line"><span>${esc(line.label)}${line.qty > 1 ? ' x' + line.qty : ''}</span><strong>${fmtPrice(line.total)} CLP</strong></div>`
      })
      html += `<div class="qc-subtotal"><span>Total dispositivos</span><strong>${fmtPrice(q.total_physical)} CLP</strong></div>`
    }

    // Grand total
    html += `<div class="qc-total"><span>Costo total estimado</span><strong>${fmtPrice(q.grand_total)} CLP</strong></div>`
    html += '<div class="qc-valid">Precios vigentes · IVA no incluido · Dispositivos con pago único al contratar</div>'
    html += '</div>'
    resultEl.innerHTML = html
  })
}

async function renderLandingContent(){
  if(!API_MODE) return
  try {
    const r = await api.getSiteContent()
    if(!r.ok || !r.data?.success) return
    const items = r.data.items || []

    // FAQs
    const faqs = items.filter((x:any) => x.section === 'faq' && x.is_active).sort((a:any,b:any) => (a.sort_order||0)-(b.sort_order||0))
    const faqContainer = document.getElementById('faqList')
    if(faqContainer && faqs.length){
      faqContainer.innerHTML = faqs.map(function(f:any, i:number){
        const delay = i === 0 ? '' : (i === 1 ? ' rv-d1' : (i === 2 ? ' rv-d2' : ' rv-d3'))
        return `<div class="faq-item rv${delay}">
          <div class="faq-q"><span>${esc(f.title)}</span><i class="fas fa-chevron-down"></i></div>
          <div class="faq-a"><div class="faq-a-inner">${esc(f.content)}</div></div>
        </div>`
      }).join('')
    }

    // Free plan CTA
    const free = items.filter((x:any) => x.section === 'free_plan' && x.is_active).reduce((acc:any, cur:any) => { acc[cur.key] = cur; return acc }, {} as any)
    const titleEl = document.getElementById('freePlanTitle')
    const textEl = document.getElementById('freePlanText')
    const btnEl = document.getElementById('lCtaBtn') as HTMLAnchorElement | null
    if(free.title && titleEl) titleEl.innerHTML = esc(free.title.content).replace(/\n/g, '<br>')
    if(free.cta && textEl) textEl.innerHTML = esc(free.cta.content).replace(/\n/g, '<br>')
    if(free.cta && btnEl && free.cta.title) btnEl.innerHTML = `<i class="fas fa-rocket"></i> ${esc(free.cta.title)}`
  } catch(e) {}
}

// ===== CART STATE =====
const CART_KEY = 'domus_cart'
let _pqCart: { code: string; name: string; price: number; qty: number }[] = []

function _cartSave(): void {
  localStorage.setItem(CART_KEY, JSON.stringify(_pqCart))
}

function _cartLoad(): void {
  try {
    const saved = localStorage.getItem(CART_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed)) _pqCart = parsed
    }
  } catch {}
}

_cartLoad()

function _cartTotalItems(): number {
  return _pqCart.reduce((s, x) => s + x.qty, 0)
}

function _cartTotalAmount(): number {
  return _pqCart.reduce((s, x) => s + x.price * x.qty, 0)
}

function _cartUpdateBadges(): void {
  const total = _cartTotalItems()
  document.querySelectorAll('.cart-badge').forEach(el => {
    (el as HTMLElement).textContent = String(total)
    ;(el as HTMLElement).style.display = total > 0 ? '' : 'none'
  })
  document.querySelectorAll('.cart-fab-qty').forEach(el => {
    (el as HTMLElement).textContent = String(total)
  })
  const isApp = document.getElementById('APP')?.style.display !== 'none'
  const fabApp = document.getElementById('btnCartFab')
  if (fabApp) fabApp.style.display = total > 0 && isApp ? '' : 'none'
  const fabLanding = document.getElementById('btnLandingCartFab')
  if (fabLanding) fabLanding.style.display = total > 0 && !isApp ? '' : 'none'
  const navCart = document.getElementById('lNavCart')
  if (navCart) navCart.style.display = total > 0 && !isApp ? '' : 'none'
}

function _cartUpdateProductQtyInput(code: string): void {
  const cartItem = _pqCart.find(x => x.code === code)
  document.querySelectorAll(`.pc-qty-input[data-code="${esc(code)}"]`).forEach(el => {
    (el as HTMLInputElement).value = String(cartItem?.qty || 0)
  })
  _cartUpdateBadges()
}

function _cartSetQty(code: string, name: string, price: number, qty: number): void {
  if (qty <= 0) {
    _pqCart = _pqCart.filter(x => x.code !== code)
  } else {
    const existing = _pqCart.find(x => x.code === code)
    if (existing) existing.qty = qty
    else _pqCart.push({ code, name, price, qty })
  }
  _cartSave()
  _cartUpdateProductQtyInput(code)
}

function _cartInc(code: string, name: string, price: number): void {
  const existing = _pqCart.find(x => x.code === code)
  if (existing) _cartSetQty(code, name, price, existing.qty + 1)
  else _cartSetQty(code, name, price, 1)
}

function _cartDec(code: string, name: string, price: number): void {
  const existing = _pqCart.find(x => x.code === code)
  if (existing) _cartSetQty(code, name, price, existing.qty - 1)
}

function _renderProductQtyInput(code: string, qty: number): string {
  return `<div class="pci-qty">
    <button class="pci-qty-btn" data-pq-dec="${esc(code)}">−</button>
    <input type="number" class="pci-qty-input pc-qty-input" data-code="${esc(code)}" value="${qty}" min="0" max="999" inputmode="numeric">
    <button class="pci-qty-btn" data-pq-inc="${esc(code)}">+</button>
  </div>`
}

function _productIcon(code: string): string {
  if (code.includes('gateway')) return 'fa-wifi'
  if (code.includes('siren')) return 'fa-volume-high'
  if (code.includes('motion')) return 'fa-person-walking'
  if (code.includes('door')) return 'fa-door-open'
  return 'fa-microchip'
}

// ===== PRODUCT CATALOG (LANDING) =====
async function renderProductCatalog(){
  const container = document.getElementById('productCatalog')
  if(!container) return
  if(!API_MODE){ container.innerHTML = ''; return }
  const r = await api.getProducts(undefined, 'physical')
  if(!r.ok || !r.data?.success){ container.innerHTML = ''; return }
  const products = (r.data.products || []).filter((p: any) => p.is_active)
  if(!products.length){ container.innerHTML = ''; return }

  let h = '<div class="pc-grid">'
  products.forEach((p: any, i: number) => {
    const inCart = _pqCart.find(x => x.code === p.code)
    const qty = inCart?.qty || 0
    const delay = i === 0 ? '' : (i === 1 ? ' rv-d1' : (i === 2 ? ' rv-d2' : ' rv-d3'))
    h += `<div class="pc-item rv${delay}" data-code="${esc(p.code)}" data-name="${esc(p.name)}" data-price="${p.price}">
      <div class="pci-icon"><i class="fas ${_productIcon(p.code)}"></i></div>
      <div class="pci-name">${esc(p.name)}</div>
      <div class="pci-price">${fmtPrice(p.price)} <span>${esc(p.currency)}</span></div>
      ${_renderProductQtyInput(p.code, qty)}
      <button class="pci-add btn btn-s btn-sm" data-pq-add="${esc(p.code)}"><i class="fas fa-cart-plus"></i> Agregar</button>
    </div>`
  })
  h += '</div>'
  container.innerHTML = h
  _cartUpdateBadges()
}

document.getElementById('productCatalog')!.addEventListener('click', function(e){
  const target = e.target as HTMLElement
  const inc = target.closest('[data-pq-inc]') as HTMLElement | null
  const dec = target.closest('[data-pq-dec]') as HTMLElement | null
  const add = target.closest('[data-pq-add]') as HTMLElement | null

  const item = target.closest('.pc-item') as HTMLElement | null
  if(!item) return
  const code = item.dataset.code || ''
  const name = item.dataset.name || ''
  const price = parseFloat(item.dataset.price || '0')

  if(inc){ _cartInc(code, name, price); return }
  if(dec){ _cartDec(code, name, price); return }
  if(add){ _cartInc(code, name, price); toast(`${name} agregado al carro`, 'ok') }
})

document.getElementById('productCatalog')!.addEventListener('input', function(e){
  const target = e.target as HTMLElement
  const input = target.closest('.pc-qty-input') as HTMLInputElement | null
  if(!input) return
  const item = input.closest('.pc-item') as HTMLElement | null
  if(!item) return
  const code = item.dataset.code || ''
  const name = item.dataset.name || ''
  const price = parseFloat(item.dataset.price || '0')
  const val = parseInt(input.value) || 0
  _cartSetQty(code, name, price, val)
})

document.getElementById('btnOpenProductQuote')?.addEventListener('click', openCartModal)
document.getElementById('btnCartFab')?.addEventListener('click', openCartModal)
document.getElementById('btnTopCart')?.addEventListener('click', openCartModal)
document.getElementById('btnLandingCartFab')?.addEventListener('click', openCartModal)
document.getElementById('lNavCart')?.addEventListener('click', function(e){ e.preventDefault(); openCartModal() })

let _pqStep: 'cart' | 'checkout' = 'cart'

function _cartRenderStep(): void {
  const stepCart = document.getElementById('pqStepCart')!
  const stepCheckout = document.getElementById('pqStepCheckout')!
  const title = document.getElementById('pqTitle')!

  if (_pqStep === 'cart') {
    stepCart.style.display = ''
    stepCheckout.style.display = 'none'
    title.textContent = 'Carro de Compras'
    _cartRenderCartStep()
  } else {
    stepCart.style.display = 'none'
    stepCheckout.style.display = ''
    title.textContent = 'Finalizar Compra'
    _cartRenderCheckoutStep()
  }
}

function _cartRenderCartStep(): void {
  const container = document.getElementById('pqCartItems')!
  if (_pqCart.length === 0) {
    container.innerHTML = '<div style="text-align:center;padding:24px;color:var(--fg3)"><i class="fas fa-cart-empty fa-2x" style="margin-bottom:8px;display:block"></i>Carro vacío</div>'
    document.getElementById('pqCartFooter')!.style.display = 'none'
    return
  }
  document.getElementById('pqCartFooter')!.style.display = ''
  let h = ''
  let idx = 0
  _pqCart.forEach(item => {
    const lineTotal = item.price * item.qty
    h += `<div class="pq-cart-row" data-code="${esc(item.code)}">
      <div class="pq-cart-info">
        <div class="pq-cart-name">${esc(item.name)}</div>
        <div class="pq-cart-unit">${fmtPrice(item.price)} CLP c/u</div>
      </div>
      <div class="pq-cart-qty">
        <button class="pci-qty-btn" data-cart-dec="${esc(item.code)}">−</button>
        <input type="number" class="pci-qty-input pq-cart-qty-input" data-cart-input="${esc(item.code)}" value="${item.qty}" min="0" max="999" inputmode="numeric">
        <button class="pci-qty-btn" data-cart-inc="${esc(item.code)}">+</button>
      </div>
      <div class="pq-cart-line-total">${fmtPrice(lineTotal)} CLP</div>
      <button class="pq-cart-remove" data-cart-remove="${esc(item.code)}" title="Eliminar"><i class="fas fa-trash-can"></i></button>
    </div>`
    idx++
  })
  container.innerHTML = h
  const totalEl = document.getElementById('pqCartTotal')!
  totalEl.textContent = `${fmtPrice(_cartTotalAmount())} CLP`
}

function _cartRenderCheckoutStep(): void {
  const isLoggedIn = !!(CU && CU.email)
  const form = document.getElementById('frmProductQuote') as HTMLFormElement
  form.reset()

  const nameEl = document.getElementById('pqName') as HTMLInputElement
  const emailEl = document.getElementById('pqEmail') as HTMLInputElement
  const addrGroup = document.getElementById('pqAddressGroup')!
  const btnSubmit = form.querySelector('button[type="submit"]') as HTMLButtonElement

  if (isLoggedIn) {
    if (nameEl && CU.name) nameEl.value = `${CU.name || ''} ${CU.lastname || ''}`.trim()
    if (emailEl && CU.email) emailEl.value = CU.email
    if (btnSubmit) btnSubmit.innerHTML = '<i class="fas fa-credit-card"></i> Pagar con Flow'
    addrGroup.style.display = ''
    document.getElementById('pqCheckoutSummary')!.innerHTML =
      `<div style="background:var(--bg);border:1px solid var(--bdr2);border-radius:var(--rl);padding:12px;margin-bottom:16px;font-size:13px">
        <div style="font-weight:600;margin-bottom:4px">Resumen</div>
        <div style="color:var(--fg2)">${_pqCart.length} producto(s) · ${_cartTotalItems()} unidad(es)</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px">Total: ${fmtPrice(_cartTotalAmount())} CLP</div>
      </div>`
  } else {
    if (btnSubmit) btnSubmit.innerHTML = '<i class="fas fa-envelope"></i> Solicitar Cotización'
    addrGroup.style.display = 'none'
    document.getElementById('pqCheckoutSummary')!.innerHTML =
      `<div style="background:var(--bg);border:1px solid var(--bdr2);border-radius:var(--rl);padding:12px;margin-bottom:16px;font-size:13px">
        <div style="font-weight:600;margin-bottom:4px">Resumen</div>
        <div style="color:var(--fg2)">${_pqCart.length} producto(s) · ${_cartTotalItems()} unidad(es)</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px">Total: ${fmtPrice(_cartTotalAmount())} CLP</div>
      </div>`
  }
}

function _cartHandleCartClick(e: MouseEvent): void {
  const target = e.target as HTMLElement
  const inc = target.closest('[data-cart-inc]') as HTMLElement | null
  const dec = target.closest('[data-cart-dec]') as HTMLElement | null
  const remove = target.closest('[data-cart-remove]') as HTMLElement | null
  const el = inc || dec || remove
  if (!el) return
  const code = el.dataset.cartInc || el.dataset.cartDec || el.dataset.cartRemove || ''
  const item = _pqCart.find(x => x.code === code)
  if (!item) return

  if (inc) _cartSetQty(code, item.name, item.price, item.qty + 1)
  else if (dec) _cartSetQty(code, item.name, item.price, item.qty - 1)
  else if (remove) _cartSetQty(code, item.name, item.price, 0)

  _cartRenderCartStep()
  _cartUpdateBadges()
}

function _cartHandleCartInput(e: Event): void {
  const target = e.target as HTMLElement
  const input = target.closest('.pq-cart-qty-input') as HTMLInputElement | null
  if (!input) return
  const code = input.dataset.cartInput || ''
  const item = _pqCart.find(x => x.code === code)
  if (!item) return
  const val = parseInt(input.value) || 0
  _cartSetQty(code, item.name, item.price, val)
  const row = input.closest('.pq-cart-row') as HTMLElement | null
  const updatedItem = _pqCart.find(x => x.code === code)
  if (row) {
    if (updatedItem) {
      const lineTotalEl = row.querySelector('.pq-cart-line-total') as HTMLElement | null
      if (lineTotalEl) lineTotalEl.textContent = `${fmtPrice(updatedItem.price * updatedItem.qty)} CLP`
    } else {
      row.remove()
    }
  }
  if (_pqCart.length === 0) {
    _cartRenderCartStep()
    return
  }
  const totalEl = document.getElementById('pqCartTotal')
  if (totalEl) totalEl.textContent = `${fmtPrice(_cartTotalAmount())} CLP`
  _cartUpdateBadges()
}

function openCartModal(): void {
  if (_pqCart.length === 0) { toast('Agrega productos al carro primero', 'wa'); return }
  _pqStep = 'cart'
  _cartRenderStep()
  document.getElementById('modProductQuote')!.classList.add('show')
}

// Cart click handler (inside the modal cart step)
document.getElementById('pqCartItems')!.addEventListener('click', _cartHandleCartClick)
document.getElementById('pqCartItems')!.addEventListener('input', _cartHandleCartInput)

// "Ir al pago" button
document.getElementById('btnCartToCheckout')?.addEventListener('click', function(){
  if (_pqCart.length === 0) { toast('Carro vacío', 'wa'); return }
  _pqStep = 'checkout'
  _cartRenderStep()
})

// "Volver" button (checkout → cart)
document.getElementById('btnCartBack')?.addEventListener('click', function(){
  _pqStep = 'cart'
  _cartRenderStep()
})

document.getElementById('frmProductQuote')!.onsubmit = async function(e){
  e.preventDefault()
  if(_pqCart.length === 0){ toast('Carro vacío', 'wa'); return }

  // Validate checkout step fields
  const name = (document.getElementById('pqName') as HTMLInputElement).value.trim()
  const email = (document.getElementById('pqEmail') as HTMLInputElement).value.trim()
  const phone = (document.getElementById('pqPhone') as HTMLInputElement).value.trim()
  if(!name || !email){ toast('Nombre y email son requeridos', 'wa'); return }
  if(!email.includes('@')){ toast('Email inválido', 'wa'); return }

  const isLoggedIn = !!(CU && CU.email)
  const items = _pqCart.map(x => ({ code: x.code, quantity: x.qty }))

  if (isLoggedIn) {
    const address = (document.getElementById('pqAddress') as HTMLInputElement)?.value?.trim()
    if (!address) { toast('Dirección de envío es requerida', 'wa'); return }

    const payload = {
      items,
      contact: { name, email, phone: phone || null },
      shipping_address: address,
    }
    const r = await api.checkout(payload)
    if(r.ok && r.data?.success && r.data.init_point){
      _pqCart = []; _cartSave()
      document.getElementById('modProductQuote')!.classList.remove('show')
      _cartUpdateBadges()
      window.location.href = r.data.init_point
    } else if(r.ok && r.data?.success && !r.data.init_point){
      toast('Orden creada. Te contactaremos para coordinar el pago.', 'ok')
      _pqCart = []; _cartSave()
      document.getElementById('modProductQuote')!.classList.remove('show')
      _cartUpdateBadges()
    } else {
      toast(r.data?.error || 'Error al procesar la compra', 'er')
    }
  } else {
    const payload = { items, contact: { name, email, phone: phone || null } }
    const r = await api.submitQuote(payload)
    if(r.ok && r.data?.success){
      toast('Cotización enviada. Te contactaremos pronto.', 'ok')
      _pqCart = []; _cartSave()
      document.getElementById('modProductQuote')!.classList.remove('show')
      _cartUpdateBadges()
      renderProductCatalog()
    } else {
      toast(r.data?.error || 'Error al enviar cotización', 'er')
    }
  }
}

// ===== QUOTES ADMIN =====
async function renderQuotesAdmin(){
  const g = document.getElementById('quotesAdminG')!
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'
  const r = await api.getQuotes()
  if(!r.ok || !r.data?.success){ g.innerHTML = emptyState('Error cargando cotizaciones','fa-triangle-exclamation'); return }
  const items = (r.data.quotes || []).sort((a:any,b:any) => b.created_at.localeCompare(a.created_at))
  if(!items.length){ g.innerHTML = emptyState('Sin cotizaciones de productos','fa-file-invoice'); return }

  let h = '<div style="max-width:1100px;margin:0 auto"><div class="tw"><div style="overflow-x:auto"><table class="plan-admin-table"><thead><tr><th>Fecha</th><th>Contacto</th><th>Email</th><th>Teléfono</th><th>Total</th><th>Estado</th><th>Notas</th><th>Acción</th></tr></thead><tbody>'
  items.forEach((q: any) => {
    const stLbl = statusLabels[q.status] || q.status
    const stCol = statusColors[q.status] || 'var(--fg3)'
    h += `<tr>
      <td data-label="Fecha">${q.created_at ? q.created_at.slice(0,10) : '—'}</td>
      <td data-label="Contacto">${esc(q.contact_name)}</td>
      <td data-label="Email"><a href="mailto:${esc(q.contact_email)}">${esc(q.contact_email)}</a></td>
      <td data-label="Teléfono">${q.contact_phone ? esc(q.contact_phone) : '—'}</td>
      <td data-label="Total" style="font-family:'JetBrains Mono',monospace;font-weight:600">${fmtPrice(q.total_amount)} ${esc(q.currency)}</td>
      <td data-label="Estado"><span style="color:${stCol};font-weight:600">${stLbl}</span></td>
      <td data-label="Notas" style="max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${q.notes ? esc(q.notes) : '—'}</td>
      <td data-label="Acción"><div class="ac"><button class="ab ed" data-quote-view="${q.id}" title="Ver"><i class="fas fa-eye"></i></button><button class="ab" data-quote-status="${q.id}" title="Cambiar estado"><i class="fas fa-pen"></i></button></div></td>
    </tr>`
  })
  h += '</tbody></table></div></div></div>'
  g.innerHTML = h
}

document.getElementById('quotesAdminG')!.addEventListener('click', async function(e){
  const viewBtn = (e.target as HTMLElement).closest('[data-quote-view]') as HTMLElement | null
  const statusBtn = (e.target as HTMLElement).closest('[data-quote-status]') as HTMLElement | null

  if(viewBtn){
    go('/legal/quote/' + encodeURIComponent(viewBtn.dataset.quoteView!))
    return
  }


  if(statusBtn){
    const id = statusBtn.dataset.quoteStatus!
    const statuses = ['pending','contacted','approved','rejected','converted']
    const labels = ['Pendiente','Contactado','Aprobada','Rechazada','Convertida']
    const newStatus = prompt(`Estado actual. Ingresa nuevo estado:\n${statuses.map((s,i) => `${s}: ${labels[i]}`).join('\n')}`)
    if(!newStatus || !statuses.includes(newStatus)){ toast('Estado inválido', 'wa'); return }
    const r = await api.updateQuote(id, { status: newStatus })
    if(r.ok && r.data?.success){ toast('Estado actualizado','ok'); renderQuotesAdmin() }
    else toast(r.data?.error || 'Error','er')
  }
})

const statusLabels: Record<string,string> = { pending:'Pendiente', contacted:'Contactado', approved:'Aprobada', rejected:'Rechazada', converted:'Convertida' }
const statusColors: Record<string,string> = { pending:'var(--orange)', contacted:'var(--cyan)', approved:'var(--teal)', rejected:'var(--red)', converted:'var(--blue)' }

// ===== SHOPPING (authenticated) =====
async function renderShopping(){
  const g = document.getElementById('shoppingG')!
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'
  const r = await api.getProducts(undefined, 'physical')
  if(!r.ok || !r.data?.success){ g.innerHTML = emptyState('Error cargando productos','fa-triangle-exclamation'); return }
  const products = (r.data.products || []).filter((p: any) => p.is_active)
  if(!products.length){ g.innerHTML = emptyState('No hay productos disponibles','fa-box'); return }

  let h = `<div style="max-width:1100px;margin:0 auto">
    <div class="stit"><i class="fas fa-cart-shopping"></i> Tienda de Dispositivos</div>
    <p style="color:var(--fg2);font-size:14px;margin-bottom:24px">Agrega los dispositivos que necesites y finaliza tu compra.</p>
    <div class="pc-grid">`

  products.forEach((p: any) => {
    const inCart = _pqCart.find(x => x.code === p.code)
    const qty = inCart?.qty || 0
    h += `<div class="pc-item" data-code="${esc(p.code)}" data-name="${esc(p.name)}" data-price="${p.price}">
      <div class="pci-icon"><i class="fas ${_productIcon(p.code)}"></i></div>
      <div class="pci-name">${esc(p.name)}</div>
      <div class="pci-price">${fmtPrice(p.price)} <span>${esc(p.currency)}</span></div>
      ${_renderProductQtyInput(p.code, qty)}
      <button class="pci-add btn btn-s btn-sm" data-pq-add="${esc(p.code)}"><i class="fas fa-cart-plus"></i> Agregar</button>
    </div>`
  })

  h += `</div></div>`
  g.innerHTML = h
  _cartUpdateBadges()
}

document.getElementById('shoppingG')!.addEventListener('click', function(e){
  const target = e.target as HTMLElement
  const inc = target.closest('[data-pq-inc]') as HTMLElement | null
  const dec = target.closest('[data-pq-dec]') as HTMLElement | null
  const add = target.closest('[data-pq-add]') as HTMLElement | null

  const item = target.closest('.pc-item') as HTMLElement | null
  if(!item) return
  const code = item.dataset.code || ''
  const name = item.dataset.name || ''
  const price = parseFloat(item.dataset.price || '0')

  if(inc){ _cartInc(code, name, price); return }
  if(dec){ _cartDec(code, name, price); return }
  if(add){ _cartInc(code, name, price); toast(`${name} agregado al carro`, 'ok') }
})

document.getElementById('shoppingG')!.addEventListener('input', function(e){
  const target = e.target as HTMLElement
  const input = target.closest('.pc-qty-input') as HTMLInputElement | null
  if(!input) return
  const item = input.closest('.pc-item') as HTMLElement | null
  if(!item) return
  const code = item.dataset.code || ''
  const name = item.dataset.name || ''
  const price = parseFloat(item.dataset.price || '0')
  const val = parseInt(input.value) || 0
  _cartSetQty(code, name, price, val)
})

// ===== ORDERS =====
const orderStatusLabels: Record<string,string> = {
  pending: 'Pendiente', paid: 'Pagado', shipped: 'Enviado', completed: 'Completado', cancelled: 'Cancelado'
}
const orderStatusColors: Record<string,string> = {
  pending: 'var(--amber)', paid: 'var(--green)', shipped: 'var(--cyan)', completed: 'var(--fg2)', cancelled: 'var(--fg3)'
}

async function renderOrders(){
  const g = document.getElementById('ordersG')!
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'
  const r = await api.getOrders()
  if(!r.ok || !r.data?.success){ g.innerHTML = emptyState('Error cargando ordenes','fa-triangle-exclamation'); return }
  const orders = r.data.orders || []
  if(!orders.length){
    g.innerHTML = `<div style="max-width:800px;margin:0 auto">
      <div class="stit"><i class="fas fa-truck"></i> Mis Ordenes</div>
      ${emptyState('No tienes ordenes aún. Visita la Tienda para comprar dispositivos.','fa-box')}
      <div style="text-align:center;margin-top:16px"><button class="btn btn-p" onclick="nav('shopping')"><i class="fas fa-cart-shopping"></i> Ir a la Tienda</button></div>
    </div>`
    return
  }
  let h = `<div style="max-width:900px;margin:0 auto">
    <div class="stit"><i class="fas fa-truck"></i> Mis Ordenes</div>
    <p style="color:var(--fg2);font-size:14px;margin-bottom:20px">Historial de compras de dispositivos.</p>`
  orders.forEach((o: any) => {
    const items = o.items || []
    const statusColor = orderStatusColors[o.status] || 'var(--fg3)'
    h += `<div style="background:var(--card);border:1px solid var(--bdr);border-radius:var(--rl);padding:16px;margin-bottom:12px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
        <div>
          <span style="font-weight:600;font-size:14px">Orden #${o.id.slice(0,8)}</span>
          <span style="color:var(--fg3);font-size:12px;margin-left:8px">${o.created_at?.slice(0,10) || ''}</span>
        </div>
        <span style="background:${statusColor}20;color:${statusColor};padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600">${orderStatusLabels[o.status] || o.status}</span>
      </div>
      <div style="font-size:13px;color:var(--fg2);margin-bottom:4px">${o.contact_name} · ${o.contact_email}</div>
      ${o.shipping_address ? `<div style="font-size:12px;color:var(--fg3);margin-bottom:4px"><i class="fas fa-location-dot"></i> ${esc(o.shipping_address)}</div>` : ''}
      <div style="font-size:13px;margin-top:6px">
        ${items.map((it: any) => `<span style="display:inline-block;background:var(--bg);border:1px solid var(--bdr2);border-radius:12px;padding:2px 10px;margin:2px;font-size:12px">${esc(it.name)} x${it.qty}</span>`).join('')}
      </div>
      <div style="border-top:1px solid var(--bdr2);margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;font-size:15px">
        <span style="color:var(--fg3)">Total</span>
        <strong>${fmtPrice(o.total_amount)} CLP</strong>
      </div>
    </div>`
  })
  h += '</div>'
  g.innerHTML = h
}

// ===== CONTROL CENTER =====
async function renderControlCenter(){
  const g = document.getElementById('controlCenterG')!
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'

  const [sr, sdr] = await Promise.all([
    api.getAdminStats().catch(() => null),
    api.getDevices().catch(() => null)
  ])

  if(!sr?.ok || !sr.data?.success){ g.innerHTML = emptyState('Error cargando estadisticas', 'fa-triangle-exclamation'); return }

  const s = sr.data.stats
  const devs = sdr?.ok ? sdr.data.devices || [] : []

  const totalDevs = s.devices?.total || 0
  const wsMsgs = totalDevs * 2 * 60 * 24 * 30
  const doDurationSec = totalDevs * 0.02 * 60 * 24 * 30
  const doGbSec = doDurationSec * 128 / 1000 / 1000
  const doReqHeartbeats = wsMsgs / 20
  const doReqCommands = totalDevs * 10
  const doReqTotal = doReqHeartbeats + doReqCommands
  const costoBase = 5.00
  const costoDuration = Math.max(0, doGbSec - 400000) * 12.50 / 1000000
  const costoDOReqs = Math.max(0, doReqTotal - 1000000) * 0.15 / 1000000
  const costoTotal = costoBase + costoDuration + costoDOReqs
  const onlinePct = totalDevs > 0 ? Math.round(s.devices.online / totalDevs * 100) : 0

  const byType: Record<string, {total:number, online:number, offline:number}> = {}
  for (const d of devs) {
    const t = d.type || 'sensor'
    if (!byType[t]) byType[t] = {total:0, online:0, offline:0}
    byType[t].total++
    if (d.status === 'online') byType[t].online++
    else byType[t].offline++
  }
  const typeRows = Object.entries(byType).map(([t, v]) => `<div class="sc"><div class="si2" style="background:${t==='sirena'?'rgba(239,68,68,.15)':t==='portero'?'rgba(6,182,212,.15)':t==='puerta'?'rgba(16,185,129,.15)':t==='camara'?'rgba(168,85,247,.15)':'rgba(245,158,11,.15)'};color:${t==='sirena'?'var(--red)':t==='portero'?'var(--cyan)':t==='puerta'?'var(--green)':t==='camara'?'var(--purple)':'var(--amber)'}"><i class="fas ${t==='sirena'?'fa-bell':t==='portero'?'fa-car':t==='puerta'?'fa-door-open':t==='camara'?'fa-video':'fa-satellite-dish'}"></i></div><div class="sv">${v.total}</div><div class="sl">${t}<br><span style="font-size:10px;color:var(--green)">${v.online} online</span> · <span style="font-size:10px;color:var(--red)">${v.offline} offline</span></div></div>`).join('')

  let h = `
  <div style="max-width:1400px;margin:0 auto">
    <div class="stats-g">
      <div class="sc"><div class="si2" style="background:rgba(239,68,68,.15);color:var(--red)"><i class="fas fa-triangle-exclamation"></i></div><div class="sv" style="color:var(--red)">${s.alerts.active}</div><div class="sl">Alertas Activas</div></div>
      <div class="sc"><div class="si2" style="background:rgba(16,185,129,.15);color:var(--green)"><i class="fas fa-check-circle"></i></div><div class="sv" style="color:var(--green)">${s.alerts.resolved}</div><div class="sl">Resueltas</div></div>
      <div class="sc"><div class="si2" style="background:rgba(245,158,11,.15);color:var(--amber)"><i class="fas fa-chart-line"></i></div><div class="sv" style="color:var(--amber)">${s.alerts.thisMonth}</div><div class="sl">Este Mes</div></div>
      <div class="sc"><div class="si2" style="background:rgba(99,102,241,.15);color:var(--indigo)"><i class="fas fa-users"></i></div><div class="sv" style="color:var(--indigo)">${s.users}</div><div class="sl">Usuarios</div></div>
      <div class="sc"><div class="si2" style="background:rgba(6,182,212,.15);color:var(--cyan)"><i class="fas fa-microchip"></i></div><div class="sv" style="color:var(--cyan)">${totalDevs}</div><div class="sl">Dispositivos</div></div>
      <div class="sc"><div class="si2" style="background:rgba(16,185,129,.15);color:var(--green)"><i class="fas fa-wifi"></i></div><div class="sv" style="color:var(--green)">${s.devices.online}</div><div class="sl">Online (${onlinePct}%)</div></div>
      <div class="sc"><div class="si2" style="background:rgba(239,68,68,.15);color:var(--red)"><i class="fas fa-wifi"></i></div><div class="sv" style="color:var(--red)">${s.devices.offline}</div><div class="sl">Offline</div></div>
      <div class="sc"><div class="si2" style="background:rgba(245,158,11,.15);color:var(--amber)"><i class="fas fa-key"></i></div><div class="sv" style="color:var(--amber)">${s.devices.withGateway}</div><div class="sl">Con Gateway</div></div>
      <div class="sc"><div class="si2" style="background:rgba(16,185,129,.15);color:var(--green)"><i class="fas fa-bell"></i></div><div class="sv" style="color:var(--green)">${s.devices.panicRelay}</div><div class="sl">Panic Relay</div></div>
      <div class="sc"><div class="si2" style="background:rgba(139,92,246,.15);color:var(--purple)"><i class="fas fa-layer-group"></i></div><div class="sv" style="color:var(--purple)">${s.groups}</div><div class="sl">Grupos</div></div>
    </div>

    <div class="stit mt4"><i class="fas fa-file-invoice-dollar"></i> Costos Cloudflare</div>
    <div class="stats-g">
      <div class="sc"><div class="si2" style="background:rgba(99,102,241,.15);color:var(--indigo)"><i class="fas fa-box"></i></div><div class="sv" style="font-size:16px">$${costoBase.toFixed(2)}</div><div class="sl">Workers Paid</div></div>
      <div class="sc"><div class="si2" style="background:rgba(6,182,212,.15);color:var(--cyan)"><i class="fas fa-microchip"></i></div><div class="sv" style="font-size:16px">${s.devices.online}</div><div class="sl">Dispositivos activos</div></div>
      <div class="sc"><div class="si2" style="background:rgba(245,158,11,.15);color:var(--amber)"><i class="fas fa-exchange"></i></div><div class="sv" style="font-size:12px">${wsMsgs.toLocaleString()}</div><div class="sl">Heartbeats/mes</div></div>
      <div class="sc"><div class="si2" style="background:rgba(16,185,129,.15);color:var(--green)"><i class="fas fa-database"></i></div><div class="sv" style="font-size:16px">$${costoDuration.toFixed(2)}</div><div class="sl">DO Duration</div></div>
      <div class="sc"><div class="si2" style="background:rgba(239,68,68,.15);color:var(--red)"><i class="fas fa-tower-broadcast"></i></div><div class="sv" style="font-size:16px">$${costoDOReqs.toFixed(2)}</div><div class="sl">DO Requests</div></div>
      <div class="sc" style="border-color:var(--accent)"><div class="si2" style="background:rgba(59,130,246,.15);color:var(--accent)"><i class="fas fa-calculator"></i></div><div class="sv" style="font-size:18px;color:var(--accent)">$${costoTotal.toFixed(2)}</div><div class="sl">Total / mes</div></div>
    </div>

    <div class="stit mt4"><i class="fas fa-plug"></i> Dispositivos por tipo</div>
    <div class="stats-g" style="grid-template-columns:repeat(auto-fill,minmax(180px,1fr))">${typeRows}</div>

    <div class="stit mt4"><i class="fas fa-signal"></i> Conexiones WebSocket</div>
    <div class="stats-g" style="grid-template-columns:repeat(auto-fill,minmax(250px,1fr))">
      <div class="sc"><div class="si2" style="background:rgba(16,185,129,.15);color:var(--green)"><i class="fas fa-wifi"></i></div><div class="sv" style="font-size:24px;color:${s.devices.online > 0 ? 'var(--green)' : 'var(--red)'}">${s.devices.online}</div><div class="sl">IoT Gateways Online</div></div>
      <div class="sc"><div class="si2" style="background:${s.alerts.active > 0 ? 'rgba(239,68,68,.15)' : 'rgba(100,116,139,.15)'};color:${s.alerts.active > 0 ? 'var(--red)' : 'var(--fg3)'}"><i class="fas fa-bell"></i></div><div class="sv" style="font-size:24px;color:${s.alerts.active > 0 ? 'var(--red)' : 'var(--fg3)'}">${s.alerts.active}</div><div class="sl">Alertas Activas</div></div>
    </div>

    <div class="stit mt4"><i class="fas fa-list"></i> Eventos <span style="font-size:11px;color:var(--fg3)">(ultimos 50)</span></div>
    <div id="eventLogContainer" style="background:var(--card);border:1px solid var(--bdr);border-radius:var(--rl);padding:12px;margin-top:8px;font-size:12px;max-height:360px;overflow-y:auto;font-family:'JetBrains Mono',monospace">
      <p style="color:var(--fg3);margin:0;text-align:center">Cargando...</p>
    </div>

    <div class="stit mt4"><i class="fas fa-pen-to-square"></i> Contenido del Sitio</div>
    <div style="background:var(--card);border:1px solid var(--bdr);border-radius:var(--rl);padding:16px;margin-top:8px">
      <p style="margin:0 0 12px;color:var(--fg3);font-size:13px">Administra las preguntas frecuentes (FAQ) y los textos del plan gratuito que se muestran en la landing page.</p>
      <button class="btn btn-s" id="btnSiteContent"><i class="fas fa-edit"></i> Editar Contenido del Sitio</button>
    </div>
  </div>`

  g.innerHTML = h
  loadEventLog()
  document.getElementById('btnSiteContent')?.addEventListener('click', () => openSiteContentMod())
}

async function loadEventLog(){
  const el = document.getElementById('eventLogContainer')
  if(!el) return
  const r = await api.getEventLog('?limit=50').catch(() => null)
  if(!r?.ok || !r.data?.events?.length){ el.innerHTML = '<p style="color:var(--fg3);margin:0;text-align:center">Sin eventos registrados</p>'; return }
  const eventColors: Record<string,string> = {
    'alert.created':'var(--red)',
    'alert.resolved':'var(--green)',
    'alert.cancelled':'var(--fg3)',
    'device.command_delivered':'var(--cyan)',
    'device.command_failed':'var(--orange)',
    'door.opened':'var(--green)',
    'gate.opened':'var(--cyan)',
    'relay.activated':'var(--amber)'
  }
  el.innerHTML = r.data.events.map((e:any) => {
    const c = eventColors[e.event] || 'var(--fg2)'
    const meta = e.metadata ? JSON.parse(e.metadata) : null
    return `<div style="padding:4px 0;border-bottom:1px solid var(--bdr2);display:flex;gap:8px;align-items:flex-start">
      <span style="color:${c};flex-shrink:0;width:14px;font-size:10px;margin-top:3px"><i class="fas fa-circle"></i></span>
      <span style="flex-shrink:0;color:var(--fg3);font-size:10px;min-width:70px">${e.created_at?.slice(11,19)||''}</span>
      <span style="flex-shrink:0;min-width:120px;color:${c}">${e.event}</span>
      <span style="color:var(--fg2);flex:1">${esc(e.description||'')}</span>
      ${meta ? `<span style="color:var(--fg3);font-size:10px">${esc(JSON.stringify(meta))}</span>` : ''}
    </div>`
  }).join('')
}

// ===== PLANS =====
async function renderPlans(){
  const g=document.getElementById('plansG')!
  const isSuper = CU.role==='superadmin'
  if(!API_MODE){
    renderPlansStatic(g, isSuper)
    return
  }
  const [r, sr] = await Promise.all([
    api.getPlans(),
    api.getBillingStatus().catch(() => null),
  ])
  if(!r.ok || !r.data.success){ g.innerHTML=emptyState('Error cargando planes','fa-triangle-exclamation'); return }
  const plans = r.data.plans || []
  let currentPlan = 'free'
  if(sr?.ok && sr.data?.subscription?.status === 'active'){
    const subPlanId = sr.data.subscription.plan_id
    const subPlan = plans.find((p:any) => p.id === subPlanId)
    if(subPlan) currentPlan = subPlan.name.toLowerCase()
  }
  if(isSuper){
    g.innerHTML = renderPlansAdminTable(plans)
  } else {
    g.innerHTML = renderPlansUserCards(plans, currentPlan)
  }
}

function renderPlansAdminTable(plans:any[]){
  let h = `<div class="tw" style="max-width:960px;margin:0 auto">
    <div class="th" style="margin-bottom:16px">
      <h3>Planes del Sistema</h3>
      <button class="btn btn-s btn-sm" id="btnNewPlan"><i class="fas fa-plus"></i> Nuevo Plan</button>
    </div>
    <div class="plan-admin-cards">`
  plans.forEach(function(p:any){
    const feats = (p.features || '').split(',').filter(Boolean)
    const featCount = feats.length
    const periodLbl = p.period==='year'?'Anual':'Mensual'
    const priceTypeLbl = p.price_type === 'quote' ? 'Cotizar' : (p.price_type === 'free' ? 'Gratis' : 'Fijo')
    const priceLbl = p.price_type === 'quote' ? esc(p.quote_button_text || 'Cotizar') : (p.price_type === 'free' ? 'Gratis' : fmtPrice(p.price) + '<span style="font-size:10px;color:var(--fg3);font-weight:400"> CLP</span>')
    const iotLbl = p.iot_enabled ? '<span class="st resolved">Si</span>' : '<span class="st active">No</span>'
    const stateLbl = p.active ? '<span class="st resolved">Activo</span>' : '<span class="st active">Inactivo</span>'
    const desc = p.description ? esc(p.description) : ''
    h += `<div class="plan-admin-card ${p.active?'':'inactive'}">
      <div class="pac-head">
        <div>
          <div class="pac-name">${esc(p.name)}</div>
          ${desc ? `<div class="pac-desc">${desc}</div>` : ''}
        </div>
        <div class="pac-badges">
          ${stateLbl}
          ${iotLbl}
        </div>
      </div>
      <div class="pac-body">
        <div class="pac-row">
          <div class="pac-field">
            <span class="pac-label">Precio</span>
            <span class="pac-value">${priceLbl}</span>
          </div>
          <div class="pac-field">
            <span class="pac-label">Tipo</span>
            <span class="pac-value">${priceTypeLbl}</span>
          </div>
          <div class="pac-field">
            <span class="pac-label">Periodo</span>
            <span class="pac-value">${periodLbl}</span>
          </div>
        </div>
        <div class="pac-divider"></div>
        <div class="pac-row">
          <div class="pac-field">
            <span class="pac-label">Usuarios</span>
            <span class="pac-value">${p.max_users||'<i>Ilimitado</i>'}</span>
          </div>
          <div class="pac-field">
            <span class="pac-label">Contactos</span>
            <span class="pac-value">${p.max_contacts||'<i>Ilimitado</i>'}</span>
          </div>
          <div class="pac-field">
            <span class="pac-label">Dispositivos</span>
            <span class="pac-value">${p.max_devices||'<i>Ilimitado</i>'}</span>
          </div>
        </div>
        ${featCount > 0 ? `<div class="pac-divider"></div><div class="pac-feats"><span class="pac-label">Características</span><span class="pac-value">${featCount} característica${featCount!==1?'s':''}</span></div>` : ''}
      </div>
      <div class="pac-actions">
        <button class="btn btn-s btn-sm" data-pedit="${p.id}"><i class="fas fa-pen"></i> Editar</button>
        <button class="btn btn-s btn-sm" data-pdel="${p.id}" style="color:var(--red)"><i class="fas fa-${p.active?'ban':'check'}"></i> ${p.active?'Desactivar':'Activar'}</button>
      </div>
    </div>`
  })
  h += `</div>
    <div class="stit mt4" style="max-width:960px;margin:24px auto 0"><i class="fas fa-eye"></i> Vista previa (como lo ven los usuarios)</div>
    <div class="pg" style="margin-top:12px">${renderPlansUserCardsHtml(plans, CU.plan, true)}</div>
  </div>`
  return h
}

function renderPlansUserCards(plans:any[], currentPlan:string){
  return renderPlansUserCardsHtml(plans, currentPlan, false)
}

function renderPlansUserCardsHtml(plans:any[], currentPlan:string, previewMode:boolean){
  const mapped = plans.map(function(p:any){
    const feats = (p.features || '').split(',').filter(Boolean)
    const isFeat = p.name && p.name.toLowerCase()==='familiar'
    const isFree = p.price_type === 'free'
    return {
      id:p.id, name:p.name, price:String(p.price),
      price_type: p.price_type,
      isFree,
      per: p.period==='year' ? 'ao' : 'mes',
      desc: p.description || '',
      feat: isFeat, iot: p.iot_enabled,
      max_users: p.max_users, max_contacts: p.max_contacts,
      f: feats.map(function(t:string){ return {t, o:true} })
    }
  })
  let h=''
  mapped.forEach(function(p:any){
    const cur = currentPlan === p.name.toLowerCase()
    const feat = p.feat || false
    h += `<div class="pc ${feat?'feat':''}">`
    if(cur) h += `<div style="position:absolute;top:0;left:0;right:0;background:var(--green);color:#fff;font-size:10px;font-weight:700;padding:3px;text-transform:uppercase;letter-spacing:1px">Plan Actual</div>`
    h += `<div class="pn">${esc(p.name)}</div>`
    h += `<div class="pp">${p.isFree ? 'Gratis' : fmtPrice(p.price)}<span>/${esc(p.per)}</span></div>`
    h += `<div class="pd">${esc(p.desc || (p.per==='mes'?'por mes':'por ao'))}</div>`
    h += `<ul>`
    p.f.forEach(function(f:any){ h += `<li class="${f.o?'':'dis'}"><i class="fas fa-${f.o?'check':'xmark'}"></i> ${esc(f.t)}</li>` })
    h += `</ul>`
    if(!previewMode){
      if(cur){
        h += `<button class="btn btn-s" disabled style="width:100%;opacity:.5">Plan Actual</button>`
      } else {
        h += `<button class="btn ${feat?'btn-p':'btn-s'}" style="width:100%" data-plan="${p.name.toLowerCase()}" data-plan-id="${p.id}" data-plan-price="${p.price}" data-plan-type="${p.price_type}">${p.isFree ? 'Cambiar a Free' : 'Suscribirse'}</button>`
      }
    } else {
      h += `<button class="btn btn-s" disabled style="width:100%;opacity:.5">${cur?'Plan Actual':'Suscribirse'}</button>`
    }
    h += `</div>`
  })
  return h
}

function renderPlansStatic(g:HTMLElement, editable:boolean){
  const cp=CU.plan
  const plans=[
    {id:'free',name:'Gratis',price:'0',per:'mes',desc:'1 usuario, 1 contacto de emergencia',feat:false,f:[{t:'1 usuario',o:true},{t:'1 contacto de emergencia',o:true},{t:'Apertura porton vehicular',o:true},{t:'Apertura puerta peatonal',o:true},{t:'Boton de panico con alerta',o:true},{t:'Sirena comunitaria',o:true},{t:'Geolocalizacion',o:true},{t:'Historial de alertas',o:true}]},
    {id:'familiar',name:'Familiar',price:'14990',per:'mes',desc:'Hasta 4 personas',feat:true,f:[{t:'Todo lo de Gratis',o:true},{t:'4 usuarios en una misma cuenta',o:true},{t:'Hasta 4 contactos de emergencia',o:true},{t:'Alertas a toda la familia',o:true},{t:'Sirena de mayor potencia',o:true},{t:'Tres tipos de alerta',o:true},{t:'4 botones fisicos incluidos',o:true},{t:'Persona adicional: +$6.000/mes',o:true}]},
    {id:'grupos',name:'Condominio',price:'119990',per:'mes',desc:'Para 20-40 unidades',feat:false,f:[{t:'Todo lo de Familiar',o:true},{t:'Todos los vecinos del condominio',o:true},{t:'Panel de administracion',o:true},{t:'Sirena comunitaria 30W en plaza',o:true},{t:'Alerta masiva a todos los vecinos',o:true},{t:'1 boton fisico por unidad',o:true},{t:'3 meses de gracia',o:true},{t:'Garantia 2 anos + soporte 24/7',o:true}]}
  ]
  if(editable){
    g.innerHTML = renderPlansAdminTable(plans)
  } else {
    g.innerHTML = renderPlansUserCardsHtml(plans, cp, false)
  }
}

document.getElementById('plansG')!.onclick=async function(e){
  const btn=(e.target as HTMLElement).closest('[data-plan]') as HTMLElement|null
  if(btn){
    const planName = btn.dataset.plan!
    const planId = btn.dataset.planId
    if(!API_MODE){
      const d=db()!; const u=d.users.find(function(x:any){ return x.id===CU.id })!
      u.plan=planName; dbSave(d); CU.plan=planName
      toast('Plan cambiado a '+planName,'ok'); renderPlans()
    } else {
      if(planName === 'free' || planName === 'gratis' || (btn.dataset.planPrice === '0')){
        // Free plan: just update directly
        const r = await api.updateUser(CU.id, { plan: planName })
        if(r.ok && r.data?.success){ CU.plan = planName; toast('Plan cambiado a '+planName,'ok'); renderPlans() }
        else toast(r.data?.error || 'Error al cambiar plan','er')
      } else {
        // Paid plan: abrir link de pago Mercado Pago
        const r = await api.subscribeToPlan(planId!, String(window.location.origin || '') + '/billing')
        if(r.ok && r.data?.success){
          if(r.data.init_point){
            window.location.href = r.data.init_point
          } else if(r.data.payment_link){
            window.location.href = r.data.payment_link
          } else {
            CU.plan = planName
            toast('Plan actualizado','ok')
            renderPlans()
          }
        } else if(r.status === 409){
          toast(r.data?.error || 'Ya tienes una suscripcion activa','wa')
          nav('billing')
        } else {
          toast(r.data?.error || 'Error al cambiar plan','er')
        }
      }
    }
    return
  }
  const editBtn=(e.target as HTMLElement).closest('[data-pedit]') as HTMLElement|null
  const delBtn=(e.target as HTMLElement).closest('[data-pdel]') as HTMLElement|null
  const newBtn=(e.target as HTMLElement).closest('#btnNewPlan') as HTMLElement|null
  if(newBtn){ go('/plans/new'); return }
  if(editBtn){ go('/plans/' + encodeURIComponent(editBtn.dataset.pedit!)); return }
  if(delBtn){
    const id=delBtn.dataset.pdel!
    if(!confirm('Desactivar este plan? Los usuarios suscritos mantendran su plan pero no sera visible para nuevas suscripciones.')) return
    const r=await api.deletePlan(id)
    if(r.ok && r.data.success){ toast('Plan desactivado','ok'); renderPlans() }
    else toast('Error','er')
  }
}

// ===== SITE CONTENT (FAQs + Free Plan) =====
async function openSiteContentMod(){
  document.getElementById('modSiteContent')!.classList.add('show')
  await renderSiteContentList()
}

async function renderSiteContentList(){
  const g = document.getElementById('siteContentList')!
  g.innerHTML = '<p style="text-align:center;color:var(--fg3);padding:20px"><i class="fas fa-spinner fa-spin"></i> Cargando...</p>'
  const r = await api.getSiteContent()
  if(!r.ok || !r.data?.success){ g.innerHTML = '<p style="text-align:center;color:var(--red);padding:20px">Error cargando contenido</p>'; return }
  const items = (r.data.items || []).sort((a:any,b:any) => {
    if(a.section !== b.section) return a.section.localeCompare(b.section)
    return (a.sort_order||0) - (b.sort_order||0)
  })
  if(!items.length){ g.innerHTML = '<p style="text-align:center;color:var(--fg3);padding:20px">Sin items configurados</p>'; return }
  let h = '<table style="width:100%;font-size:12px"><thead><tr><th>Sección</th><th>Key</th><th>Título</th><th>Orden</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>'
  items.forEach((it:any) => {
    h += `<tr>
      <td>${esc(it.section)}</td>
      <td>${esc(it.key)}</td>
      <td>${esc(it.title)}</td>
      <td>${it.sort_order||0}</td>
      <td>${it.is_active ? '<span class="st resolved">Activo</span>' : '<span class="st active">Inactivo</span>'}</td>
      <td><div class="ac"><button class="ab ed" data-sc-edit="${it.id}" title="Editar"><i class="fas fa-pen"></i></button><button class="ab" data-sc-del="${it.id}" title="Desactivar"><i class="fas fa-ban"></i></button></div></td>
    </tr>`
  })
  h += '</tbody></table>'
  g.innerHTML = h
}

function openSiteContentForm(item?: any, defaultSection = 'faq'){
  const f = document.getElementById('frmSiteContent') as HTMLFormElement
  f.reset()
  ;(document.getElementById('scfId') as HTMLInputElement).value = item?.id || ''
  ;(document.getElementById('scfFormTitle') as HTMLElement).textContent = item ? 'Editar Item' : 'Nuevo Item'
  if(item){
    ;(document.getElementById('scfSection') as HTMLSelectElement).value = item.section || defaultSection
    ;(document.getElementById('scfKey') as HTMLInputElement).value = item.key || ''
    ;(document.getElementById('scfTitle') as HTMLInputElement).value = item.title || ''
    ;(document.getElementById('scfContent') as HTMLTextAreaElement).value = item.content || ''
    ;(document.getElementById('scfIcon') as HTMLInputElement).value = item.icon || ''
    ;(document.getElementById('scfOrder') as HTMLInputElement).value = String(item.sort_order || 0)
    ;(document.getElementById('scfActive') as HTMLInputElement).checked = !!item.is_active
  } else {
    ;(document.getElementById('scfSection') as HTMLSelectElement).value = defaultSection
  }
  document.getElementById('modSiteContentForm')!.classList.add('show')
}

document.getElementById('btnNewSiteContent')!.addEventListener('click', () => openSiteContentForm())

document.getElementById('siteContentList')!.addEventListener('click', async function(e){
  const editBtn = (e.target as HTMLElement).closest('[data-sc-edit]') as HTMLElement | null
  const delBtn = (e.target as HTMLElement).closest('[data-sc-del]') as HTMLElement | null
  if(editBtn){
    const id = editBtn.dataset.scEdit!
    const r = await api.getSiteContent()
    if(r.ok && r.data?.success){
      const item = (r.data.items || []).find((x:any) => x.id === id)
      if(item) openSiteContentForm(item)
    }
    return
  }
  if(delBtn){
    const id = delBtn.dataset.scDel!
    if(!confirm('Desactivar este item?')) return
    const r = await api.deleteSiteContent(id)
    if(r.ok && r.data?.success){ toast('Item desactivado','ok'); renderSiteContentList() }
    else toast('Error','er')
  }
})

document.getElementById('frmSiteContent')!.onsubmit = async function(e){
  e.preventDefault()
  const id = (document.getElementById('scfId') as HTMLInputElement).value
  const payload = {
    section: (document.getElementById('scfSection') as HTMLSelectElement).value,
    key: (document.getElementById('scfKey') as HTMLInputElement).value.trim(),
    title: (document.getElementById('scfTitle') as HTMLInputElement).value.trim(),
    content: (document.getElementById('scfContent') as HTMLTextAreaElement).value.trim(),
    icon: (document.getElementById('scfIcon') as HTMLInputElement).value.trim() || null,
    sort_order: parseInt((document.getElementById('scfOrder') as HTMLInputElement).value || '0'),
    is_active: (document.getElementById('scfActive') as HTMLInputElement).checked
  }
  const r = id ? await api.updateSiteContent(id, payload) : await api.createSiteContent(payload)
  if(r.ok && r.data?.success){
    toast(id ? 'Item actualizado' : 'Item creado','ok')
    document.getElementById('modSiteContentForm')!.classList.remove('show')
    renderSiteContentList()
    if(document.getElementById('pg-faq-admin')?.offsetParent) renderFaqAdmin()
  } else {
    toast(r.data?.error || 'Error','er')
  }
}

// ===== FAQ ADMIN =====
async function renderFaqAdmin(){
  const g = document.getElementById('faqAdminG')!
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'
  const r = await api.getSiteContent('faq')
  if(!r.ok || !r.data?.success){ g.innerHTML = emptyState('Error cargando FAQs','fa-triangle-exclamation'); return }
  const items = (r.data.items || []).sort((a:any,b:any) => (a.sort_order||0) - (b.sort_order||0))
  if(!items.length){ g.innerHTML = emptyState('Sin preguntas frecuentes','fa-circle-question'); return }
  let h = '<div style="max-width:900px;margin:0 auto"><div class="tw"><div style="overflow-x:auto"><table class="plan-admin-table"><thead><tr><th>Orden</th><th>Título</th><th>Contenido</th><th>Icono</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>'
  items.forEach((it:any) => {
    h += `<tr>
      <td data-label="Orden">${it.sort_order||0}</td>
      <td data-label="Título">${esc(it.title)}</td>
      <td data-label="Contenido" style="max-width:300px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(it.content)}</td>
      <td data-label="Icono"><i class="fas ${esc(it.icon || 'fa-circle-question')}"></i></td>
      <td data-label="Estado">${it.is_active ? '<span class="st resolved">Activo</span>' : '<span class="st active">Inactivo</span>'}</td>
      <td data-label="Acciones"><div class="ac"><button class="ab ed" data-faq-edit="${it.id}" title="Editar"><i class="fas fa-pen"></i></button><button class="ab" data-faq-del="${it.id}" title="Desactivar"><i class="fas fa-ban"></i></button></div></td>
    </tr>`
  })
  h += '</tbody></table></div></div></div>'
  g.innerHTML = h
}

document.getElementById('btnNewFaqAdmin')!.addEventListener('click', () => openSiteContentForm())

document.getElementById('faqAdminG')!.addEventListener('click', async function(e){
  const editBtn = (e.target as HTMLElement).closest('[data-faq-edit]') as HTMLElement | null
  const delBtn = (e.target as HTMLElement).closest('[data-faq-del]') as HTMLElement | null
  if(editBtn){
    const id = editBtn.dataset.faqEdit!
    const r = await api.getSiteContent()
    if(r.ok && r.data?.success){
      const item = (r.data.items || []).find((x:any) => x.id === id)
      if(item) openSiteContentForm(item)
    }
    return
  }
  if(delBtn){
    const id = delBtn.dataset.faqDel!
    if(!confirm('Desactivar esta pregunta?')) return
    const r = await api.deleteSiteContent(id)
    if(r.ok && r.data?.success){ toast('Pregunta desactivada','ok'); renderFaqAdmin() }
    else toast('Error','er')
  }
})

// ===== PRODUCTS ADMIN =====
const IMG_BASE = import.meta.env.VITE_API_URL || ''

function productThumb(src: string): string {
  return `<img src="${IMG_BASE}/api/v1/products/images/${encodeURIComponent(src)}" alt="" style="width:40px;height:40px;object-fit:cover;border-radius:4px;display:block">`
}

async function renderProductsAdmin(){
  const g = document.getElementById('productsAdminG')!
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'
  const r = await api.getProductsAdmin()
  if(!r.ok || !r.data?.success){ g.innerHTML = emptyState('Error cargando productos','fa-triangle-exclamation'); return }
  const items = (r.data.products || []).sort((a:any,b:any) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name))
  if(!items.length){ g.innerHTML = emptyState('Sin productos configurados','fa-tags'); return }
  let h = '<div style="max-width:1100px;margin:0 auto"><div class="tw"><div style="overflow-x:auto"><table class="plan-admin-table"><thead><tr><th></th><th>Código</th><th>Categoría</th><th>Tipo</th><th>Nombre</th><th>Precio</th><th>Vigencia</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>'
  items.forEach((p:any) => {
    const valid = p.valid_until ? `Hasta ${p.valid_until.slice(0,10)}` : 'Sin vencimiento'
    const typeLabel = p.type === 'physical' ? 'Físico' : 'Servicio'
    const thumb = p.images?.length ? productThumb(p.images[0].file_key) : '<span style="color:var(--fg3);font-size:11px">—</span>'
    h += `<tr>
      <td data-label="Img">${thumb}</td>
      <td data-label="Código"><code>${esc(p.code)}</code></td>
      <td data-label="Categoría">${esc(p.category)}</td>
      <td data-label="Tipo">${typeLabel}</td>
      <td data-label="Nombre">${esc(p.name)}</td>
      <td data-label="Precio" style="font-family:'JetBrains Mono',monospace;font-weight:600">${fmtPrice(p.price)} ${esc(p.currency)}</td>
      <td data-label="Vigencia">${esc(valid)}</td>
      <td data-label="Estado">${p.is_active ? '<span class="st resolved">Activo</span>' : '<span class="st active">Inactivo</span>'}</td>
      <td data-label="Acciones"><div class="ac"><button class="ab ed" data-prod-edit="${p.id}" title="Editar"><i class="fas fa-pen"></i></button><button class="ab" data-prod-del="${p.id}" title="Desactivar"><i class="fas fa-ban"></i></button></div></td>
    </tr>`
  })
  h += '</tbody></table></div></div></div>'
  g.innerHTML = h
}

document.getElementById('btnNewProductAdmin')!.addEventListener('click', () => go('/products-admin/new'))

document.getElementById('productsAdminG')!.addEventListener('click', async function(e){
  const editBtn = (e.target as HTMLElement).closest('[data-prod-edit]') as HTMLElement | null
  const delBtn = (e.target as HTMLElement).closest('[data-prod-del]') as HTMLElement | null
  if(editBtn){
    go('/products-admin/' + encodeURIComponent(editBtn.dataset.prodEdit!))
    return
  }
  if(delBtn){
    const id = delBtn.dataset.prodDel!
    if(!confirm('Desactivar este producto?')) return
    const r = await api.deleteProduct(id)
    if(r.ok && r.data?.success){ toast('Producto desactivado','ok'); renderProductsAdmin() }
    else toast('Error','er')
  }
})

let _currentProductId: string | null = null

function renderProductImages(images: any[]){
  const container = document.getElementById('prodImagesList')!
  if(!images.length){ container.innerHTML = '<span class="thumb-list__empty">Sin imágenes</span>'; return }
  container.innerHTML = images.map(img => `
    <div class="thumb">
      <img src="${IMG_BASE}/api/v1/products/images/${encodeURIComponent(img.file_key)}" alt="${esc(img.filename)}" loading="lazy">
      <button type="button" class="thumb__del prod-img-del" data-img-id="${esc(String(img.id))}" aria-label="Eliminar imagen ${esc(img.filename)}"><i class="fas fa-xmark" aria-hidden="true"></i></button>
    </div>
  `).join('')
}

// Subir imágenes requiere un producto ya guardado (necesita id)
function setProductImageUpload(enabled: boolean){
  const btn = document.getElementById('prodImageBtn')!
  const input = document.getElementById('prodImageInput') as HTMLInputElement
  btn.setAttribute('aria-disabled', enabled ? 'false' : 'true')
  input.disabled = !enabled
  document.getElementById('prodImageStatus')!.textContent = enabled ? '' : 'Guarda el producto para poder subir imágenes.'
}

const $in = (id: string) => document.getElementById(id) as HTMLInputElement
const $sel = (id: string) => document.getElementById(id) as HTMLSelectElement

async function renderProductEditPage(id?: string){
  const f = document.getElementById('frmProduct') as HTMLFormElement
  f.reset()
  clearFormErrors(f)
  _currentProductId = id || null
  $in('prodId').value = id || ''
  document.getElementById('prodTitle')!.textContent = id ? 'Editar Producto' : 'Nuevo Producto'
  document.getElementById('prodSubmitLabel')!.textContent = id ? 'Guardar cambios' : 'Crear producto'
  renderProductImages([])
  setProductImageUpload(!!id)
  if(!id){
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    $in('prodValidFrom').value = now.toISOString().slice(0,16)
    $in('prodCurrency').value = 'CLP'
    $in('prodName').focus()
    return
  }
  const r = await api.getProducts()
  const p = r.ok && r.data?.success ? (r.data.products || []).find((x:any) => x.id === id) : null
  if(!p){ toast('Producto no encontrado','er'); go('/products-admin'); return }
  if(_currentProductId !== id) return // el usuario ya navegó a otro producto
  $in('prodCode').value = p.code || ''
  $sel('prodCategory').value = p.category || 'service'
  $sel('prodType').value = p.type || 'service'
  $in('prodName').value = p.name || ''
  $in('prodPrice').value = p.price ?? ''
  $in('prodCurrency').value = p.currency || 'CLP'
  $in('prodValidFrom').value = p.valid_from ? p.valid_from.slice(0,16) : ''
  $in('prodValidUntil').value = p.valid_until ? p.valid_until.slice(0,16) : ''
  $in('prodActive').checked = !!p.is_active
  renderProductImages(p.images || [])
}

document.getElementById('frmProduct')!.onsubmit = async function(e){
  e.preventDefault()
  const form = this as HTMLFormElement
  clearFormErrors(form)
  const id = $in('prodId').value
  const name = $in('prodName').value.trim()
  const code = $in('prodCode').value.trim()
  const priceRaw = $in('prodPrice').value
  const from = $in('prodValidFrom').value
  const until = $in('prodValidUntil').value
  let bad = ''
  if(!name){ fieldMessage('prodName', 'Ingresa el nombre del producto'); bad ||= 'prodName' }
  if(!code){ fieldMessage('prodCode', 'Ingresa un código'); bad ||= 'prodCode' }
  else if(/\s/.test(code)){ fieldMessage('prodCode', 'El código no puede tener espacios'); bad ||= 'prodCode' }
  if(priceRaw === '' || Number(priceRaw) < 0){ fieldMessage('prodPrice', 'Ingresa un precio válido (0 o más)'); bad ||= 'prodPrice' }
  if(from && until && new Date(until) <= new Date(from)){ fieldMessage('prodValidUntil', 'Debe ser posterior a "Vigente desde"'); bad ||= 'prodValidUntil' }
  if(bad){ $in(bad).focus(); return }
  const payload = {
    code,
    category: $sel('prodCategory').value,
    type: $sel('prodType').value,
    name,
    price: parseFloat(priceRaw || '0'),
    currency: $in('prodCurrency').value.trim().toUpperCase() || 'CLP',
    valid_from: from ? new Date(from).toISOString() : new Date().toISOString(),
    valid_until: until ? new Date(until).toISOString() : null,
    is_active: $in('prodActive').checked
  }
  const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement
  submit.disabled = true
  const r = id ? await api.updateProduct(id, payload) : await api.createProduct(payload)
  submit.disabled = false
  if(r.ok && r.data?.success){
    const newId = r.data.product?.id
    if(!id && newId){
      // Recién creado: quedarse en su página para poder subir imágenes
      toast('Producto creado. Ya puedes agregar imágenes.','ok')
      go('/products-admin/' + encodeURIComponent(newId))
    } else {
      toast('Producto actualizado','ok')
      go('/products-admin')
    }
  } else {
    toast(r.data?.error || 'Error al guardar el producto','er')
  }
}

// Image upload handler
document.getElementById('prodImageInput')!.addEventListener('change', async function(e){
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if(!file || !_currentProductId) return
  const status = document.getElementById('prodImageStatus')!
  status.textContent = 'Subiendo...'
  const r = await api.uploadProductImage(_currentProductId, file)
  input.value = ''
  if(r.ok && r.data?.success){
    status.textContent = 'Imagen subida'
    renderProductImages(r.data.images || [])
  } else {
    status.textContent = r.data?.error || 'Error al subir'
  }
  setTimeout(() => { status.textContent = '' }, 3000)
})

// Image delete handler (delegado en la página)
document.getElementById('pg-product-edit')!.addEventListener('click', async function(e){
  const delBtn = (e.target as HTMLElement).closest('.prod-img-del') as HTMLElement | null
  if(!delBtn || !_currentProductId) return
  const imageId = delBtn.dataset.imgId!
  if(!confirm('Eliminar esta imagen?')) return
  const r = await api.deleteProductImage(_currentProductId, imageId)
  if(r.ok && r.data?.success){
    toast('Imagen eliminada','ok')
    renderProductImages(r.data.images || [])
  } else {
    toast(r.data?.error || 'Error','er')
  }
})

// ===== BILLING / SUBSCRIPTION =====
async function renderSuscripciones(){
  await renderBilling('suscripcionesG')
}

async function renderBilling(containerId = 'billingG'){
  const g = document.getElementById(containerId)!
  if(CU.role!=='superadmin' && CU.role!=='user'){ g.innerHTML = emptyState('Acceso restringido','fa-lock'); return }
  g.innerHTML = '<div style="text-align:center;padding:40px;color:var(--fg3)"><i class="fas fa-spinner fa-spin" style="font-size:24px"></i></div>'

  const [sr, hr, pr] = await Promise.all([
    api.getBillingStatus().catch(() => null),
    api.getBillingHistory().catch(() => null),
    api.getPlans().catch(() => null),
  ])

  const sub = sr?.ok ? sr.data?.subscription : null
  const history = hr?.ok ? hr.data?.history || [] : []
  const plansList = pr?.ok ? pr.data?.plans || [] : []

  function fmtPrice(n: number){
    return '$' + Number(n).toLocaleString('es-CL')
  }

  function fmtDate(d: string | undefined){
    if(!d) return '—'
    return d.slice(0,10).split('-').reverse().join('/')
  }

  function statusBadge(st: string){
    const mp: Record<string,string> = { active:'ok', pending:'wa', cancelled:'er', paused:'in', approved:'ok', rejected:'er', refunded:'in' }
    return `<span class="st ${mp[st]||'in'}">${st}</span>`
  }

  let h = '<div style="max-width:840px;margin:0 auto">'

  // Subscription status card
  h += '<div class="stit mt0"><i class="fas fa-receipt"></i> Mi Suscripcion</div>'
  if(sub){
    const isActive = sub.status === 'active'
    const isPending = sub.status === 'pending'
    const planDetail = plansList.find((p:any) => p.id === sub.plan_id)
    h += `<div class="cc" style="padding:28px">
      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:16px">
        <div style="display:flex;align-items:center;gap:12px">
          <div style="font-size:36px;color:${isActive?'var(--green)':'var(--fg3)'}"><i class="fas ${isActive?'fa-circle-check':'fa-circle'}"></i></div>
          <div>
            <div style="font-size:20px;font-weight:700">${esc(sub.plan_name)}</div>
            <div style="font-size:14px;color:var(--fg3)">${fmtPrice(sub.price)} / ${sub.period==='year'?'anual':'mensual'}</div>
          </div>
        </div>
        <div>${statusBadge(sub.status)}</div>
      </div>`

    // Active plan details
    if(isActive && planDetail){
      const features = (planDetail.features || '').split(',').filter(Boolean)
      h += `<div style="background:var(--bg);border:1px solid var(--bdr);border-radius:8px;padding:16px;margin-bottom:16px">
        <div style="font-weight:600;font-size:14px;margin-bottom:10px;color:var(--fg2)">Recursos del Plan</div>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px;font-size:13px">
          ${planDetail.max_users != null ? `<div><span style="color:var(--fg3)">Usuarios:</span> ${planDetail.max_users < 0 ? 'Ilimitados' : planDetail.max_users}</div>` : ''}
          ${planDetail.max_contacts != null ? `<div><span style="color:var(--fg3)">Contactos:</span> ${planDetail.max_contacts < 0 ? 'Ilimitados' : planDetail.max_contacts}</div>` : ''}
          ${planDetail.iot_enabled ? '<div><span style="color:var(--fg3)">IoT:</span> <span style="color:var(--green)">Habilitado</span></div>' : '<div><span style="color:var(--fg3)">IoT:</span> <span style="color:var(--fg3)">No incluido</span></div>'}
        </div>
      </div>`
    }

    // Dates
    h += `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;margin-bottom:16px;font-size:13px">
      ${sub.since ? `<div style="background:var(--bg);border:1px solid var(--bdr);border-radius:8px;padding:12px"><span style="color:var(--fg3);display:block;font-size:11px">Inicio del plan</span><span style="font-weight:600">${fmtDate(sub.since)}</span></div>` : ''}
      ${sub.period_end ? `<div style="background:var(--bg);border:1px solid var(--bdr);border-radius:8px;padding:12px"><span style="color:var(--fg3);display:block;font-size:11px">Próximo vencimiento</span><span style="font-weight:600">${fmtDate(sub.period_end)}</span></div>` : ''}
      ${sub.cancelled_at ? `<div style="background:var(--bg);border:1px solid var(--bdr);border-radius:8px;padding:12px"><span style="color:var(--fg3);display:block;font-size:11px">Cancelado el</span><span style="font-weight:600">${fmtDate(sub.cancelled_at)}</span></div>` : ''}
    </div>`

    // Plan features list
    if(isActive && planDetail){
      const feats = (planDetail.features || '').split(',').filter(Boolean)
      if(feats.length){
        h += `<div style="margin-bottom:12px">
          <div style="font-weight:600;font-size:13px;margin-bottom:6px;color:var(--fg2)">Caracteristicas incluidas</div>
          <div style="display:flex;flex-wrap:wrap;gap:4px">`
        feats.forEach((f:string) => {
          h += `<span style="font-size:12px;background:var(--green-g);color:var(--green);padding:2px 8px;border-radius:4px"><i class="fas fa-check" style="font-size:10px"></i> ${esc(f.trim())}</span>`
        })
        h += `</div></div>`
      }
    }

    // Meta info
    h += `<div style="font-size:12px;color:var(--fg3);opacity:.7">
      ${sub.id ? `ID: <code style="font-size:11px">${sub.id.slice(0,8)}...</code>` : ''}
    </div>`

      h += `<div style="margin-top:16px;display:flex;gap:8px;flex-wrap:wrap">
        ${isActive ? `<button class="btn btn-d btn-sm" id="btnCancelSub"><i class="fas fa-ban"></i> Cancelar Suscripcion</button>` : ''}
        ${isPending ? `<button class="btn btn-s btn-sm" id="btnRetryPay"><i class="fas fa-credit-card"></i> Reintentar Pago</button>` : ''}
        ${isPending ? `<button class="btn btn-d btn-sm" id="btnCancelPending"><i class="fas fa-times"></i> Cancelar</button>` : ''}
      </div>
    </div>`
  } else {
    // No subscription
    h += `<div class="cc" style="text-align:center;padding:40px">
      <div style="font-size:48px;color:var(--fg3);margin-bottom:12px"><i class="fas fa-circle"></i></div>
      <div style="font-size:18px;font-weight:600;margin-bottom:4px">Sin suscripcion activa</div>
      <div style="font-size:14px;color:var(--fg3);margin-bottom:16px">Elige un plan para proteger tu hogar o comunidad.</div>
      <button class="btn btn-s" id="btnGoPlans"><i class="fas fa-credit-card"></i> Ver Planes</button>
    </div>`
  }

  // Payment history
  h += '<div class="stit mt4"><i class="fas fa-clock-rotate-left"></i> Historial de Pagos</div>'
  if(history.length){
    h += history.map((p:any) => `
      <div style="display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:var(--card);border:1px solid var(--bdr);border-radius:var(--rl);margin-bottom:6px">
        <div>
          <div style="font-weight:600;font-size:14px">${esc(p.plan_name||'Pago')}</div>
          <div style="font-size:12px;color:var(--fg3)">${fmtDate(p.created_at)} ${p.payment_method ? '· '+esc(p.payment_method) : ''}</div>
        </div>
        <div style="text-align:right">
          <div style="font-weight:700;font-size:15px">${fmtPrice(p.amount)}</div>
          <div>${statusBadge(p.status)}</div>
        </div>
      </div>
    `).join('')
  } else {
    h += `<div style="text-align:center;padding:24px;color:var(--fg3);font-size:13px">Sin pagos registrados</div>`
  }

  h += '</div>'
  g.innerHTML = h

  // Nav to plans
  const goBtn = document.getElementById('btnGoPlans')
  if(goBtn) goBtn.onclick = () => nav('subscription')

  const billingContainer = () => document.getElementById('billingG')?.offsetParent ? 'billingG' : 'suscripcionesG'

  // Cancel active subscription handler
  const cancelBtn = document.getElementById('btnCancelSub')
  if(cancelBtn){
    cancelBtn.onclick = async () => {
      if(!confirm('Cancelar tu suscripcion? Perderas acceso a las funcionalidades del plan actual al final del periodo facturado.')) return
      const r = await api.cancelSubscription()
      if(r.ok && r.data?.success){ toast('Suscripcion cancelada','ok'); renderBilling(billingContainer()) }
      else toast(r.data?.error || 'Error al cancelar','er')
    }
  }

  // Retry payment for pending subscription
  const retryBtn = document.getElementById('btnRetryPay')
  if(retryBtn){
    retryBtn.onclick = async () => {
      if(!sub || !sub.plan_id) return
      const r = await api.subscribeToPlan(sub.plan_id, String(window.location.origin || '') + '/billing')
      if(r.ok && r.data?.success && r.data.init_point){
        window.location.href = r.data.init_point
      } else if(r.ok && r.data?.success && r.data.payment_link){
        window.location.href = r.data.payment_link
      } else {
        toast(r.data?.error || 'Error al obtener link de pago','er')
      }
    }
  }

  // Cancel pending subscription
  const cancelPendingBtn = document.getElementById('btnCancelPending')
  if(cancelPendingBtn){
    cancelPendingBtn.onclick = async () => {
      if(!confirm('Cancelar esta suscripcion pendiente?')) return
      const r = await api.cancelSubscription()
      if(r.ok && r.data?.success){ toast('Suscripcion cancelada','ok'); renderBilling(billingContainer()) }
      else toast(r.data?.error || 'Error al cancelar','er')
    }
  }
}

let planFeatures: string[] = []

function renderPlanFeatureChips(){
  const grid = document.getElementById('pfFeatureGrid')!
  const hidden = document.getElementById('pfF') as HTMLInputElement
  hidden.value = planFeatures.join(',')
  if(planFeatures.length === 0){
    grid.innerHTML = '<span style="color:var(--fg3);font-size:12px;font-style:italic">Sin características</span>'
    return
  }
  grid.innerHTML = planFeatures.map((f, i) => `
    <span class="feature-chip">
      ${esc(f)}
      <button type="button" data-pf-remove="${i}" aria-label="Eliminar"><i class="fas fa-xmark"></i></button>
    </span>
  `).join('')
}

function addPlanFeature(){
  const input = document.getElementById('pfFeatureInput') as HTMLInputElement
  const v = input.value.trim()
  if(!v) return
  if(planFeatures.includes(v)) { toast('Caracteristica ya existe','wa'); return }
  planFeatures.push(v)
  input.value = ''
  renderPlanFeatureChips()
}

document.getElementById('pfFeatureAdd')!.addEventListener('click', addPlanFeature)
document.getElementById('pfFeatureInput')!.addEventListener('keydown', function(e){
  if(e.key === 'Enter'){ e.preventDefault(); addPlanFeature() }
})
document.getElementById('pfFeatureGrid')!.addEventListener('click', function(e){
  const btn = (e.target as HTMLElement).closest('[data-pf-remove]') as HTMLElement | null
  if(!btn) return
  const idx = parseInt(btn.dataset.pfRemove!)
  planFeatures.splice(idx, 1)
  renderPlanFeatureChips()
})

async function fillProductSelects(){
  const r = await api.getProducts()
  const products = (r.ok && r.data?.success ? r.data.products || [] : []) as any[]
  const mk = (sel: HTMLSelectElement, filter?: string) => {
    const cur = sel.value
    sel.innerHTML = `<option value="">${sel.dataset.default || 'Seleccionar...'}</option>`
    products.filter((p: any) => p.is_active && (!filter || p.category === filter)).forEach((p: any) => {
      const o = document.createElement('option')
      o.value = p.code
      o.textContent = `${p.name} (${fmtPrice(p.price)} ${p.currency})`
      sel.appendChild(o)
    })
    if(cur) sel.value = cur
  }
  const base = document.getElementById('pfBaseProduct') as HTMLSelectElement
  const user = document.getElementById('pfProdUser') as HTMLSelectElement
  const gateway = document.getElementById('pfProdGateway') as HTMLSelectElement
  const siren = document.getElementById('pfProdSiren') as HTMLSelectElement
  base.dataset.default = 'Sin base'
  user.dataset.default = 'Sin usuario'
  gateway.dataset.default = 'Sin gateway'
  siren.dataset.default = 'Sin sirena'
  mk(base, 'base')
  mk(user, 'user')
  mk(gateway, 'device')
  mk(siren, 'device')
  return products
}

async function renderPlanEditPage(pid?: string) {
  const pf = document.getElementById('frmPlan') as HTMLFormElement
  pf.reset()
  planFeatures = []
  renderPlanFeatureChips()
  ;(document.getElementById('pfId') as HTMLInputElement).value = pid || ''
  document.getElementById('mpTitle')!.textContent = pid ? 'Editar Plan' : 'Nuevo Plan'
  ;(document.getElementById('pfSubmitLabel') as HTMLElement).textContent = pid ? 'Guardar Plan' : 'Guardar Plan'
  // Fill product selects first
  await fillProductSelects()
  if (pid && API_MODE) {
    const r = await api.getPlans()
    if (r.ok && r.data?.success) {
      const plan = (r.data.plans || []).find(function (x: any) { return x.id === pid })
      if (plan) {
        ;(document.getElementById('pfN') as HTMLInputElement).value = plan.name || ''
        ;(document.getElementById('pfPt') as HTMLSelectElement).value = plan.price_type || 'fixed'
        ;(document.getElementById('pfP') as HTMLInputElement).value = plan.price == null ? '' : plan.price
        ;(document.getElementById('pfPeriod') as HTMLSelectElement).value = plan.period || 'month'
        ;(document.getElementById('pfQt') as HTMLInputElement).value = plan.quote_button_text || ''
        ;(document.getElementById('pfD') as HTMLInputElement).value = plan.description || ''
        planFeatures = (plan.features || '').split(',').map(function (x: string) { return x.trim() }).filter(Boolean)
        renderPlanFeatureChips()
        ;(document.getElementById('pfMu') as HTMLInputElement).value = plan.max_users || ''
        ;(document.getElementById('pfMc') as HTMLInputElement).value = plan.max_contacts || ''
        ;(document.getElementById('pfMd') as HTMLInputElement).value = plan.max_devices || ''
        ;(document.getElementById('pfIoT') as HTMLInputElement).checked = !!plan.iot_enabled
        ;(document.getElementById('pfCustom') as HTMLInputElement).checked = !!plan.is_customizable
        ;(document.getElementById('pfMpLink') as HTMLInputElement).value = plan.payment_link || ''
        const productCodes = plan.product_codes ? (typeof plan.product_codes === 'string' ? JSON.parse(plan.product_codes) : plan.product_codes) : {}
        ;(document.getElementById('pfBaseProduct') as HTMLSelectElement).value = plan.base_product_code || ''
        ;(document.getElementById('pfProdUser') as HTMLSelectElement).value = productCodes.user || ''
        ;(document.getElementById('pfProdGateway') as HTMLSelectElement).value = productCodes.gateway || ''
        ;(document.getElementById('pfProdSiren') as HTMLSelectElement).value = productCodes.siren || ''
      }
    }
  }
}


document.getElementById('frmPlan')!.onsubmit = async function (e) {
  e.preventDefault()
  clearFormErrors(document.getElementById('frmPlan') as HTMLFormElement | null)
  const id = (document.getElementById('pfId') as HTMLInputElement).value
  const priceType = (document.getElementById('pfPt') as HTMLSelectElement).value
  const numVal = (id: string) => {
    const el = document.getElementById(id) as HTMLInputElement
    return el.value ? parseInt(el.value) : null
  }
  const productCodes: any = {}
  const userCode = (document.getElementById('pfProdUser') as HTMLSelectElement).value
  const gatewayCode = (document.getElementById('pfProdGateway') as HTMLSelectElement).value
  const sirenCode = (document.getElementById('pfProdSiren') as HTMLSelectElement).value
  if (userCode) productCodes.user = userCode
  if (gatewayCode) productCodes.gateway = gatewayCode
  if (sirenCode) productCodes.siren = sirenCode
  const payload: any = {
    name: (document.getElementById('pfN') as HTMLInputElement).value.trim(),
    price_type: priceType,
    price: priceType === 'quote' ? 0 : parseFloat((document.getElementById('pfP') as HTMLInputElement).value || '0'),
    period: (document.getElementById('pfPeriod') as HTMLSelectElement).value,
    quote_button_text: (document.getElementById('pfQt') as HTMLInputElement).value.trim() || null,
    description: (document.getElementById('pfD') as HTMLInputElement).value.trim(),
    features: planFeatures.join(','),
    max_users: numVal('pfMu'),
    max_contacts: numVal('pfMc'),
    max_devices: numVal('pfMd'),
    base_product_code: (document.getElementById('pfBaseProduct') as HTMLSelectElement).value || null,
    product_codes: productCodes,
    iot_enabled: (document.getElementById('pfIoT') as HTMLInputElement).checked ? 1 : 0,
    is_customizable: (document.getElementById('pfCustom') as HTMLInputElement).checked ? 1 : 0,
    payment_link: (document.getElementById('pfMpLink') as HTMLInputElement).value.trim() || null
  }
  const r = id ? await api.updatePlan(id, payload) : await api.createPlan(payload)
  if (r.ok && r.data.success) {
    toast(id ? 'Plan actualizado' : 'Plan creado', 'ok')
    go('/plans')
  } else {
    toast(r.data?.error || 'Error', 'er')
  }
}

// ===== CONTACTS =====
let lastContacts: any[] = []
async function renderContacts(){
  const container = document.getElementById('contactsList')!
  container.innerHTML = '<div class="empty"><i class="fas fa-circle-notch fa-spin"></i><p>Cargando contactos...</p></div>'

  const res = API_MODE ? await api.getContacts() : { ok: true, data: { success: true, contacts: (db()?.contacts || []) } }
  if (!res.ok || !res.data?.success) {
    container.innerHTML = '<div class="empty"><i class="fas fa-triangle-exclamation"></i><p>Error cargando contactos</p></div>'
    return
  }
  const contacts = res.data.contacts || []
  lastContacts = contacts

  if (!contacts.length) {
    container.innerHTML = emptyState('Sin contactos de emergencia', 'fa-address-book')
    return
  }

  let h = ''
  contacts.forEach((ct: any) => {
    h += '<div class="ai"><div class="ai-i security"><i class="fas fa-user-shield"></i></div>'
    h += '<div class="ai-b"><div class="ai-t">' + esc(ct.name) + '</div>'
    h += '<div class="ai-d">' + esc(ct.phone) + (ct.relationship ? ' — ' + esc(ct.relationship) : '') + '</div>'
    h += '<div class="ai-d" style="font-size:11px;color:' + ((ct.whatsapp ?? 1) ? 'var(--green)' : 'var(--fg3)') + '"><i class="fab fa-whatsapp"></i> ' + ((ct.whatsapp ?? 1) ? 'Recibe WhatsApp' : 'Sin WhatsApp') + '</div></div>'
    h += '<div class="ai-m"><span class="st ' + (ct.active ? 'resolved' : 'active') + '">' + (ct.active ? 'Activo' : 'Inactivo') + '</span></div>'
    h += '<div class="ac">'
    h += '<button class="ab ed" data-cedit="' + ct.id + '" title="Editar"><i class="fas fa-pen"></i></button>'
    if (ct.user_id === CU.id || ['admin','superadmin'].includes(CU.role)) {
      h += '<button class="ab" data-ctoggle="' + ct.id + '" title="' + (ct.active ? 'Desactivar' : 'Activar') + '"><i class="fas fa-' + (ct.active ? 'ban' : 'check') + '"></i></button>'
      h += '<button class="ab" data-cdel="' + ct.id + '" title="Eliminar"><i class="fas fa-trash"></i></button>'
    }
    h += '</div></div>'
  })
  container.innerHTML = h

  container.onclick = (e: Event) => {
    const btn = (e.target as HTMLElement).closest('[data-cedit],[data-ctoggle],[data-cdel]') as HTMLElement | null
    if (!btn) return
    if (btn.dataset.cedit) go('/contacts/' + encodeURIComponent(btn.dataset.cedit))
    else if (btn.dataset.ctoggle) toggleContact(btn.dataset.ctoggle)
    else if (btn.dataset.cdel) delContact(btn.dataset.cdel)
  }
}

async function renderContactEditPage(id?: string) {
  const form = document.getElementById('frmContact') as HTMLFormElement
  form.reset()
  ;(document.getElementById('cfId') as HTMLInputElement).value = ''
  document.getElementById('mcTitle')!.textContent = 'Nuevo Contacto'
  ;(document.getElementById('cfSubmitLabel') as HTMLElement).textContent = 'Guardar contacto'

  if (id) {
    document.getElementById('mcTitle')!.textContent = 'Editar Contacto'
    ;(document.getElementById('cfId') as HTMLInputElement).value = id
    const ct = lastContacts.find((x: any) => x.id === id) || (db()?.contacts || []).find((x: any) => x.id === id)
    if (ct) {
      const n = document.getElementById('cfN') as HTMLInputElement
      const p = document.getElementById('cfP') as HTMLInputElement
      const r = document.getElementById('cfR') as HTMLInputElement
      n.value = ct.name || ''
      p.value = ct.phone || ''
      r.value = ct.relationship || ''
      ;(document.getElementById('cfW') as HTMLInputElement).checked = (ct.whatsapp ?? 1) ? true : false
    }
  } else {
    ;(document.getElementById('cfW') as HTMLInputElement).checked = true
  }
}

document.getElementById('btnNewContact')!.onclick = () => go('/contacts/new')

document.getElementById('frmContact')!.onsubmit = async (e) => {
  e.preventDefault()
  clearFormErrors(document.getElementById('frmContact') as HTMLFormElement | null)
  const id = (document.getElementById('cfId') as HTMLInputElement).value
  const name = (document.getElementById('cfN') as HTMLInputElement).value.trim()
  const phone = (document.getElementById('cfP') as HTMLInputElement).value.trim()
  const relationship = (document.getElementById('cfR') as HTMLInputElement).value.trim()
  const whatsapp = (document.getElementById('cfW') as HTMLInputElement).checked ? 1 : 0
  let invalid = false
  if (!name) { fieldMessage('cfN', 'Ingresa el nombre'); invalid = true }
  if (!phone) { fieldMessage('cfP', 'Ingresa el telefono'); invalid = true }
  if (invalid) return

  if (API_MODE) {
    const payload = { name, phone, relationship, whatsapp }
    const r = id ? await api.updateContact(id, payload) : await api.createContact(payload)
    if (r.ok && r.data?.success) {
      toast(id ? 'Contacto actualizado' : 'Contacto creado', 'ok')
      go('/notifications')
    } else {
      toast(r.data?.error || 'Error al guardar contacto', 'er'); return
    }
  } else {
    const d = db()!
    if (!d.contacts) d.contacts = []
    if (id) {
      const ct = d.contacts.find((x: any) => x.id === id)
      if (ct) { ct.name = name; ct.phone = phone; ct.relationship = relationship; ct.whatsapp = whatsapp }
    } else {
      d.contacts.push({ id: gid(), user_id: CU.id, name, phone, relationship, active: 1, whatsapp, created_at: new Date().toISOString() })
    }
    dbSave(d)
    toast(id ? 'Contacto actualizado' : 'Contacto creado', 'ok')
    go('/notifications')
  }
}

async function toggleContact(id: string) {
  if (API_MODE) {
    const r = await api.toggleContact(id)
    if (!r.ok || !r.data?.success) { toast('Error', 'er'); return }
  } else {
    const d = db()!
    const ct = d.contacts?.find((x: any) => x.id === id)
    if (ct) { ct.active = ct.active ? 0 : 1; dbSave(d) }
  }
  toast('Contacto actualizado', 'ok')
  renderContacts()
}

async function delContact(id: string) {
  if (API_MODE) {
    const r = await api.deleteContact(id)
    if (!r.ok || !r.data?.success) { toast('Error', 'er'); return }
  } else {
    const d = db()!
    d.contacts = (d.contacts || []).filter((x: any) => x.id !== id)
    dbSave(d)
  }
  toast('Contacto eliminado', 'ok')
  renderContacts()
}

// ===== EXTERNAL CONTACTS =====
async function renderExtContacts(){
  const tbody = document.getElementById('extCBody')!
  tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--fg3)"><i class="fas fa-circle-notch fa-spin"></i> Cargando...</td></tr>'

  if (!API_MODE) { tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--fg3)">No disponible offline</td></tr>'; return }

  const res = await api.getExtContacts()
  if (!res.ok || !res.data?.success) {
    tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--fg3)">Error cargando contactos</td></tr>'
    return
  }
  const contacts = res.data.contacts || []

  if (!contacts.length) {
    tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--fg3)"><i class="fas fa-address-book"></i> Sin contactos externos</td></tr>'
    return
  }

  let h = ''
  contacts.forEach((c: any) => {
    const status = c.status || (c.active ? 'active' : 'opted_out')
    const statusLbl = status === 'active' ? 'Confirmado' : status === 'pending_optin' ? 'Pendiente' : 'Dado de baja'
    const statusCls = status === 'active' ? 'resolved' : status === 'pending_optin' ? 'active' : 'cancelled'
    h += '<tr>'
    h += '<td data-label="Nombre"><div style="font-weight:600">' + esc(c.name) + '</div></td>'
    h += '<td data-label="Telefono">' + esc(c.phone) + '</td>'
    h += '<td data-label="Relacion">' + (c.relationship ? esc(c.relationship) : '-') + '</td>'
    h += '<td data-label="Grupo">' + (c.group_name || (c.group_id ? 'Asignado' : '-')) + '</td>'
    h += '<td data-label="Estado"><span class="st ' + statusCls + '">' + statusLbl + '</span></td>'
    h += '<td class="extc-actions" data-label="Acciones"><div style="display:flex;gap:4px">'
    h += '<button class="btn btn-s btn-sm" data-ecedit="' + c.id + '"><i class="fas fa-pen"></i></button>'
    if (status === 'pending_optin') h += '<button class="btn btn-o btn-sm" data-ecresend="' + c.id + '"><i class="fas fa-paper-plane"></i></button>'
    h += '<button class="btn btn-d btn-sm" data-ecdel="' + c.id + '"><i class="fas fa-trash"></i></button>'
    h += '</div></td>'
    h += '</tr>'
  })
  tbody.innerHTML = h

  tbody.onclick = async (e: Event) => {
    const btn = (e.target as HTMLElement).closest('[data-ecedit],[data-ecdel],[data-ecresend]') as HTMLElement | null
    if (!btn) return
    if (btn.dataset.ecedit) go('/external-contacts/' + encodeURIComponent(btn.dataset.ecedit))
    else if (btn.dataset.ecdel) delExtContact(btn.dataset.ecdel)
    else if (btn.dataset.ecresend) {
      const r = await api.resendExtConfirm(btn.dataset.ecresend)
      toast(r.ok && r.data?.wa_sent ? 'WhatsApp de confirmacion reenviado' : 'Error al reenviar (Evolution no disponible)', r.ok ? 'ok' : 'wa')
    }
  }
}

async function loadExtGroups(){
  const sel = document.getElementById('ecfG') as HTMLSelectElement
  if (!sel) return
  if (CU.role !== 'admin' && CU.role !== 'superadmin') { sel.style.display = 'none'; return }
  sel.style.display = ''
  const res = await api.getGroups()
  if (res.ok && res.data?.success) {
    sel.innerHTML = '<option value="">Sin grupo</option>'
    ;(res.data.groups || []).forEach((g: any) => {
      sel.innerHTML += '<option value="' + esc(g.id) + '">' + esc(g.name) + '</option>'
    })
  }
}

async function renderExtContactEditPage(id?: string) {
  const form = document.getElementById('frmExtContact') as HTMLFormElement
  form.reset()
  ;(document.getElementById('ecfId') as HTMLInputElement).value = ''
  document.getElementById('mecTitle')!.textContent = 'Nuevo Contacto Externo'
  ;(document.getElementById('ecfSubmitLabel') as HTMLElement).textContent = 'Guardar contacto externo'
  await loadExtGroups()

  if (id) {
    document.getElementById('mecTitle')!.textContent = 'Editar Contacto Externo'
    ;(document.getElementById('ecfId') as HTMLInputElement).value = id
  }
}

document.getElementById('btnNewExtContact')!.onclick = () => go('/external-contacts/new')

document.getElementById('frmExtContact')!.onsubmit = async (e) => {
  e.preventDefault()
  clearFormErrors(document.getElementById('frmExtContact') as HTMLFormElement | null)
  const id = (document.getElementById('ecfId') as HTMLInputElement).value
  const name = (document.getElementById('ecfN') as HTMLInputElement).value.trim()
  const phone = (document.getElementById('ecfP') as HTMLInputElement).value.trim()
  const relationship = (document.getElementById('ecfR') as HTMLInputElement).value.trim()
  const group_id = (document.getElementById('ecfG') as HTMLSelectElement).value
  let invalid = false
  if (!name) { fieldMessage('ecfN', 'Ingresa el nombre'); invalid = true }
  if (!phone) { fieldMessage('ecfP', 'Ingresa el telefono'); invalid = true }
  if (invalid) return

  const payload: any = { name, phone, relationship }
  if (group_id) payload.group_id = group_id
  const r = id ? await api.updateExtContact(id, payload) : await api.createExtContact(payload)
  if (r.ok && r.data?.success) {
    toast(id ? 'Contacto externo actualizado' : 'Contacto externo creado', 'ok')
    go('/external-contacts')
  } else {
    toast(r.data?.error || 'Error al guardar', 'er'); return
  }
}

async function delExtContact(id: string) {
  if (!confirm('Eliminar este contacto externo?')) return
  const r = await api.deleteExtContact(id)
  if (r.ok && r.data?.success) {
    toast('Contacto externo eliminado', 'ok')
    renderExtContacts()
  } else {
    toast('Error al eliminar', 'er')
  }
}

// ===== IOT =====
let _iotDevicesCache: any[] = []
let _iotView: 'cards'|'table' = 'cards'

function _setIotView(v: 'cards'|'table'){
  _iotView = v
  localStorage.setItem('iotView', v)
  document.getElementById('iotViewCards')!.classList.toggle('act', v==='cards')
  document.getElementById('iotViewTable')!.classList.toggle('act', v==='table')
  document.getElementById('iotG')!.className = v==='table' ? 'iot-tabla' : 'ig'
}

function _buildIotTable(devices: any[], ico: Record<string,string>, col: Record<string,string>, lbl: Record<string,string>, bcls: Record<string,string>, apiBase: string, envLabels: Record<string,string>, isAdmin = true): string {
  let h='<table class="iot-tabla-tbl"><thead><tr><th>Tipo</th><th>Nombre</th><th>Ubicación</th><th>Estado</th><th>Topic</th><th>Acciones</th></tr></thead><tbody>'
  devices.forEach(function(dev:any){
    const c=col[dev.type]||'var(--fg3)'
    const cfg = dev.config_json ? (typeof dev.config_json === 'string' ? JSON.parse(dev.config_json) : dev.config_json) : {}
    const relays: any[] = cfg.relays || []
    const statusLabel = dev.status==='online'?'En linea':'Desconectado'
    const statusCls = dev.status==='online'?'s-on':'s-off'
    h+=`<tr data-iot-device-id="${esc(dev.id)}"><td><span class="iot-t-icon" style="background:${c}22;color:${c}"><i class="fas ${ico[dev.type]||'fa-microchip'}"></i></span></td>`
    h+=`<td class="iot-t-name">${esc(dev.name)}</td>`
    h+=`<td>${esc(dev.loc||'')}</td>`
    h+=`<td><span class="ds ${dev.status}">${statusLabel}</span></td>`
    h+=`<td><code style="font-size:10px">${esc(dev.topic||'')}</code></td>`
    h+=`<td><div class="id2-a">`
    if(lbl[dev.type]) h+=`<button class="btn ${bcls[dev.type]} btn-sm" data-iot-act="${dev.id}"><i class="fas fa-power-off"></i></button>`
    else if(relays.length) h+=`<button class="btn btn-d btn-sm" data-iot-act="${dev.id}"><i class="fas fa-power-off"></i></button>`
    relays.forEach((r:any, ri:number) => {
      if(!r.pin) return
      const a = r.function==='free' ? 'toggle' : 'pulse'
      const durDef: Record<string,number> = { panic:1, gate:1, pedestrian:2 }
      const d = r.duration ?? durDef[r.function] ?? undefined
      h+=`<button class="btn btn-s btn-sm" data-iot-relay="${dev.id}" data-relay-id="${ri}" data-relay-action="${a}" data-relay-duration="${d||''}" data-relay-fn="${r.function||'free'}" data-relay-siren="${r.trigger_siren?'1':'0'}" title="${esc(r.label||'Rele '+(ri+1))}"><i class="fas ${r.icon||'fa-plug'}"></i></button>`
    })
    if(isAdmin){
      h+=`<button class="btn btn-s btn-sm" data-iot-rereg="${dev.id}" title="Info conexión"><i class="fas fa-arrows-rotate"></i></button>`
      h+=`<button class="btn btn-s btn-sm" data-iot-token="${dev.id}" title="Regenerar token"><i class="fas fa-key"></i></button>`
      h+=`<button class="btn btn-s btn-sm" data-iot-config="${dev.id}" title="Configurar"><i class="fas fa-gear"></i></button>`
      h+=`<button class="btn btn-o btn-sm" data-iot-zigbee="${dev.id}" title="Zigbee"><i class="fas fa-wave-square"></i></button>`
      h+=`<button class="btn btn-s btn-sm" data-iot-del="${dev.id}"><i class="fas fa-trash"></i></button>`
    }
    h+=`</div></td></tr>`
  })
  h+='</tbody></table>'
  return h
}

async function renderIoT(){
  const g = document.getElementById('iotG')!
  if(!API_MODE){ g.innerHTML=emptyState('Modulo IoT requiere conexion','fa-wifi'); return }
  if(!g.innerHTML.trim()) g.innerHTML = skelGrid(6) + loadingState('Cargando dispositivos...')
  // Admin/superadmin ven todos los dispositivos; rol user solo los de sus grupos
  // (paridad con sistema antiguo). El backend ya autoriza a miembros en group/:gid y command.
  const isIoTAdmin = ['superadmin','admin'].includes(CU?.role)
  let devices: any[] = []
  if(isIoTAdmin){
    const r = await api.getDevices()
    if(!r.ok || !r.data.success){ g.innerHTML=emptyState('Error cargando dispositivos','fa-triangle-exclamation'); return }
    devices = r.data.devices || []
  } else {
    const gids = CU?.group_ids || (CU?.group_id ? [CU.group_id] : [])
    for(const gid of gids){
      const r = await api.getGroupDevices(gid).catch(() => null)
      if(r && r.ok && r.data?.success && r.data.devices) devices = devices.concat(r.data.devices)
    }
  }
  _iotDevicesCache = devices
  if(devices.length) dsCacheSet('devices_' + (CU?.id || 'x'), devices)
  const ico:Record<string,string>={sirena:'fa-bell',portero:'fa-car',puerta:'fa-door-open',camara:'fa-video',sensor:'fa-satellite-dish'}
  const col:Record<string,string>={sirena:'var(--red)',portero:'var(--cyan)',puerta:'var(--green)',camara:'var(--purple)',sensor:'var(--amber)'}
  const lbl:Record<string,string>={sirena:'Activar',portero:'Abrir',puerta:'Abrir'}
  const bcls:Record<string,string>={sirena:'btn-d',portero:'btn-a',puerta:'btn-ok'}
  const apiBase = API_BASE.replace(/\/api\/v1$/, '')
  const envLabels: Record<string,string> = { local:'Local', remote:'Remoto', production:'Producción' }

  if(!devices.length){ g.innerHTML=emptyState('Sin dispositivos','fa-microchip'); return }

  if(_iotView==='table'){
    g.className = 'iot-tabla'
    g.innerHTML = _buildIotTable(devices, ico, col, lbl, bcls, apiBase, envLabels, isIoTAdmin)
  } else {
    g.className = 'ig'
    let h=''
    devices.forEach(function(dev:any){
      const c=col[dev.type]||'var(--fg3)'
      const cfg = dev.config_json ? (typeof dev.config_json === 'string' ? JSON.parse(dev.config_json) : dev.config_json) : {}
      const relays: any[] = cfg.relays || []
      const targetEnv = dev.target_env || cfg.target_env || 'local'
      h+=`<div class="id2" data-iot-device-id="${esc(dev.id)}"><div class="id2-h"><div class="id2-i" style="background:${c}22;color:${c}"><i class="fas ${ico[dev.type]||'fa-microchip'}"></i></div><span class="ds ${dev.status}">${dev.status==='online'?'En linea':'Desconectado'}</span></div>`
      h+=`<div class="id2-n">${esc(dev.name)}</div><div class="id2-l"><i class="fas fa-location-dot"></i> ${esc(dev.loc||'')}</div>`
      if(dev.topic) h+=`<div style="font-size:11px;color:var(--fg3);font-family:'JetBrains Mono',monospace;margin-bottom:10px">${esc(dev.topic)}</div>`
      h+=`<div style="font-size:10px;color:var(--fg3);margin-bottom:2px"><strong>Entorno:</strong> ${envLabels[targetEnv]||targetEnv}</div>`
      if(isIoTAdmin){
        if(dev.gateway_token){
          const wsUrl = apiBase.replace(/^http/, 'ws') + '/ws/iot?token=' + dev.gateway_token
          h+=`<div style="font-size:10px;color:var(--fg3);margin-bottom:6px;word-break:break-all"><strong>Gateway Token:</strong><br><code style="font-size:9px">${esc(dev.gateway_token)}</code></div>`
          h+=`<div style="font-size:10px;color:var(--fg3);margin-bottom:6px;word-break:break-all"><strong>WS:</strong><br><code style="font-size:9px">${esc(wsUrl)}</code></div>`
        } else {
          h+=`<div style="font-size:10px;color:var(--fg3);margin-bottom:6px">Sin token de gateway</div>`
        }
      }
      if(relays.length){
        h+=`<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px">`
        const durDef2: Record<string,number> = { panic:1, gate:1, pedestrian:2 }
        relays.forEach((r:any, ri:number) => {
          if(!r.pin) return
          const a = r.function==='free' ? 'toggle' : 'pulse'
          const d = r.duration ?? durDef2[r.function] ?? undefined
          h+=`<button class="btn btn-s btn-sm" data-iot-relay="${dev.id}" data-relay-id="${ri}" data-relay-action="${a}" data-relay-duration="${d||''}" data-relay-fn="${r.function||'free'}" data-relay-siren="${r.trigger_siren?'1':'0'}" title="${esc(r.label||'Rele '+(ri+1))}"><i class="fas ${r.icon||'fa-plug'}"></i> ${esc(r.label||('R'+(ri+1)))}</button>`
        })
        h+=`</div>`
      }
      h+=`<div class="id2-a">`
      if(lbl[dev.type]) h+=`<button class="btn ${bcls[dev.type]} btn-sm" data-iot-act="${dev.id}"><i class="fas fa-power-off"></i> ${lbl[dev.type]}</button>`
      else if(relays.length) h+=`<button class="btn btn-d btn-sm" data-iot-act="${dev.id}"><i class="fas fa-power-off"></i> Activar</button>`
      else if(dev.type==='camara') h+=`<button class="btn btn-s btn-sm" data-iot-view="${dev.id}"><i class="fas fa-eye"></i> Ver</button>`
      else if(dev.type==='sensor') h+=`<button class="btn btn-s btn-sm" data-iot-read="${dev.id}"><i class="fas fa-wave-square"></i> Leer</button>`
      if(isIoTAdmin){
        h+=`<button class="btn btn-s btn-sm" data-iot-rereg="${dev.id}" title="Re-registrar / info conexion"><i class="fas fa-arrows-rotate"></i></button>`
        h+=`<button class="btn btn-s btn-sm" data-iot-token="${dev.id}" title="Regenerar token"><i class="fas fa-key"></i></button>`
        h+=`<button class="btn btn-s btn-sm" data-iot-config="${dev.id}" title="Configurar"><i class="fas fa-gear"></i></button>`
        h+=`<button class="btn btn-o btn-sm" data-iot-zigbee="${dev.id}" title="Configuracion Zigbee"><i class="fas fa-wave-square"></i></button>`
        h+=`<button class="btn btn-s btn-sm" data-iot-del="${dev.id}"><i class="fas fa-trash"></i></button>`
      }
      h+=`</div></div>`
    })
    g.innerHTML=h
  }
  // Auto-refresh IoT page cada 30s
  if(_iotRefreshTimer) clearInterval(_iotRefreshTimer)
  _iotRefreshTimer = setInterval(() => {
    if(CP === 'iot') renderIoT()
    else if(_iotRefreshTimer){ clearInterval(_iotRefreshTimer); _iotRefreshTimer = null }
  }, 30000)
}

document.getElementById('iotG')!.onclick=async function(e){
  const relayBtn=(e.target as HTMLElement).closest('[data-iot-relay]') as HTMLElement|null
  if(relayBtn){
    const id=relayBtn.dataset.iotRelay!
    const relayId = parseInt(relayBtn.dataset.relayId!)
    const action = relayBtn.dataset.relayAction || 'toggle'
    const duration = relayBtn.dataset.relayDuration ? parseInt(relayBtn.dataset.relayDuration) : undefined
    const params: any = { relay_id: relayId }
    if(duration !== undefined) params.duration = duration
    const r=await api.sendDeviceCommand(id, 'relay_'+action, params)
    if(r.ok && r.data?.sent > 0){
      toast('Comando enviado al rele','ok')
      handleRelayAction(relayBtn, id, relayId, action)
    } else {
      toast(r.data?.error || 'Dispositivo desconectado','wa')
    }
    renderIoT()
    return
  }
  const actBtn=(e.target as HTMLElement).closest('[data-iot-act]') as HTMLElement|null
  if(actBtn){
    const id=actBtn.dataset.iotAct!
    const r=await api.sendDeviceCommand(id, 'activate')
    if(r.ok && r.data?.sent > 0) toast('Comando enviado al dispositivo','ok')
    else toast(r.data?.error || 'Dispositivo desconectado','wa')
    renderIoT()
    return
  }
  const viewBtn=(e.target as HTMLElement).closest('[data-iot-view]') as HTMLElement|null
  if(viewBtn){ toast('Visor camara (simulado)','ok'); return }
  const readBtn=(e.target as HTMLElement).closest('[data-iot-read]') as HTMLElement|null
  if(readBtn){ toast('Sensor sin movimiento','ok'); return }
  const reregBtn=(e.target as HTMLElement).closest('[data-iot-rereg]') as HTMLElement|null
  if(reregBtn){
    const id=reregBtn.dataset.iotRereg!
    go('/iot/' + encodeURIComponent(id))
    return
  }
  const tokenBtn=(e.target as HTMLElement).closest('[data-iot-token]') as HTMLElement|null
  if(tokenBtn){
    const id=tokenBtn.dataset.iotToken!
    if(!confirm('Regenerar token de gateway? El anterior dejara de funcionar.')) return
    const r=await api.regenerateToken(id)
    if(r.ok && r.data?.success){ toast('Token regenerado','ok'); renderIoT() }
    else toast(r.data?.error || 'Error','er')
    return
  }
  const cfgBtn=(e.target as HTMLElement).closest('[data-iot-config]') as HTMLElement|null
  if(cfgBtn){ go('/iot/' + encodeURIComponent(cfgBtn.dataset.iotConfig || '')); return }
  const zbBtn=(e.target as HTMLElement).closest('[data-iot-zigbee]') as HTMLElement|null
  if(zbBtn){
    const id=zbBtn.dataset.iotZigbee!
    const dev=_iotDevicesCache.find((d:any)=>d.id===id)
    if(!dev) return
    document.getElementById('modZigbee')!.classList.add('show')
    await renderZigbeeConfig(dev)
    return
  }
  const delBtn=(e.target as HTMLElement).closest('[data-iot-del]') as HTMLElement|null
  if(delBtn){ const id=delBtn.dataset.iotDel!; await api.deleteDevice(id); toast('Dispositivo eliminado','ok'); renderIoT(); }
}

async function renderZigbeeConfig(dev: any){
  const body=document.getElementById('zbBody')!
  body.innerHTML='<div style="text-align:center;padding:30px"><i class="fas fa-circle-notch fa-spin"></i> Cargando...</div>'

  const wsConnected = dev.status === 'online'

  if(!wsConnected){
    body.innerHTML=`<div class="empty" style="padding:30px"><i class="fas fa-plug-circle-xmark"></i><p>Dispositivo desconectado</p><p style="font-size:12px;color:var(--fg3)">No se puede obtener informacion Zigbee</p></div>`
    return
  }

  try {
    // Intentar obtener lista de dispositivos Zigbee via WS
    // En producción esto se hace via un comando WS
    const cfg = dev.config_json ? (typeof dev.config_json === 'string' ? JSON.parse(dev.config_json) : dev.config_json) : {}
    const zbCfg = cfg.zigbee || {}

    const panId = zbCfg.pan_id || '0x1993'
    const channel = zbCfg.channel || 15
    const deviceCount = zbCfg.device_count || 0
    const devices: any[] = zbCfg.devices || []
    const bindings: any[] = zbCfg.bindings || []

    let h = ''
    // Info de red Zigbee
    h+=`<div class="cc"><div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:13px">`
    h+=`<div><strong>PAN ID:</strong><br><code style="font-size:12px">${panId}</code></div>`
    h+=`<div><strong>Canal:</strong><br>${channel}</div>`
    h+=`<div><strong>Dispositivos:</strong><br>${deviceCount}</div>`
    h+=`<div><strong>Estado:</strong><br><span class="st ${wsConnected?'resolved':'active'}">${wsConnected?'Conectado':'Desconectado'}</span></div>`
    h+=`</div></div>`

    // Acciones
    h+=`<div class="stit mt4"><i class="fas fa-sliders"></i> Acciones</div>`
    h+=`<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:12px">`
    h+=`<button class="btn btn-o btn-sm" id="zbPermitJoin"><i class="fas fa-door-open"></i> Permitir emparejamiento (120s)</button>`
    h+=`<button class="btn btn-s btn-sm" id="zbRefresh"><i class="fas fa-rotate"></i> Refrescar</button>`
    h+=`</div>`

    // Dispositivos Zigbee
    h+=`<div class="stit mt4"><i class="fas fa-microchip"></i> Dispositivos Zigbee</div>`
    if(!devices.length){
      h+=`<div class="empty" style="padding:20px"><i class="fas fa-bluetooth"></i><p>Sin dispositivos Zigbee vinculados</p><p style="font-size:12px;color:var(--fg3)">Activa el emparejamiento y presiona el boton del dispositivo</p></div>`
    } else {
      h+=`<div style="display:flex;flex-direction:column;gap:6px">`
      devices.forEach((d:any,i:number)=>{
        const colors=['#ef4444','#f59e0b','#10b981','#06b6d4','#a855f7','#ec4899']
        const ci=colors[i%colors.length]
        h+=`<div class="ai"><div class="ai-i" style="background:${ci}22;color:${ci}"><i class="fas fa-microchip"></i></div>`
        h+=`<div class="ai-b"><div class="ai-t" style="font-family:monospace;font-size:12px">${d.ieee||d.ieee_addr||'0x0000'}</div>`
        h+=`<div class="ai-d">Short: 0x${(d.short_addr||0).toString(16)} | Endpoints: ${d.endpoints||1} | ${d.online?'<span class="st resolved">En linea</span>':'<span class="st active">Offline</span>'}</div></div>`
        h+=`<div class="ac"><button class="btn btn-s btn-sm zb-bind" data-zb-ieee="${d.ieee||d.ieee_addr||''}" data-zb-ep="${d.endpoint||1}" title="Vincular a usuario"><i class="fas fa-link"></i></button></div></div>`
      })
      h+=`</div>`
    }

    // Bindings
    h+=`<div class="stit mt4"><i class="fas fa-link"></i> Binding: Boton → Usuario</div>`
    if(!bindings.length){
      h+=`<p style="color:var(--fg3);font-size:13px">Sin bindings configurados. Selecciona un dispositivo y vincularlo a un usuario.</p>`
    } else {
      h+=`<div style="display:flex;flex-direction:column;gap:4px;font-size:13px">`
      bindings.forEach((b:any)=>{
        h+=`<div class="ai"><div class="ai-i security"><i class="fas fa-user"></i></div>`
        h+=`<div class="ai-b"><div class="ai-t">${esc(b.label||'Sin etiqueta')}</div>`
        h+=`<div class="ai-d">${b.ieee_addr||b.ieee||''} → ${b.user_id||'?'} (${b.alert_type||'security'})</div></div>`
        h+=`<div class="ac"><button class="btn btn-s btn-sm zb-unbind" data-zb-ieee="${b.ieee_addr||b.ieee||''}" data-zb-ep="${b.endpoint||1}"><i class="fas fa-unlink"></i></button></div></div>`
      })
      h+=`</div>`
    }

    body.innerHTML = h

    // Event handlers
    document.getElementById('zbPermitJoin')?.addEventListener('click', async ()=>{
      await api.sendDeviceCommand(dev.id, 'command', {action:'zigbee_permit_join', duration:120})
      toast('Emparejamiento activado por 120 segundos','ok')
    })
    document.getElementById('zbRefresh')?.addEventListener('click', ()=> renderZigbeeConfig(dev))
    body.querySelectorAll('.zb-bind').forEach(btn=>{
      btn.addEventListener('click', ()=>{
        const ieee = (btn as HTMLElement).dataset.zbIeee || ''
        const ep = (btn as HTMLElement).dataset.zbEp || '1'
        openZigbeeBindModal(dev, ieee, parseInt(ep))
      })
    })
    body.querySelectorAll('.zb-unbind').forEach(btn=>{
      btn.addEventListener('click', async ()=>{
        const ieee = (btn as HTMLElement).dataset.zbIeee || ''
        const ep = (btn as HTMLElement).dataset.zbEp || '1'
        await api.sendDeviceCommand(dev.id, 'command', {action:'zigbee_unbind_button', ieee_addr:ieee, endpoint:parseInt(ep)})
        toast('Binding eliminado','ok')
        renderZigbeeConfig(dev)
      })
    })
  } catch(e){
    body.innerHTML=`<div class="empty" style="padding:30px"><i class="fas fa-triangle-exclamation"></i><p>Error al cargar configuracion Zigbee</p><p style="font-size:12px;color:var(--fg3)">${e}</p></div>`
  }
}

async function openZigbeeBindModal(dev: any, ieee: string, endpoint: number){
  (document.getElementById('zbfDeviceId') as HTMLInputElement).value = dev.id
  ;(document.getElementById('zbfIeee') as HTMLInputElement).value = ieee
  ;(document.getElementById('zbfEndpoint') as HTMLInputElement).value = String(endpoint)
  ;(document.getElementById('zbfIeeeDisplay') as HTMLInputElement).value = ieee
  ;(document.getElementById('zbfEpDisplay') as HTMLInputElement).value = String(endpoint)
  ;(document.getElementById('zbfLabel') as HTMLInputElement).value = ''

  // Cargar usuarios para el select
  const sel = document.getElementById('zbfUser') as HTMLSelectElement
  sel.innerHTML = '<option value="">Cargando...</option>'
  const r = await api.getUsers()
  if(r.ok && r.data?.success){
    sel.innerHTML = '<option value="">Seleccionar usuario...</option>'
    ;(r.data.users || []).forEach((u:any)=>{
      const o = document.createElement('option')
      o.value = u.id
      o.textContent = `${u.name} ${u.lastname} (${u.email})`
      sel.appendChild(o)
    })
  }

  document.getElementById('modZbBind')!.classList.add('show')
}

document.getElementById('frmZbBind')!.onsubmit = async function(e){
  e.preventDefault()
  const deviceId = (document.getElementById('zbfDeviceId') as HTMLInputElement).value
  const ieee = (document.getElementById('zbfIeee') as HTMLInputElement).value
  const endpoint = parseInt((document.getElementById('zbfEndpoint') as HTMLInputElement).value)
  const userId = (document.getElementById('zbfUser') as HTMLSelectElement).value
  const alertType = (document.getElementById('zbfAlertType') as HTMLSelectElement).value
  const label = (document.getElementById('zbfLabel') as HTMLInputElement).value.trim()

  if(!userId){ toast('Selecciona un usuario','er'); return }

  await api.sendDeviceCommand(deviceId, 'command', {
    action: 'zigbee_bind_button',
    ieee_addr: ieee,
    endpoint,
    user_id: userId,
    alert_type: alertType,
    label: label || 'Boton Zigbee'
  })

  toast('Binding creado','ok')
  document.getElementById('modZbBind')!.classList.remove('show')
  // Refrescar la config
  renderZigbeeConfig(_iotDevicesCache.find((d:any)=>d.id===deviceId))
}
document.getElementById('frmDevCfg')!.onsubmit=async function(e){
  e.preventDefault()
  const id = (document.getElementById('dcfId') as HTMLInputElement).value
  const group_id = (document.getElementById('dcfGroup') as HTMLSelectElement).value || null
  // Read 4 relays from DOM
  const rdiv = document.getElementById('dcfRelays')!
  const relays = []
  for(let i = 0; i < 4; i++){
    const label = (rdiv.querySelector(`[data-rl="${i}"]`) as HTMLInputElement)?.value || ''
    const pin = (rdiv.querySelector(`[data-rp="${i}"]`) as HTMLInputElement)?.value || ''
    const func = (rdiv.querySelector(`[data-rf="${i}"]`) as HTMLSelectElement)?.value || 'free'
    const icon = (rdiv.querySelector(`[data-ri="${i}"]`) as HTMLInputElement)?.value || 'fa-plug'
    const dur = (rdiv.querySelector(`[data-rd="${i}"]`) as HTMLInputElement)?.value || ''
    const showDash = (rdiv.querySelector(`[data-rdash="${i}"]`) as HTMLInputElement)?.checked ?? true
    const triggerSiren = (rdiv.querySelector(`[data-rsiren="${i}"]`) as HTMLInputElement)?.checked ?? false
    relays.push({id:i+1, label, pin: pin ? parseInt(pin) : null, icon, function: func, duration: dur ? parseFloat(dur) : undefined, show_dashboard: showDash, trigger_siren: triggerSiren})
  }
  const targetEnv = (document.getElementById('dcfTargetEnv') as HTMLSelectElement).value || 'local'
  const displayName = (document.getElementById('dcfName') as HTMLInputElement).value.trim().slice(0, 40)
  const cfgOut: any = { relays, target_env: targetEnv }
  if(displayName) cfgOut.display_name = displayName
  const payload: any = { group_id, config_json: JSON.stringify(cfgOut) }
  // Keep legacy fields for backward compat
  const panicRelay = relays.find(r => r.function === 'panic')
  if(panicRelay) { payload.relay_pin = panicRelay.pin; payload.panic_relay = 1 }
  const r = await api.updateDevice(id, payload)
  if(r.ok && r.data?.success){ toast('Configuración guardada','ok'); renderIoT(); if(id) renderIotDevicePage(id) }
  else toast(r.data?.error || 'Error','er')
}
async function fillDeviceConfig(dev: any){
  await loadGroupsCache()
  const gSel = document.getElementById('dcfGroup') as HTMLSelectElement
  gSel.innerHTML = '<option value="">Sin grupo</option>'
  _groupsCache.forEach((g:any) => {
    const o = document.createElement('option')
    o.value = g.id; o.textContent = g.name
    if(g.id === dev.group_id) o.selected = true
    gSel.appendChild(o)
  })
  ;(document.getElementById('dcfId') as HTMLInputElement).value = dev.id
  const cfg = dev.config_json ? (typeof dev.config_json === 'string' ? JSON.parse(dev.config_json) : dev.config_json) : {}
  ;(document.getElementById('dcfTargetEnv') as HTMLSelectElement).value = cfg.target_env || 'local'
  const nameEl = document.getElementById('dcfName') as HTMLInputElement
  nameEl.value = cfg.display_name || ''
  nameEl.placeholder = dev.name ? `Ej: ${dev.name}` : 'Ej: Casa, Portón principal…'
  // Render 4 relays
  const relays = (cfg.relays || Array.from({length:4},(_,i)=>({id:i+1,label:'','pin':'','icon':'','function':i===0?'panic':i===1?'gate':i===2?'pedestrian':'free'})))
  const funcLabels = {panic:'Pánico',gate:'Vehícular',pedestrian:'Peatonal',free:'Libre'}
  const defaultDurations: Record<string,number> = {panic:1,gate:1,pedestrian:2}
  const rdiv = document.getElementById('dcfRelays')!
  rdiv.innerHTML = relays.map((r:any,i:number)=>`
    <div class="cc">
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr 100px;gap:6px">
        <div class="fg"><label>Relé ${r.id} - Label</label><input class="fi" data-rl="${i}" value="${esc(r.label||'')}" placeholder="Ej: Sirena"></div>
        <div class="fg"><label>GPIO</label><input class="fi" data-rp="${i}" value="${esc(r.pin||'')}" placeholder="32" type="number"></div>
        <div class="fg"><label>Función</label><select class="fi" data-rf="${i}">
          ${Object.entries(funcLabels).map(([k,v])=>`<option value="${k}" ${r.function===k?'selected':''}>${v}</option>`).join('')}
        </select></div>
        <div class="fg"><label>Icono</label><input class="fi" data-ri="${i}" value="${esc(r.icon||'')}" placeholder="fa-bell"><a class="ico-help" href="https://fontawesome.com/search?m=free&s=solid" target="_blank">Ver iconos FA <i class="fas fa-external-link-alt"></i></a></div>
        <div class="fg"><label>Duración (seg)</label><input class="fi" data-rd="${i}" value="${r.duration??defaultDurations[r.function]??''}" placeholder="${defaultDurations[r.function]??'toggle'}" type="number" min="0" step="0.5"></div>
        <div class="fg" style="display:flex;align-items:center;gap:4px;padding-top:18px"><input type="checkbox" data-rdash="${i}" ${r.show_dashboard!==false?'checked':''} id="rdash-${i}"><label for="rdash-${i}" style="font-size:11px">Dashboard</label></div>
        <div class="fg" style="display:flex;align-items:center;gap:4px;padding-top:18px"><input type="checkbox" data-rsiren="${i}" ${r.trigger_siren?'checked':''} id="rsiren-${i}"><label for="rsiren-${i}" style="font-size:11px;color:var(--red)">Dispara sirena</label></div>
      </div>
    </div>
  `).join('')
}

async function renderIotDevicePage(id: string){
  if(!id){ go('/iot'); return }
  if(!_iotDevicesCache.length){
    const isIoTAdmin = ['superadmin','admin'].includes(CU?.role)
    const devices: any[] = []
    if(isIoTAdmin){
      const r = await api.getDevices().catch(()=>null as any)
      if(r?.ok && r.data?.devices) devices.push(...r.data.devices)
    } else {
      const gids = CU?.group_ids || (CU?.group_id ? [CU.group_id] : [])
      for(const gid of gids){
        const r = await api.getGroupDevices(gid).catch(()=>null as any)
        if(r?.ok && r.data?.devices) devices.push(...r.data.devices)
      }
    }
    devices.forEach((d:any) => { if(!_iotDevicesCache.some((x:any)=>x.id===d.id)) _iotDevicesCache.push(d) })
  }
  const d = _iotDevicesCache.find((x:any)=>x.id===id)
  if(!d){ toast('Dispositivo no encontrado','er'); go('/iot'); return }
  document.getElementById('devTitle')!.textContent = d.name || 'Dispositivo'
  document.getElementById('devSub')!.textContent = `${d.type || 'Gateway'} · ${d.status === 'online' ? 'En línea' : 'Desconectado'}`
  await fillDeviceConfig(d)
  renderReRegister(d)
}

function _openReRegisterModal(dev: any){
  renderReRegister(dev)
}

function renderReRegister(dev: any){
  const body = document.getElementById('rrBody')!
  const cfg = dev.config_json || {}
  const targetEnv = dev.target_env || cfg.target_env || 'local'
  const envLabels: Record<string,string> = { local:'Local (misma red)', remote:'Remoto (dev/staging)', production:'Producción' }
  const apiBase = API_BASE.replace(/\/api\/v1$/, '')

  // Construir URLs según entorno
  const envUrls: Record<string, { api: string, ws: string }> = {
    local:  { api: apiBase,                            ws: apiBase.replace(/^http/, 'ws') },
    remote: { api: import.meta.env.VITE_DEV_API_URL || apiBase, ws: (import.meta.env.VITE_DEV_API_URL || apiBase).replace(/^http/, 'ws') },
    production: { api: import.meta.env.VITE_PROD_API_URL || apiBase, ws: (import.meta.env.VITE_PROD_API_URL || apiBase).replace(/^http/, 'ws') },
  }
  const urls = envUrls[targetEnv] || envUrls.local

  body.innerHTML = `
    <div class="cc">
      <div style="display:flex;flex-direction:column;gap:8px;font-size:13px;">
        <div><strong>Dispositivo:</strong> ${esc(dev.name)}</div>
        <div><strong>Entorno:</strong> ${envLabels[targetEnv]||targetEnv}</div>
        <hr style="border:none;border-top:1px solid var(--bdr2);margin:4px 0">
        <div><strong>API URL:</strong><br><code style="font-size:11px;word-break:break-all">${esc(urls.api)}</code></div>
        <div><strong>WebSocket URL:</strong><br><code style="font-size:11px;word-break:break-all">${esc(urls.ws + '/ws/iot?token=' + dev.gateway_token)}</code></div>
        <div><strong>Gateway Token:</strong><br><code style="font-size:11px;word-break:break-all">${esc(dev.gateway_token)}</code></div>
        <div style="background:var(--bg);border:1px solid var(--bdr2);border-radius:var(--rl);padding:8px;margin-top:4px">
          <div style="font-weight:600;margin-bottom:4px">Endpoint para configurar en el ESP32:</div>
          <code style="font-size:10px;word-break:break-all">${esc(urls.api)}/api/v1/iot/gateway/config?token=${esc(dev.gateway_token)}</code>
        </div>
        <div style="font-size:11px;color:var(--fg3);margin-top:4px">
          Para cambiar el entorno, ve a <strong>Configurar</strong> → <strong>Entorno de conexión</strong>.
          Luego presiona <strong>Reconfigurar</strong> para que el ESP32 aplique los cambios sin reiniciar.
        </div>
        <div id="rrReconfigStatus" style="font-size:12px;margin-top:6px"></div>
      </div>
    </div>
    <div style="display:flex;gap:6px;margin-top:8px">
      <button type="button" class="btn btn-p" id="btnRrReconfig" style="flex:1"><i class="fas fa-arrows-rotate"></i> Reconfigurar ESP32</button>
    </div>`
  // Attach reconfigure handler
  document.getElementById('btnRrReconfig')!.onclick = async function(){
    const btn = this as HTMLButtonElement
    const status = document.getElementById('rrReconfigStatus')!
    btn.disabled = true
    status.textContent = 'Enviando reconfigure...'
    try {
      const r = await api.sendDeviceCommand(dev.id, 'reconfigure')
      if(r.ok && r.data?.sent > 0){
        status.textContent = '✅ Comando enviado. El ESP32 se reconectará al nuevo entorno en segundos.'
      } else {
        status.textContent = '⚠️ El dispositivo parece desconectado. Reintentando vía producción...'
        // Fallback: send via production API directly
        const prodUrl = 'https://domus-secura-api.haliagav.workers.dev'
        const res = await fetch(`${prodUrl}/api/v1/iot/devices/${dev.id}/command`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ action: 'reconfigure' })
        })
        const data = await res.json()
        if(data?.sent > 0) status.textContent = '✅ Comando enviado vía producción. El ESP32 se reconectará al nuevo entorno.'
        else status.textContent = '⚠️ No se pudo contactar al dispositivo. Probá reiniciarlo físicamente.'
      }
    } catch(e: any) {
      status.textContent = 'Error: ' + (e?.message || 'desconocido')
    }
    btn.disabled = false
  }
}

const savedView = localStorage.getItem('iotView') as 'cards'|'table'|null
if(savedView) _iotView = savedView
document.getElementById('iotViewCards')!.onclick=function(){ _setIotView('cards'); renderIoT() }
document.getElementById('iotViewTable')!.onclick=function(){ _setIotView('table'); renderIoT() }
_setIotView(_iotView)

document.getElementById('btnNewDev')!.onclick=function(){ (document.getElementById('frmDev') as HTMLFormElement).reset(); document.getElementById('modDev')!.classList.add('show'); }
document.getElementById('frmDev')!.onsubmit=async function(e){
  e.preventDefault()
  const payload={
    name:(document.getElementById('dfN') as HTMLInputElement).value.trim(),
    type:(document.getElementById('dfT') as HTMLSelectElement).value,
    loc:(document.getElementById('dfLoc') as HTMLInputElement).value.trim(),
    topic:(document.getElementById('dfTop') as HTMLInputElement).value.trim()
  }
  if(API_MODE){
    const r=await api.createDevice(payload)
    if(!r.ok || !r.data.success){ toast(r.data?.error || 'Error','er'); return }
  } else {
    const d=db()!; d.devices.push({id:gid(),...payload,status:'online'}); dbSave(d)
  }
  document.getElementById('modDev')!.classList.remove('show'); toast('Dispositivo agregado','ok'); renderIoT()
}


// ===== GROUPS =====
let _groupsCache: any[] = []
async function loadGroupsCache() {
  if(!API_MODE) return []
  const grpCacheKey = 'groups_' + (CU?.id || 'x')
  const cached = dsCacheGet(grpCacheKey)
  // Si el cache esta fresco, se evita la red; si hay datos aunque sea viejos, se dejan
  // pintados mientras se revalida en segundo plano
  if(cached?.data && dsCacheFresh(cached)){ _groupsCache = cached.data; return _groupsCache }
  if(cached?.data?.length && !_groupsCache.length) _groupsCache = cached.data
  const r = await api.getGroups()
  if(r.ok && r.data?.success){ _groupsCache = r.data.groups || []; dsCacheSet(grpCacheKey, _groupsCache) }
  return _groupsCache
}

function fillGroupSelect(sel: HTMLSelectElement, includeNone = true){
  const current = sel.value
  sel.innerHTML = includeNone ? '<option value="">Sin grupo</option>' : ''
  _groupsCache.forEach((g: any) => {
    if(g.active === 0 && CU.role !== 'superadmin') return
    const o = document.createElement('option')
    o.value = g.id
    o.textContent = g.name + (g.active === 0 ? ' (inactivo)' : '')
    sel.appendChild(o)
  })
  if (current) sel.value = current
}

async function renderGroups(){
  if(!API_MODE){ document.getElementById('gTbody')!.innerHTML = '<div class="empty" style="padding:40px"><i class="fas fa-people-group"></i><p>Requiere conexion</p></div>'; return }
  if(!_groupsCache.length && !dsCacheGet('groups_' + (CU?.id || 'x'))?.data?.length)
    document.getElementById('gTbody')!.innerHTML = skelGrid(6) + loadingState('Cargando grupos...')
  await loadGroupsCache()
  let planMap: Record<string,string> = {}
  if(CU.role === 'superadmin'){
    const pr = await api.getPlans()
    if(pr.ok && pr.data?.success){ (pr.data.plans || []).forEach((p:any)=>{ planMap[p.id] = p.name }) }
  }
  const ur = await api.getUsers()
  const userMap: Record<string,string> = {}
  if(ur.ok && ur.data?.success){ (ur.data.users || []).forEach((u:any)=>{ userMap[u.id] = u.name + ' ' + u.lastname }) }
  const colors=['#ef4444','#f59e0b','#10b981','#06b6d4','#a855f7','#ec4899','#f97316']

  const filter = ((document.getElementById('gSearch') as HTMLInputElement)?.value || '').toLowerCase()
  const filtered = filter ? _groupsCache.filter((g:any) => g.name.toLowerCase().includes(filter)) : _groupsCache
  if(!filtered.length){ document.getElementById('gTbody')!.innerHTML = '<div class="empty" style="padding:40px"><i class="fas fa-people-group"></i><p>Sin grupos</p></div>'; return }

  let cards = '', rows = ''
  filtered.forEach((g: any, i: number) => {
    const c = colors[i%colors.length]
    const ini = g.name.split(' ').slice(0,2).map((w:string)=>w[0]||'').join('').toUpperCase()
    const planName = esc(planMap[g.plan_id] || '—')
    const adminName = esc(userMap[g.admin_user_id] || '—')
    const memberCount = g.member_count||0
    const statusHtml = `<span class="st ${g.active?'resolved':'active'}">${g.active?'Activo':'Inactivo'}</span>`
    const actionsHtml = `<button class="ab ed" data-gedit="${g.id}" title="Editar"><i class="fas fa-pen"></i></button>
      <button class="ab" data-gmembers="${g.id}" title="Miembros"><i class="fas fa-users"></i></button>
      <button class="ab" data-gemergency="${g.id}" title="Numeros de emergencia"><i class="fas fa-phone"></i></button>
      ${memberCount === 0 ? `<button class="ab" data-gdel="${g.id}" title="Eliminar"><i class="fas fa-trash"></i></button>` : ''}`

    cards += `<div class="gc-card">
      <div class="gc-card-head">
        <div class="ua" style="background:${c}">${ini}</div>
        <div style="flex:1;min-width:0">
          <div class="un">${esc(g.name)}</div>
          <div class="ue">${esc(g.description||'')}</div>
        </div>
      </div>
      <div class="gc-card-body">
        <div><div class="gc-label">Plan</div><div>${planName}</div></div>
        <div><div class="gc-label">Admin</div><div>${adminName}</div></div>
        <div><div class="gc-label">Miembros</div><div><span class="rb user">${memberCount}</span></div></div>
        <div><div class="gc-label">Estado</div><div>${statusHtml}</div></div>
      </div>
      <div class="gc-card-actions">${actionsHtml}</div>
    </div>`

    rows += `<tr>
      <td data-label="Grupo"><div class="uc"><div class="ua" style="background:${c}">${ini}</div><div><div class="un">${esc(g.name)}</div><div class="ue">${esc(g.description||'')}</div></div></div></td>
      <td data-label="Plan">${planName}</td>
      <td data-label="Admin">${adminName}</td>
      <td data-label="Miembros"><span class="rb user">${memberCount}</span></td>
      <td data-label="Estado">${statusHtml}</td>
      <td data-label="Acciones"><div class="ac">${actionsHtml}</div></td>
    </tr>`
  })

  document.getElementById('gTbody')!.innerHTML = `
    <div class="groups-card-grid">${cards}</div>
    <div class="groups-table-wrap"><table><thead><tr>
      <th>Grupo</th><th>Plan</th><th>Admin</th><th>Miembros</th><th>Estado</th><th>Acciones</th>
    </tr></thead><tbody>${rows}</tbody></table></div>`
}

document.getElementById('gTbody')!.onclick = async (e) => {
  const btn = (e.target as HTMLElement).closest('.ab') as HTMLElement|null
  if(!btn) return
  if(btn.dataset.gedit) await openGroupMod(btn.dataset.gedit)
  else if(btn.dataset.gdel) await delGroup(btn.dataset.gdel)
  else if(btn.dataset.gmembers) await openGroupMembers(btn.dataset.gmembers)
  else if(btn.dataset.gemergency) await openEmergencyNumbers(btn.dataset.gemergency)
}

document.getElementById('gSearch')?.addEventListener('input', () => renderGroups())
document.getElementById('btnNewGroup')!.onclick = () => openGroupMod()

async function fillPlanSelect(){
  const sel = document.getElementById('gfPlan') as HTMLSelectElement
  sel.innerHTML = '<option value="">Sin plan</option>'
  const r = await api.getPlans()
  if(r.ok && r.data?.success){
    (r.data.plans || []).forEach((p: any) => {
      const o = document.createElement('option')
      o.value = p.id; o.textContent = p.name
      sel.appendChild(o)
    })
  }
}

function openGroupMod(gid?: string){
  go(gid ? `/groups/${encodeURIComponent(gid)}` : '/groups/new')
}

async function renderGroupPage(gid?: string){
  const f = document.getElementById('frmGroup') as HTMLFormElement
  f.reset()
  ;(document.getElementById('gfId') as HTMLInputElement).value = ''
  document.getElementById('mgTitle')!.textContent = 'Nuevo grupo'
  await fillPlanSelect()
  if(gid){
    const r = await api.getGroup(gid)
    if(r.ok && r.data?.success){
      const g = r.data.group
      ;(document.getElementById('gfId') as HTMLInputElement).value = g.id
      ;(document.getElementById('gfN') as HTMLInputElement).value = g.name
      ;(document.getElementById('gfD') as HTMLInputElement).value = g.description || ''
      ;(document.getElementById('gfPlan') as HTMLSelectElement).value = g.plan_id || ''
      ;(document.getElementById('gfAct') as HTMLSelectElement).value = String(g.active ?? 1)
      document.getElementById('mgTitle')!.textContent = 'Editar grupo'
    }
  } else {
    ;(document.getElementById('gfAct') as HTMLSelectElement).value = '1'
  }
  document.getElementById('gfSubmitLabel')!.textContent = gid ? 'Guardar cambios' : 'Crear grupo'
}

document.getElementById('frmGroup')!.onsubmit = async (e) => {
  e.preventDefault()
  const id = (document.getElementById('gfId') as HTMLInputElement).value
  const payload: any = {
    name: (document.getElementById('gfN') as HTMLInputElement).value.trim(),
    description: (document.getElementById('gfD') as HTMLInputElement).value.trim(),
    plan_id: (document.getElementById('gfPlan') as HTMLSelectElement).value || null,
    active: parseInt((document.getElementById('gfAct') as HTMLSelectElement).value)
  }
  clearFormErrors(document.getElementById('frmGroup') as HTMLFormElement | null)
  if(!payload.name){ fieldMessage('gfN','Ingresa el nombre del grupo'); return }
  const r = id ? await api.updateGroup(id, payload) : await api.createGroup(payload)
  if(r.ok && r.data?.success){
    toast(id ? 'Grupo actualizado' : 'Grupo creado', 'ok')
    go('/groups')
  } else {
    toast(r.data?.error || 'Error', 'er')
  }
}

async function delGroup(id: string){
  if(!confirm('Eliminar este grupo? Si tiene miembros sera desactivado.')) return
  const r = await api.deleteGroup(id)
  if(r.ok && r.data?.success){
    if(r.data.softDeleted) toast('Grupo desactivado (tiene miembros)','wa')
    else toast('Grupo eliminado','ok')
    renderGroups()
  } else {
    toast(r.data?.error || 'Error', 'er')
  }
}

// ===== EMPRESAS (alias de Grupos via API /api/v1/groups) =====
async function renderEmpresas(){
  if(!API_MODE){ document.getElementById('eTbody')!.innerHTML = '<div class="empty" style="padding:40px"><i class="fas fa-building"></i><p>Requiere conexion</p></div>'; return }
  await loadGroupsCache()
  let planMap: Record<string,string> = {}
  if(CU.role === 'superadmin'){
    const pr = await api.getPlans()
    if(pr.ok && pr.data?.success){ (pr.data.plans || []).forEach((p:any)=>{ planMap[p.id] = p.name }) }
  }
  const ur = await api.getUsers()
  const userMap: Record<string,string> = {}
  if(ur.ok && ur.data?.success){ (ur.data.users || []).forEach((u:any)=>{ userMap[u.id] = u.name + ' ' + u.lastname }) }
  const colors=['#ef4444','#f59e0b','#10b981','#06b6d4','#a855f7','#ec4899','#f97316']

  const filter = ((document.getElementById('eSearch') as HTMLInputElement)?.value || '').toLowerCase()
  const filtered = filter ? _groupsCache.filter((g:any) => g.name.toLowerCase().includes(filter)) : _groupsCache
  if(!filtered.length){ document.getElementById('eTbody')!.innerHTML = '<div class="empty" style="padding:40px"><i class="fas fa-building"></i><p>Sin empresas</p></div>'; return }

  let cards = '', rows = ''
  filtered.forEach((g: any, i: number) => {
    const c = colors[i%colors.length]
    const ini = g.name.split(' ').slice(0,2).map((w:string)=>w[0]||'').join('').toUpperCase()
    const planName = esc(planMap[g.plan_id] || '—')
    const adminName = esc(userMap[g.admin_user_id] || '—')
    const memberCount = g.member_count||0
    const statusHtml = `<span class="st ${g.active?'resolved':'active'}">${g.active?'Activo':'Inactivo'}</span>`
    const actionsHtml = `<button class="ab ed" data-eedit="${g.id}" title="Editar"><i class="fas fa-pen"></i></button>
      <button class="ab" data-emembers="${g.id}" title="Miembros"><i class="fas fa-users"></i></button>
      ${memberCount === 0 ? `<button class="ab" data-edel="${g.id}" title="Eliminar"><i class="fas fa-trash"></i></button>` : ''}`

    cards += `<div class="gc-card">
      <div class="gc-card-head">
        <div class="ua" style="background:${c}">${ini}</div>
        <div style="flex:1;min-width:0">
          <div class="un">${esc(g.name)}</div>
          <div class="ue">${esc(g.description||'')}</div>
        </div>
      </div>
      <div class="gc-card-body">
        <div><div class="gc-label">Plan</div><div>${planName}</div></div>
        <div><div class="gc-label">Admin</div><div>${adminName}</div></div>
        <div><div class="gc-label">Miembros</div><div><span class="rb user">${memberCount}</span></div></div>
        <div><div class="gc-label">Estado</div><div>${statusHtml}</div></div>
      </div>
      <div class="gc-card-actions">${actionsHtml}</div>
    </div>`

    rows += `<tr>
      <td data-label="Empresa"><div class="uc"><div class="ua" style="background:${c}">${ini}</div><div><div class="un">${esc(g.name)}</div><div class="ue">${esc(g.description||'')}</div></div></div></td>
      <td data-label="Plan">${planName}</td>
      <td data-label="Admin">${adminName}</td>
      <td data-label="Miembros"><span class="rb user">${memberCount}</span></td>
      <td data-label="Estado">${statusHtml}</td>
      <td data-label="Acciones"><div class="ac">${actionsHtml}</div></td>
    </tr>`
  })

  document.getElementById('eTbody')!.innerHTML = `
    <div class="groups-card-grid">${cards}</div>
    <div class="groups-table-wrap"><table><thead><tr>
      <th>Empresa</th><th>Plan</th><th>Admin</th><th>Miembros</th><th>Estado</th><th>Acciones</th>
    </tr></thead><tbody>${rows}</tbody></table></div>`
}

// Event handlers for empresas page
document.getElementById('eTbody')!.onclick = async (e) => {
  const btn = (e.target as HTMLElement).closest('.ab') as HTMLElement|null
  if(!btn) return
  if(btn.dataset.eedit) await openGroupMod(btn.dataset.eedit)
  else if(btn.dataset.edel) await delGroup(btn.dataset.edel)
  else if(btn.dataset.emembers) await openGroupMembers(btn.dataset.emembers)
}
document.getElementById('eSearch')?.addEventListener('input', () => renderEmpresas())
document.getElementById('btnNewEmpresa')!.onclick = () => openGroupMod()

// Group members management
let _mgmCurrentId: string | null = null
let _mgmCurrentName: string = ''

function openGroupMembers(gid: string){
  go('/groups/' + encodeURIComponent(gid) + '/miembros')
}

async function renderGroupMembersPage(gid: string){
  _mgmCurrentId = gid
  const g = _groupsCache.find((x:any)=>x.id===gid)
  _mgmCurrentName = g?.name || ''
  document.getElementById('mgmTitle')!.textContent = 'Miembros: ' + _mgmCurrentName
  ;(document.getElementById('mgmEmail') as HTMLInputElement).value = ''
  await renderMgmList()
  await renderMgmExtList()
  // Load existing invite state
  await loadInviteState(gid)
}

async function loadInviteState(gid: string){
  const r = await api.getGroupInvite(gid)
  const revokeBtn = document.getElementById('btnRevokeInvite')!
  const resultDiv = document.getElementById('inviteResult')!
  const roleBadge = document.getElementById('inviteRoleBadge')!
  if(r.ok && r.data?.success && r.data.invite_token){
    const link = `${location.origin}/register?invite=${r.data.invite_token}`
    ;(document.getElementById('inviteLink') as HTMLInputElement).value = link
    resultDiv.style.display = 'block'
    revokeBtn.style.display = ''
    const roleLabel = r.data.invite_role === 'admin' ? 'Admin del grupo' : 'Miembro'
    roleBadge.innerHTML = `<span class="rb ${r.data.invite_role}">${roleLabel}</span>${r.data.inviter_name ? ' por ' + esc(r.data.inviter_name) : ''}`
    const qrDiv = document.getElementById('inviteQr')!
    qrDiv.innerHTML = `<img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(link)}" alt="QR de invitacion" style="border-radius:8px">`
    // Sync selector to match existing invite role
    const sel = document.getElementById('inviteRoleSel') as HTMLSelectElement
    if(sel) sel.value = r.data.invite_role || 'user'
  } else {
    resultDiv.style.display = 'none'
    revokeBtn.style.display = 'none'
    roleBadge.innerHTML = ''
  }
}

// Emergency numbers management
let _enCurrentGroupId: string | null = null

function openEmergencyNumbers(gid: string, edit?: any){
  go('/emergency/' + encodeURIComponent(gid) + '/numeros' + (edit?.id ? '?edit=' + encodeURIComponent(edit.id) : ''))
}

async function renderEmergencyNumbersPage(gid: string){
  if(!gid){ go('/emergency'); return }
  _enCurrentGroupId = gid
  _enPageGroupId = gid
  const g = _groupsCache.find((x:any)=>x.id===gid)
  document.getElementById('menTitle')!.textContent = 'Emergencia: ' + (g?.name || '')
  ;(document.getElementById('enfGroupId') as HTMLInputElement).value = gid
  ;(document.getElementById('enfId') as HTMLInputElement).value = ''
  ;(document.getElementById('frmEmergencyNumber') as HTMLFormElement).reset()
  ;(document.getElementById('enfGroupId') as HTMLInputElement).value = gid
  // Edición por URL: /emergency/:gid/numeros?edit=<id>
  const editId = new URLSearchParams(location.search).get('edit')
  if(editId){
    const r = await api.getEmergencyNumbers(gid)
    const item = (r.ok && r.data?.numbers || []).find((n:any)=>n.id===editId)
    if(item){
      ;(document.getElementById('enfId') as HTMLInputElement).value = item.id
      ;(document.getElementById('enfName') as HTMLInputElement).value = item.name
      ;(document.getElementById('enfPhone') as HTMLInputElement).value = item.phone
      ;(document.getElementById('enfIcon') as HTMLSelectElement).value = item.icon || 'fa-phone'
      ;(document.getElementById('enfColor') as HTMLInputElement).value = item.color || '#ef4444'
    }
  }
  document.getElementById('enfSubmitLabel')!.textContent = editId ? 'Guardar cambios' : 'Agregar número'
  await renderEmergencyNumbersList()
}

async function renderEmergencyNumbersList(){
  if(!_enCurrentGroupId) return
  const cont = document.getElementById('menList')!
  const r = await api.getEmergencyNumbers(_enCurrentGroupId)
  if(!r.ok || !r.data?.success){ cont.innerHTML = '<div style="color:var(--fg3);font-size:13px">Error cargando</div>'; return }
  const list: any[] = r.data.numbers || []
  if(!list.length){
    cont.innerHTML = '<div style="color:var(--fg3);font-size:13px;text-align:center;padding:20px">Sin numeros configurados. Agrega Carabineros (133), Bomberos (132), etc.</div>'
    return
  }
  let h = '<table><thead><tr><th></th><th>Nombre</th><th>Telefono</th><th></th></tr></thead><tbody>'
  list.forEach((n: any) => {
    h += `<tr><td style="width:30px"><i class="fas ${n.icon||'fa-phone'}" style="color:${n.color||'#ef4444'};font-size:16px"></i></td><td>${esc(n.name)}</td><td>${esc(n.phone)}</td>`
    h += `<td style="white-space:nowrap"><button class="ab ed" data-en-edit='${JSON.stringify({id:n.id,name:n.name,phone:n.phone,icon:n.icon,color:n.color})}' title="Editar"><i class="fas fa-pen"></i></button><button class="ab" data-en-del="${n.id}" title="Eliminar"><i class="fas fa-trash"></i></button></td></tr>`
  })
  h += '</tbody></table>'
  cont.innerHTML = h
}

document.getElementById('frmEmergencyNumber')!.onsubmit = async (e) => {
  e.preventDefault()
  const id = (document.getElementById('enfId') as HTMLInputElement).value
  const groupId = (document.getElementById('enfGroupId') as HTMLInputElement).value
  const name = (document.getElementById('enfName') as HTMLInputElement).value.trim()
  const phone = (document.getElementById('enfPhone') as HTMLInputElement).value.trim()
  const icon = (document.getElementById('enfIcon') as HTMLSelectElement).value
  const color = (document.getElementById('enfColor') as HTMLInputElement).value
  clearFormErrors(document.getElementById('frmEmergencyNumber') as HTMLFormElement | null)
  let invalid = false
  if(!name){ fieldMessage('enfName','Ingresa el nombre'); invalid = true }
  if(!phone){ fieldMessage('enfPhone','Ingresa el teléfono'); invalid = true }
  if(invalid) return
  const payload = { name, phone, icon, color, group_id: groupId }
  const r = id ? await api.updateEmergencyNumber(id, payload) : await api.createEmergencyNumber(payload)
  if(r.ok && r.data?.success){
    toast(id ? 'Numero actualizado' : 'Numero agregado','ok')
    ;(document.getElementById('enfId') as HTMLInputElement).value = ''
    ;(document.getElementById('frmEmergencyNumber') as HTMLFormElement).reset()
    ;(document.getElementById('enfGroupId') as HTMLInputElement).value = _enCurrentGroupId || ''
    if(new URLSearchParams(location.search).get('edit')) history.replaceState({page:'emergency-numbers'}, '', '/emergency/' + (_enCurrentGroupId || '') + '/numeros')
    renderEmergencyNumbersList()
    if(CP === 'emergency') renderEnPageList()
  } else {
    toast(r.data?.error || 'Error','er')
  }
}

document.getElementById('menList')!.onclick = async (e) => {
  const target = e.target as HTMLElement
  const editBtn = target.closest('[data-en-edit]') as HTMLElement|null
  const delBtn = target.closest('[data-en-del]') as HTMLElement|null
  if(editBtn){
    try {
      const data = JSON.parse(editBtn.dataset.enEdit!)
      ;(document.getElementById('enfId') as HTMLInputElement).value = data.id
      ;(document.getElementById('enfName') as HTMLInputElement).value = data.name
      ;(document.getElementById('enfPhone') as HTMLInputElement).value = data.phone
      ;(document.getElementById('enfIcon') as HTMLSelectElement).value = data.icon || 'fa-phone'
      ;(document.getElementById('enfColor') as HTMLInputElement).value = data.color || '#ef4444'
    } catch{}
    return
  }
  if(delBtn){
    const id = delBtn.dataset.enDel!
    if(!confirm('Eliminar este numero de emergencia?')) return
    const r = await api.deleteEmergencyNumber(id)
    if(r.ok && r.data?.success){
      toast('Numero eliminado','ok')
      renderEmergencyNumbersList()
      if(CP === 'emergency') renderEnPageList()
    }
    else toast(r.data?.error || 'Error','er')
  }
}

// ===== Emergency numbers page (mantenedor) =====
let _enPageGroupId: string | null = null

async function renderEmergencyPage(){
  if(CU.role !== 'admin' && CU.role !== 'superadmin'){ nav('dashboard'); return }
  if(!_groupsCache.length) await loadGroupsCache()
  const manageable = CU.role === 'superadmin'
    ? _groupsCache
    : _groupsCache.filter((g:any)=>g.admin_user_id === CU.id)
  const sel = document.getElementById('enGroupSel') as HTMLSelectElement
  const prev = sel.value
  sel.innerHTML = manageable.map((g:any)=>`<option value="${g.id}">${esc(g.name)}</option>`).join('')
  if(!manageable.length){
    _enPageGroupId = null
    document.getElementById('enPageList')!.innerHTML = '<div style="color:var(--fg3);font-size:13px;text-align:center;padding:20px">No tienes grupos a tu cargo</div>'
    return
  }
  _enPageGroupId = (prev && manageable.some((g:any)=>g.id===prev)) ? prev : manageable[0].id
  sel.value = _enPageGroupId as string
  sel.onchange = () => { _enPageGroupId = sel.value; renderEnPageList() }
  await renderEnPageList()
}

async function renderEnPageList(){
  const cont = document.getElementById('enPageList')!
  if(!_enPageGroupId){ cont.innerHTML = ''; return }
  const r = await api.getEmergencyNumbers(_enPageGroupId)
  if(!r.ok || !r.data?.success){ cont.innerHTML = '<div style="color:var(--fg3);font-size:13px">Error cargando</div>'; return }
  const list: any[] = r.data.numbers || []
  if(!list.length){
    cont.innerHTML = '<div style="color:var(--fg3);font-size:13px;text-align:center;padding:20px">Sin numeros configurados. Agrega Carabineros (133), Bomberos (132), etc.</div>'
    return
  }
  let h = '<table><thead><tr><th></th><th>Nombre</th><th>Telefono</th><th></th></tr></thead><tbody>'
  list.forEach((n:any)=>{
    h += `<tr><td style="width:30px"><i class="fas ${n.icon||'fa-phone'}" style="color:${n.color||'#ef4444'};font-size:16px"></i></td><td>${esc(n.name)}</td><td>${esc(n.phone)}</td>`
    h += `<td style="white-space:nowrap"><button class="ab ed" data-enp-edit='${JSON.stringify({id:n.id,name:n.name,phone:n.phone,icon:n.icon,color:n.color})}' title="Editar"><i class="fas fa-pen"></i></button><button class="ab" data-enp-del="${n.id}" title="Eliminar"><i class="fas fa-trash"></i></button></td></tr>`
  })
  h += '</tbody></table>'
  cont.innerHTML = h
}

document.getElementById('btnNewEmergencyNumber')!.onclick = () => {
  if(_enPageGroupId) openEmergencyNumbers(_enPageGroupId)
}

document.getElementById('enPageList')!.onclick = async (e) => {
  const target = e.target as HTMLElement
  const editBtn = target.closest('[data-enp-edit]') as HTMLElement|null
  const delBtn = target.closest('[data-enp-del]') as HTMLElement|null
  if(editBtn){
    try { openEmergencyNumbers(_enPageGroupId!, JSON.parse(editBtn.dataset.enpEdit!)) } catch{}
    return
  }
  if(delBtn){
    if(!confirm('Eliminar este numero de emergencia?')) return
    const r = await api.deleteEmergencyNumber(delBtn.dataset.enpDel!)
    if(r.ok && r.data?.success){ toast('Numero eliminado','ok'); renderEnPageList() }
    else toast(r.data?.error || 'Error','er')
  }
}

async function renderMgmList(){
  if(!_mgmCurrentId) return
  const r = await api.getGroup(_mgmCurrentId)
  const cont = document.getElementById('mgmList')!
  if(!r.ok || !r.data?.success){ cont.innerHTML = '<div class="empty"><i class="fas fa-triangle-exclamation"></i><p>Error cargando miembros</p></div>'; return }
  const members: any[] = r.data.members || []
  if(!members.length){
    cont.innerHTML = '<div class="empty"><i class="fas fa-user-slash"></i><p>Sin miembros asignados</p></div>'
    return
  }
  const colors=['#ef4444','#f59e0b','#10b981','#06b6d4','#a855f7','#ec4899','#f97316']
  let h = '<table><thead><tr><th>Usuario</th><th>Rol</th><th>Plan</th><th>Estado</th><th></th></tr></thead><tbody>'
  members.forEach((u:any, i:number)=>{
    const c = colors[i%colors.length]
    const ini = (u.name[0] + u.lastname[0]).toUpperCase()
    h += `<tr><td><div class="uc"><div class="ua" style="background:${c}">${ini}</div><div><div class="un">${esc(u.name+' '+u.lastname)}</div><div class="ue">${esc(u.email)}</div></div></div></td>`
    h += `<td><span class="rb ${u.role}">${u.role}</span></td>`
    const planLabels: Record<string,string> = { free:'Gratis', familiar:'Familiar', grupos:'Condominio' }
    h += `<td><span class="pb2 ${u.plan}">${planLabels[u.plan]||u.plan}</span></td>`
    h += `<td><span class="st ${u.active?'resolved':'active'}">${u.active?'Activo':'Inactivo'}</span></td>`
    h += `<td><button class="ab" data-mgm-rm="${u.id}" title="Quitar del grupo"><i class="fas fa-user-minus"></i></button></td></tr>`
  })
  h += '</tbody></table>'
  cont.innerHTML = h
}

async function renderMgmExtList(){
  if(!_mgmCurrentId) return
  const cont = document.getElementById('mgmExtList')!
  const r = await api.getExtContacts(_mgmCurrentId)
  if(!r.ok || !r.data?.success){ cont.innerHTML = '<div style="color:var(--fg3);font-size:13px">Error cargando</div>'; return }
  const list: any[] = r.data.contacts || []
  if(!list.length){
    cont.innerHTML = '<div style="color:var(--fg3);font-size:13px">Sin contactos externos invitados</div>'
    return
  }
  let h = '<table><thead><tr><th>Nombre</th><th>Telefono</th><th></th></tr></thead><tbody>'
  list.forEach((c: any) => {
    h += `<tr><td>${esc(c.name)}</td><td>${esc(c.phone)}</td>`
    h += `<td><button class="ab" data-mgm-extrm="${c.id}" title="Quitar"><i class="fas fa-user-minus"></i></button></td></tr>`
  })
  h += '</tbody></table>'
  cont.innerHTML = h
}

document.getElementById('mgmList')!.onclick = async (e) => {
  const btn = (e.target as HTMLElement).closest('[data-mgm-rm]') as HTMLElement|null
  if(!btn || !_mgmCurrentId) return
  const userId = btn.dataset.mgmRm!
  if(!confirm('Quitar este usuario del grupo?')) return
  const r = await api.removeGroupMember(_mgmCurrentId, userId)
  if(r.ok && r.data?.success){ toast('Usuario quitado del grupo','ok'); renderMgmList(); renderGroups() }
  else toast(r.data?.error || 'Error','er')
}

document.getElementById('mgmExtList')!.onclick = async (e) => {
  const btn = (e.target as HTMLElement).closest('[data-mgm-extrm]') as HTMLElement|null
  if(!btn || !_mgmCurrentId) return
  const cid = btn.dataset.mgmExtrm!
  if(!confirm('Quitar este contacto externo del grupo?')) return
  const r = await api.deleteExtContact(cid)
  if(r.ok && r.data?.success){ toast('Contacto quitado','ok'); renderMgmExtList(); renderGroups() }
  else toast(r.data?.error || 'Error','er')
}

document.getElementById('btnMgmAssign')!.onclick = async () => {
  if(!_mgmCurrentId) return
  const email = (document.getElementById('mgmEmail') as HTMLInputElement).value.trim().toLowerCase()
  if(!email){ toast('Email requerido','er'); return }
  const ur = await api.getUsers()
  if(!ur.ok || !ur.data?.success){ toast('Error cargando usuarios','er'); return }
  const u = (ur.data.users || []).find((x:any) => x.email === email)
  if(!u){ toast('Usuario no encontrado','er'); return }
  const r = await api.addGroupMember(_mgmCurrentId, u.id)
  if(r.ok && r.data?.success){
    toast('Usuario asignado','ok')
    ;(document.getElementById('mgmEmail') as HTMLInputElement).value = ''
    renderMgmList(); renderGroups()
  } else {
    toast(r.data?.error || 'Error','er')
  }
}

document.getElementById('btnMgmAddExt')!.onclick = async () => {
  if(!_mgmCurrentId) return
  const name = (document.getElementById('mgmExtName') as HTMLInputElement).value.trim()
  const phone = (document.getElementById('mgmExtPhone') as HTMLInputElement).value.trim()
  clearFormErrors(document.getElementById('frmEmergencyNumber') as HTMLFormElement | null)
  let invalid = false
  if(!name){ fieldMessage('enfName','Ingresa el nombre'); invalid = true }
  if(!phone){ fieldMessage('enfPhone','Ingresa el teléfono'); invalid = true }
  if(invalid) return
  const r = await api.createExtContact({ name, phone, group_id: _mgmCurrentId })
  if(r.ok && r.data?.success){
    toast('Contacto externo invitado','ok')
    ;(document.getElementById('mgmExtName') as HTMLInputElement).value = ''
    ;(document.getElementById('mgmExtPhone') as HTMLInputElement).value = ''
    renderMgmExtList()
  } else {
    toast(r.data?.error || 'Error','er')
  }
}

// Invite generation
document.getElementById('btnGenInvite')!.onclick = async () => {
  if(!_mgmCurrentId) return
  const roleSel = document.getElementById('inviteRoleSel') as HTMLSelectElement
  const inviteRole = roleSel?.value || 'user'
  const inviterName = `${CU.name} ${CU.lastname}`.trim()
  const r = await api.generateGroupInvite(_mgmCurrentId, inviteRole, inviterName)
  if(r.ok && r.data?.success){
    const token = r.data.invite_token
    const link = `${location.origin}/register?invite=${token}`
    const inp = document.getElementById('inviteLink') as HTMLInputElement
    inp.value = link
    document.getElementById('inviteResult')!.style.display = 'block'
    document.getElementById('btnRevokeInvite')!.style.display = ''
    const roleLabel = r.data.invite_role === 'admin' ? 'Admin del grupo' : 'Miembro'
    document.getElementById('inviteRoleBadge')!.innerHTML = `<span class="rb ${r.data.invite_role}">${roleLabel}</span> por ${esc(inviterName)}`
    toast('Invitacion generada','ok')
    // Generate QR via QR Server API
    const qrDiv = document.getElementById('inviteQr')!
    qrDiv.innerHTML = `<img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(link)}" alt="QR de invitacion" style="border-radius:8px">`
  } else {
    toast(r.data?.error || 'Error al generar invitacion','er')
  }
}

document.getElementById('btnRevokeInvite')!.onclick = async () => {
  if(!_mgmCurrentId) return
  if(!confirm('Revocar invitacion? Los links existentes dejaran de funcionar.')) return
  const r = await api.revokeGroupInvite(_mgmCurrentId)
  if(r.ok && r.data?.success){
    document.getElementById('inviteResult')!.style.display = 'none'
    document.getElementById('btnRevokeInvite')!.style.display = 'none'
    document.getElementById('inviteRoleBadge')!.innerHTML = ''
    toast('Invitacion revocada','ok')
  } else {
    toast(r.data?.error || 'Error','er')
  }
}

document.getElementById('btnCopyInvite')!.onclick = async () => {
  const inp = document.getElementById('inviteLink') as HTMLInputElement
  try {
    await navigator.clipboard.writeText(inp.value)
    toast('Link copiado','ok')
  } catch {
    inp.select()
    document.execCommand('copy')
    toast('Link copiado','ok')
  }
}

// WhatsApp share
document.getElementById('btnWhatsAppInvite')!.onclick = () => {
  const inp = document.getElementById('inviteLink') as HTMLInputElement
  const link = inp.value
  if(!link) return
  const roleBadge = document.getElementById('inviteRoleBadge')
  const roleText = roleBadge?.textContent?.trim() || 'Miembro'
  const groupName = _mgmCurrentName
  const text = `Te invito a unirte a "${groupName}" como ${roleText} en Domus Secura:\n${link}`
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
}

// ===== PROFILE =====

let _originalPhone = ''
let _phoneVerified = false

async function renderProfile(){
  if(API_MODE && !_groupsCache.length) await loadGroupsCache()
  document.getElementById('profHead')!.innerHTML=`<div class="pav">${((CU.name?.[0]||'')+(CU.lastname?.[0]||'')).toUpperCase()}</div>
    <div><h2>${esc(CU.name+' '+CU.lastname)}</h2><p>${esc(CU.email)} — ${esc(CU.phone||'')}</p>
    <div class="pbdg"><span class="rb ${CU.role}">${CU.role}</span><span class="pb2 ${CU.plan}">${CU.plan}</span>${CU.group?`<span class="pb2" style="background:rgba(6,182,212,.15);color:var(--cyan)">${esc(CU.group)}</span>`:''}</div></div>`
  ;(document.getElementById('pName') as HTMLInputElement).value=CU.name
  ;(document.getElementById('pLast') as HTMLInputElement).value=CU.lastname
  _originalPhone = CU.phone||''
  ;(document.getElementById('pPhone') as HTMLInputElement).value=_originalPhone
  ;(document.getElementById('pPass') as HTMLInputElement).value=''
  _phoneVerified = false
  document.getElementById('btnVerifyPhone')!.style.display = 'none'
  document.getElementById('otpSection')!.style.display = 'none'
  // Default group selector
  const defSel = document.getElementById('pDefGroup') as HTMLSelectElement
  defSel.innerHTML = '<option value="">Sin grupo</option>'
  const gids = CU?.group_ids || (CU?.group_id ? [CU.group_id] : [])
  _groupsCache.forEach((g:any) => {
    if(gids.includes(g.id)){
      const o = document.createElement('option')
      o.value = g.id; o.textContent = g.name
      if(g.id === CU.default_group_id) o.selected = true
      defSel.appendChild(o)
    }
  })
}

document.getElementById('pPhone')!.addEventListener('input', function(this: HTMLInputElement){
  const v = this.value.trim()
  const btn = document.getElementById('btnVerifyPhone')!
  if(v !== _originalPhone && !_phoneVerified) {
    btn.style.display = 'inline-flex'
  } else {
    btn.style.display = 'none'
  }
})

document.getElementById('btnVerifyPhone')!.onclick = function(){
  document.getElementById('otpSection')!.style.display = 'block'
  document.getElementById('btnSendOtp')!.style.display = 'inline-flex'
  document.getElementById('btnConfirmOtp')!.style.display = 'none'
  document.getElementById('otpMsg')!.style.display = 'none'
}

document.getElementById('btnSendOtp')!.onclick = async function(){
  const btn = this as HTMLButtonElement
  btn.disabled = true
  btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Enviando...'
  document.getElementById('otpMsg')!.style.display = 'none'
  const r = await api.sendOtp()
  btn.disabled = false
  btn.innerHTML = '<i class="fas fa-paper-plane"></i> Enviar codigo'
  const msg = document.getElementById('otpMsg')!
  if(r.ok && r.data?.success){
    msg.style.color = 'var(--green)'
    msg.textContent = r.data.message || 'Codigo enviado'
    msg.style.display = 'block'
    document.getElementById('btnConfirmOtp')!.style.display = 'inline-flex'
    ;(document.getElementById('pOtp') as HTMLInputElement).value = ''
  } else {
    msg.style.color = 'var(--red)'
    msg.textContent = r.data?.error || 'Error al enviar codigo'
    msg.style.display = 'block'
  }
}

document.getElementById('btnConfirmOtp')!.onclick = async function(){
  const code = (document.getElementById('pOtp') as HTMLInputElement).value.trim()
  if(!code || code.length !== 6) { toast('Ingrese el codigo de 6 digitos','er'); return }
  const newPhone = (document.getElementById('pPhone') as HTMLInputElement).value.trim()
  const btn = this as HTMLButtonElement
  btn.disabled = true
  btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
  const r = await api.verifyOtp(code, newPhone)
  btn.disabled = false
  btn.innerHTML = '<i class="fas fa-check"></i> Confirmar'
  if(r.ok && r.data?.success){
    _phoneVerified = true
    document.getElementById('btnVerifyPhone')!.style.display = 'none'
    document.getElementById('otpMsg')!.style.display = 'none'
    ;(document.getElementById('pOtp') as HTMLInputElement).value = ''
    ;(document.getElementById('btnSendOtp') as HTMLButtonElement).style.display = 'none'
    ;(document.getElementById('btnConfirmOtp') as HTMLButtonElement).style.display = 'none'
    const me = await api.me()
    if(me.ok && me.data?.user){ CU = { ...CU, ...me.data.user } }
    _originalPhone = CU.phone||''
    renderProfile()
    updateSbUser()
    toast('Telefono verificado y actualizado','ok')
  } else {
    toast(r.data?.error || 'Codigo invalido','er')
  }
}

document.getElementById('frmProf')!.onsubmit=async (e)=>{
  e.preventDefault()
  const newPhone = (document.getElementById('pPhone') as HTMLInputElement).value.trim()
  if(newPhone !== _originalPhone && !_phoneVerified){
    toast('Verifique el nuevo telefono primero','wa')
    return
  }
  const payload: any = {
    name: (document.getElementById('pName') as HTMLInputElement).value.trim(),
    lastname: (document.getElementById('pLast') as HTMLInputElement).value.trim(),
    default_group_id: (document.getElementById('pDefGroup') as HTMLSelectElement).value || null,
  }
  const np = (document.getElementById('pPass') as HTMLInputElement).value
  if (np) payload.password = np
  if(API_MODE){
    const r = await api.updateUser(CU.id, payload)
    if(!r.ok || !r.data?.success){ toast(r.data?.error || 'Error al actualizar perfil','er'); return }
    const me = await api.me()
    if(me.ok && me.data?.user){ CU = { ...CU, ...me.data.user } }
    toast('Perfil actualizado','ok')
    renderProfile(); updateSbUser()
  } else {
    const d=db()!; const u=d.users.find((x:any)=>x.id===CU.id)!
    u.name=payload.name; u.lastname=payload.lastname; u.phone=newPhone
    if(np) u.password=np
    dbSave(d); CU=u; localStorage.setItem('sp_user', JSON.stringify(CU)); updateSbUser(); toast('Perfil actualizado','ok'); renderProfile()
  }
}
document.getElementById('btnCloseSes')!.onclick=async ()=>{
  if(!confirm('Cerrar todas las sesiones en otros dispositivos?')) return
  if(API_MODE){
    const r = await api.logoutAll()
    if(r.ok && r.data?.success){ toast('Sesiones cerradas','ok') }
    else toast(r.data?.error || 'Error', 'er')
  } else {
    toast('Sesiones cerradas (simulado)','ok')
  }
}
document.getElementById('btnDelAcc')!.onclick=async function(){
  if(!confirm('Eliminar tu cuenta permanentemente? Se borraran todos tus datos.')) return
  if(API_MODE){
    const r=await api.deleteAccount()
    if(r.ok && r.data.success){ toast('Cuenta eliminada','ok'); logout() }
    else toast(r.data?.error || 'Error', 'er')
  } else { toast('Eliminar cuenta requiere conexion','wa') }
}

// ===== AUTH FORMS =====
document.getElementById('frmLogin')!.onsubmit=async (e)=>{
  e.preventDefault(); const email=(document.getElementById('lEmail') as HTMLInputElement).value.trim().toLowerCase()
  const pass=(document.getElementById('lPass') as HTMLInputElement).value
  const frmLogin = document.getElementById('frmLogin') as HTMLFormElement | null
  clearFormErrors(frmLogin)
  let invalid = false
  if(!email){ fieldMessage('lEmail','Ingresa tu correo'); invalid = true }
  else if(!EMAIL_RE.test(email)){ fieldMessage('lEmail','Ingresa un correo válido'); invalid = true }
  if(!pass){ fieldMessage('lPass','Ingresa tu contraseña'); invalid = true }
  if(invalid){ (document.getElementById(invalid ? 'lEmail' : 'lPass') as HTMLInputElement | null)?.focus(); return }
  if(API_MODE){
    const turnstileWidget = tsWidgets['turnstileLogin']
    const turnstileToken = getTurnstileToken('turnstileLogin')
    if(turnstileWidget && !turnstileToken && !tsFailed['turnstileLogin']){
      (document.getElementById('btnLogin') as HTMLButtonElement).disabled = true
      turnstile.execute(turnstileWidget)
      return
    }
    const r=await api.login(email,pass,turnstileToken)
    // Token de un solo uso: se limpia para que reintentos generen uno fresco
    delete tsTokens['turnstileLogin']
    try { if(tsWidgets['turnstileLogin']) turnstile.reset(tsWidgets['turnstileLogin']) } catch {}
    if(r.ok&&r.data&&r.data.success){
      CU=r.data.user; setToken(r.data.token); localStorage.setItem('sp_refresh',r.data.refreshToken); localStorage.setItem('sp_user', JSON.stringify(r.data.user))
      if(r.data.force_password_change || r.data.user?.force_password_change){
        showScr('forceReset'); return
      }
      enterApp()
    } else if(r.status === 401) {
      // La API responde 'Invalid credentials' (en inglés) + intentos restantes
      const rem = r.data?.remaining
      fieldMessage('lPass', 'Correo o contraseña incorrectos' + (typeof rem === 'number' && rem >= 0 && rem <= 3 ? ` · te quedan ${rem} intento${rem === 1 ? '' : 's'}` : ''))
      ;(document.getElementById('lPass') as HTMLInputElement).select()
    } else if(r.status === 0) {
      toast('Sin conexión con el servidor. Revisa tu red e intenta de nuevo.','er')
    } else {
      toast(r.data?.error||'No se pudo iniciar sesión','er')
    }
  } else {
    const d=db()!; const user=d.users.find((u:any)=>u.email===email&&u.password===pass&&u.active)
    if(!user){toast('Credenciales incorrectas','er');return}
    CU=user; localStorage.setItem('sp_token_local','local_'+user.id); localStorage.setItem('sp_user', JSON.stringify(user)); enterApp()
  }
}

// OTP send/verify button for registration
document.getElementById('btnRegSendOtp')!.onclick = async function(){
  const btn = this as HTMLButtonElement
  const isVerify = btn.dataset.mode === 'verify'
  const msg = document.getElementById('rOtpMsg')!

  if(!isVerify){
    const phone = (document.getElementById('rPhone') as HTMLInputElement).value.trim()
    const email = (document.getElementById('rEmail') as HTMLInputElement).value.trim().toLowerCase()
    if(!phone || !email){ toast('Ingresa correo y telefono primero','er'); return }
    // El widget invisible solo genera token tras execute(): si no hay, se ejecuta y
    // el callback re-dispara este boton con el token fresco (igual que login).
    const turnstileWidget = tsWidgets['turnstileReg']
    const turnstileToken = getTurnstileToken('turnstileReg')
    if(turnstileWidget && !turnstileToken && !tsFailed['turnstileReg']){
      btn.disabled = true
      turnstile.execute(turnstileWidget)
      return
    }
    btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
    const r = await api.registerSendOtp(phone, email, turnstileToken)
    // Token de un solo uso: se limpia para que reintentos generen uno fresco
    delete tsTokens['turnstileReg']
    try { if(tsWidgets['turnstileReg']) turnstile.reset(tsWidgets['turnstileReg']) } catch {}
    btn.disabled = false
    if(r.ok && r.data?.success){
      if(r.data.code){ console.log('[REG-OTP] Codigo:', r.data.code); msg.textContent = 'Codigo: ' + r.data.code }
      else { msg.textContent = r.data.message || 'Codigo enviado al telefono' }
      btn.innerHTML = '<i class="fas fa-check-circle"></i> Validar'
      btn.dataset.mode = 'verify'
      msg.style.color = 'var(--green)'
      msg.style.display = 'block'
    } else {
      btn.innerHTML = '<i class="fas fa-paper-plane"></i> Enviar'
      msg.style.color = 'var(--red)'
      msg.textContent = r.data?.error || 'Error al enviar codigo'
      msg.style.display = 'block'
    }
  } else {
    const code = (document.getElementById('rOtp') as HTMLInputElement).value.trim()
    const phone = (document.getElementById('rPhone') as HTMLInputElement).value.trim()
    const email = (document.getElementById('rEmail') as HTMLInputElement).value.trim().toLowerCase()
    if(!code || code.length !== 6){ toast('Ingresa el codigo de 6 digitos','er'); return }
    btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
    const r = await api.registerVerifyOtp(email, code, phone)
    btn.disabled = false
    if(r.ok && r.data?.success){
      _regPhoneVerified = true
      btn.innerHTML = '<i class="fas fa-check"></i> Verificado'
      btn.disabled = true
      msg.style.color = 'var(--green)'
      msg.textContent = 'Telefono verificado correctamente'
      ;(document.getElementById('rOtp') as HTMLInputElement).readOnly = true
    } else {
      btn.innerHTML = '<i class="fas fa-check-circle"></i> Validar'
      msg.style.color = 'var(--red)'
      msg.textContent = r.data?.error || 'Codigo invalido'
      msg.style.display = 'block'
    }
  }
}

// Plan highlight on change
document.getElementById('rPlan')!.addEventListener('change', updatePlanHighlight)

// Phone input shows OTP section when filled
document.getElementById('rPhone')!.addEventListener('input', function(){
  const phone = (this as HTMLInputElement).value.trim()
  document.getElementById('rOtpSection')!.style.display = phone.length >= 8 ? 'block' : 'none'
})

document.getElementById('frmReg')!.onsubmit=async (e)=>{
  e.preventDefault()
  const email=(document.getElementById('rEmail') as HTMLInputElement).value.trim().toLowerCase()
  const pass=(document.getElementById('rPass') as HTMLInputElement).value
  const name=(document.getElementById('rName') as HTMLInputElement).value.trim()
  const phone=(document.getElementById('rPhone') as HTMLInputElement).value.trim()
  if(!email||!pass||!name){toast('Completa los campos obligatorios','er');return}
  if(pass.length<6){toast('Minimo 6 caracteres','er');return}
  if(!phone){toast('Telefono es obligatorio','er');return}
  if(API_MODE){
    if(!_regPhoneVerified){ toast('Debes verificar tu telefono con el codigo OTP primero','er'); return }
    const turnstileWidget = tsWidgets['turnstileReg']
    const turnstileToken = getTurnstileToken('turnstileReg')
    if(turnstileWidget && !turnstileToken && !tsFailed['turnstileReg']){
      (document.getElementById('btnReg') as HTMLButtonElement).disabled = true
      turnstile.execute(turnstileWidget)
      return
    }
    const inviteToken = sessionStorage.getItem('invite_token')

    const payload: any={
      name,lastname:(document.getElementById('rLast') as HTMLInputElement).value.trim(),email,
      phone, password:pass, otp_code: (document.getElementById('rOtp') as HTMLInputElement).value.trim(),
      plan:(document.getElementById('rPlan') as HTMLSelectElement).value,
      consent:(document.getElementById('rConsent') as HTMLInputElement).checked,
      turnstile_token: turnstileToken
    }
    if(inviteToken) payload.invite_token = inviteToken
    const r=await api.register(payload)
    if(r.ok&&r.data&&r.data.success){
      delete tsTokens['turnstileReg']
      try { if(tsWidgets['turnstileReg']) turnstile.reset(tsWidgets['turnstileReg']) } catch {}
      sessionStorage.removeItem('invite_token')
      const paidPlan = payload.plan
      const isPaid = paidPlan && paidPlan !== 'free' && paidPlan !== 'gratis'
      if(isPaid) r.data.user.plan = 'free'
      CU=r.data.user; setToken(r.data.token); localStorage.setItem('sp_refresh',r.data.refreshToken); localStorage.setItem('sp_user', JSON.stringify(r.data.user)); toast('Cuenta creada', 'ok'); enterApp()
      if(isPaid){
        const subRes = await api.subscribeToPlan(paidPlan, String(window.location.origin || '') + '/billing')
        if(subRes.ok && subRes.data?.init_point){
          window.location.href = subRes.data.init_point
        } else if(subRes.ok && subRes.data?.payment_link){
          window.location.href = subRes.data.payment_link
        } else {
          toast(subRes.data?.error || 'Error al iniciar suscripcion. Ve a Planes para intentar de nuevo', 'er')
        }
      }
    } else {
      toast(r.data?.error||'Error al registrar','er')
    }
  } else {
    const d=db()!; if(d.users.some((u:any)=>u.email===email)){toast('Correo ya registrado','er');return}
    const nu={id:gid(),name,lastname:(document.getElementById('rLast') as HTMLInputElement).value.trim(),email,phone,password:pass,role:'user',plan:(document.getElementById('rPlan') as HTMLSelectElement).value,
      consent:(document.getElementById('rConsent') as HTMLInputElement).checked,active:true,created:new Date().toISOString().split('T')[0]}
    d.users.push(nu); dbSave(d); CU=nu; localStorage.setItem('sp_token_local', 'local_'+nu.id); localStorage.setItem('sp_user', JSON.stringify(nu)); toast('Cuenta creada', 'ok'); enterApp()
  }
}

document.getElementById('goRegister')!.onclick=()=>showScr('reg')
document.getElementById('goLogin')!.onclick=()=>showScr('login')
document.getElementById('goLandingLogin')!.onclick=()=>showLanding()
document.getElementById('goLandingReg')!.onclick=()=>showLanding()
document.getElementById('goForgot')!.onclick=()=>showScr('forgot')
document.getElementById('goBackLoginForgot')!.onclick=()=>showScr('login')
document.getElementById('goBackLoginReset')!.onclick=()=>showScr('login')

// Forgot password form
let _forgotEmail = ''
document.getElementById('frmForgot')!.onsubmit=async (e)=>{
  e.preventDefault()
  const email=(document.getElementById('fEmail') as HTMLInputElement).value.trim().toLowerCase()
  clearFormErrors(document.getElementById('frmForgot') as HTMLFormElement | null)
  if(!email){ fieldMessage('fEmail','Ingresa tu correo'); return }
  if(!EMAIL_RE.test(email)){ fieldMessage('fEmail','Ingresa un correo válido'); return }
  const msg=document.getElementById('fMsg')!
  const btn=document.getElementById('btnForgotSend') as HTMLButtonElement
  // El widget invisible solo genera token tras execute(): si no hay, se ejecuta y
  // el callback re-dispara este submit con el token fresco (igual que login).
  const turnstileWidget = tsWidgets['turnstileForgot']
  const turnstileToken = getTurnstileToken('turnstileForgot')
  if(turnstileWidget && !turnstileToken && !tsFailed['turnstileForgot']){
    btn.disabled=true
    turnstile.execute(turnstileWidget)
    return
  }
  btn.disabled=true; btn.innerHTML='<i class="fas fa-spinner fa-spin"></i>'
  const r=await api.forgotPasswordSendOtp(email, turnstileToken)
  // Token de un solo uso: se limpia para que reintentos generen uno fresco
  delete tsTokens['turnstileForgot']
  try { if(tsWidgets['turnstileForgot']) turnstile.reset(tsWidgets['turnstileForgot']) } catch {}
  btn.disabled=false
  if(r.ok && r.data?.success){
    if(r.data.dev_code){ console.log('[FORGOT-OTP] Codigo:', r.data.dev_code); msg.textContent='Codigo: '+r.data.dev_code; msg.style.color='var(--green)'; msg.style.display='block'; setTimeout(() => showScr('reset'), 3000) }
    else { msg.textContent='Si el usuario esta registrado, recibiras un codigo en tu WhatsApp.'; msg.style.color='var(--green)'; msg.style.display='block'; setTimeout(() => showScr('reset'), 3000) }
    _forgotEmail=email
    try { sessionStorage.setItem('sp_forgot_email', email) } catch {}
  } else {
    btn.innerHTML='<i class="fas fa-paper-plane"></i> Enviar Codigo'
    msg.style.color='var(--red)'; msg.textContent=r.data?.error||'Error al enviar codigo'; msg.style.display='block'
  }
}

// Reset password form
document.getElementById('frmReset')!.onsubmit=async (e)=>{
  e.preventDefault()
  const code=(document.getElementById('rCode') as HTMLInputElement).value.trim()
  const pass=(document.getElementById('rNewPass') as HTMLInputElement).value
  const pass2=(document.getElementById('rNewPass2') as HTMLInputElement).value
  clearFormErrors(document.getElementById('frmReset') as HTMLFormElement | null)
  if(!code||code.length!==6){ fieldMessage('rCode','Ingresa el código de 6 dígitos'); return }
  if(!pass||pass.length<6){ fieldMessage('rNewPass','Mínimo 6 caracteres'); return }
  if(pass!==pass2){ fieldMessage('rNewPass2','Las contraseñas no coinciden'); return }
  const msg=document.getElementById('rMsg')!
  const btn=document.getElementById('btnResetPass') as HTMLButtonElement
  btn.disabled=true; btn.innerHTML='<i class="fas fa-spinner fa-spin"></i>'
  const r=await api.forgotPasswordReset(_forgotEmail, code, pass)
  btn.disabled=false; btn.innerHTML='<i class="fas fa-check"></i> Cambiar Contrasena'
  if(r.ok && r.data?.success){
    msg.style.color='var(--green)'; msg.textContent='Contrasena actualizada! Redirigiendo...'; msg.style.display='block'
    try { sessionStorage.removeItem('sp_forgot_email') } catch {}
    setTimeout(()=>showScr('login'), 2000)
  } else {
    msg.style.color='var(--red)'; msg.textContent=r.data?.error||'Error al cambiar contrasena'; msg.style.display='block'
  }
}

// Password visibility toggle
document.getElementById('lPassToggle')!.onclick=function(){
  const el=document.getElementById('lPass') as HTMLInputElement
  const icon=(this as HTMLElement).querySelector('i')!
  if(el.type==='password'){ el.type='text'; icon.classList.replace('fa-eye','fa-eye-slash') }
  else { el.type='password'; icon.classList.replace('fa-eye-slash','fa-eye') }
}
// Force reset password form
document.getElementById('frmForceReset')!.onsubmit=async (e)=>{
  e.preventDefault()
  const pass=(document.getElementById('frNewPass') as HTMLInputElement).value
  const pass2=(document.getElementById('frNewPass2') as HTMLInputElement).value
  const tokenEl=document.getElementById('frToken') as HTMLInputElement
  let token=tokenEl.value
  if(!token){
    const params=new URLSearchParams(window.location.search)
    token=params.get('token')||''
    if(!token){ toast('Token no encontrado','er'); return }
  }
  clearFormErrors(document.getElementById('frmForceReset') as HTMLFormElement | null)
  if(!pass||pass.length<6){ fieldMessage('frNewPass','Mínimo 6 caracteres'); return }
  if(pass!==pass2){ fieldMessage('frNewPass2','Las contraseñas no coinciden'); return }
  const msg=document.getElementById('frMsg')!
  const btn=document.getElementById('btnForceReset') as HTMLButtonElement
  btn.disabled=true; btn.innerHTML='<i class="fas fa-spinner fa-spin"></i>'
  const r=await api.forceReset(token, pass)
  btn.disabled=false; btn.innerHTML='<i class="fas fa-check"></i> Establecer Contrasena'
  if(r.ok && r.data?.success){
    msg.style.color='var(--green)'; msg.textContent='Contrasena establecida! Redirigiendo...'; msg.style.display='block'
    const email=CU?.email||''
    CU=null; clearToken(); localStorage.removeItem('sp_user')
    setTimeout(()=>{ showScr('login'); toast('Ya puedes iniciar sesion con tu nueva contrasena','ok') }, 2000)
  } else {
    msg.style.color='var(--red)'; msg.textContent=r.data?.error||'Error'; msg.style.display='block'
  }
}

document.getElementById('btnMenu')!.onclick=toggleSb
// Mismo patron mousedown+mouseup para sbOv (consistencia con .mo)
const sbOv = document.getElementById('sbOv')!
let sbDown = false
sbOv.addEventListener('mousedown', (e)=>{ sbDown = (e.target === sbOv) })
sbOv.addEventListener('mouseup', (e)=>{ if(sbDown && e.target === sbOv){ toggleSb() } sbDown = false })
document.getElementById('sbUser')!.onclick=()=>nav('profile')
document.getElementById('btnAlerts')!.onclick=()=>nav('alerts')
document.getElementById('btnLogout')!.onclick=logout

// ===== MODALS =====
document.querySelectorAll('[data-close]').forEach((el)=>{
  (el as HTMLElement).onclick=function(){ const closeId = (el as HTMLElement).dataset.close!; document.getElementById(closeId)!.classList.remove('show') }
})
// Click-outside-to-close: solo cierra si el GESTO completo empezo Y termino sobre el overlay.
// Asi no se cierra si el usuario hace mousedown dentro de un input y suelta fuera (p. ej. arrastrando 
// texto o haciendo click fuera por accidente).
document.querySelectorAll('.mo').forEach((ov)=>{
  let downOnOverlay = false
  ;(ov as HTMLElement).addEventListener('mousedown', (e)=>{
    downOnOverlay = (e.target === ov)
  })
  ;(ov as HTMLElement).addEventListener('mouseup', (e)=>{
    if(downOnOverlay && e.target === ov){ (ov as HTMLElement).classList.remove('show') }
    downOnOverlay = false
  })
})


// ===== LEGAL / PRIVACY =====
import { TERMS_HTML, PRIVACY_HTML } from './lib/legal'


async function renderLegalPage(doc: string) {
  const titleEl = document.getElementById('legalTitle')!
  const bodyEl = document.getElementById('legalBody')!
  const backBtn = document.getElementById('legalBackBtn') as HTMLAnchorElement

  if (doc === 'terms') {
    titleEl.textContent = 'Términos y Condiciones'
    bodyEl.innerHTML = TERMS_HTML
    backBtn.href = '/'
    backBtn.dataset.back = 'landing'
  } else if (doc === 'privacy') {
    titleEl.textContent = 'Política de Privacidad'
    bodyEl.innerHTML = PRIVACY_HTML
    backBtn.href = '/'
    backBtn.dataset.back = 'landing'
  } else if (doc === 'alerts') {
    titleEl.textContent = 'Alertas Activas'
    const r = await api.getAlerts('?limit=50&status=active')
    if (r.ok && r.data?.alerts) {
      bodyEl.innerHTML = r.data.alerts.map((a: any) => alertHtml(a, a.name && a.lastname ? a.name + ' ' + a.lastname : '')).join('')
    } else {
      bodyEl.innerHTML = '<p style="color:var(--fg3)">Sin alertas activas</p>'
    }
    backBtn.href = '/alerts'
    backBtn.dataset.back = 'alerts'
  }
}

async function renderLegalQuotePage(quoteId: string) {
  const r = await api.getQuote(quoteId)
  if (!r.ok || !r.data?.success) {
    toast('Error al cargar cotización', 'er')
    go('/quotes-admin')
    return
  }
  const q = r.data.quote
  let html = `<div style="padding:12px"><h3 style="margin:0 0 12px">Cotización: ${esc(q.contact_name)}</h3>`
  html += `<div style="margin-bottom:12px;font-size:13px"><div><strong>Email:</strong> ${esc(q.contact_email)}</div>`
  if (q.contact_phone) html += `<div><strong>Teléfono:</strong> ${esc(q.contact_phone)}</div>`
  html += `<div><strong>Estado:</strong> <span style="color:${statusColors[q.status] || 'var(--fg3)'}">${statusLabels[q.status] || q.status}</span></div>`
  html += `<div><strong>Total:</strong> ${fmtPrice(q.total_amount)} ${esc(q.currency)}</div>`
  html += `<div><strong>Fecha:</strong> ${q.created_at ? q.created_at.slice(0, 10) : '—'}</div></div>`

  if (q.items && q.items.length) {
    html += '<div style="background:var(--surface-sunken);border:1px solid var(--border-subtle);border-radius:var(--rl);padding:12px"><div style="font-weight:600;margin-bottom:8px;font-size:13px">Productos:</div>'
    q.items.forEach((li: any) => {
      html += `<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:13px">
        <span>${esc(li.product_name)} x${li.quantity}</span>
        <strong>${fmtPrice(li.total_price)} CLP</strong>
      </div>`
    })
    html += '</div>'
  }

  if (q.notes) html += `<div style="margin-top:12px;font-size:13px"><strong>Notas:</strong><br>${esc(q.notes)}</div>`
  html += '</div>'

  const titleEl = document.getElementById('legalTitle')!
  const bodyEl = document.getElementById('legalBody')!
  const backBtn = document.getElementById('legalBackBtn') as HTMLAnchorElement
  titleEl.textContent = 'Detalle de Cotización'
  bodyEl.innerHTML = html
  backBtn.href = '/quotes-admin'
  backBtn.dataset.back = 'quotes-admin'
}

// ===== LANDING EVENTS =====
function bindLandingEvents(){
  // Nav scroll shadow
  const nav = document.getElementById('NAV')
  if(nav) window.addEventListener('scroll', ()=>{
    if(window.scrollY > 50) nav.classList.add('scrolled')
    else nav.classList.remove('scrolled')
  }, { passive: true })

  // Hamburger menu toggle
  const hamburger = document.getElementById('hamburger')
  const navLinks = document.getElementById('navLinks')
  if(hamburger && navLinks){
    hamburger.addEventListener('click', ()=>{
      const o = navLinks.classList.toggle('open')
      hamburger.querySelector('i')!.className = o ? 'fas fa-xmark' : 'fas fa-bars'
    })
    navLinks.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', ()=>{
        navLinks.classList.remove('open')
        hamburger.querySelector('i')!.className = 'fas fa-bars'
      })
    })
  }

  // Reveal animations (IntersectionObserver)
  const rvEls = document.querySelectorAll('#landing .rv')
  let obsSupported = false
  if('IntersectionObserver' in window){
    try {
      const rvObs = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if(entry.isIntersecting){
            entry.target.classList.remove('rv-hide')
            entry.target.classList.add('rv-show')
            rvObs.unobserve(entry.target)
          }
        })
      }, { threshold: .08, rootMargin: '0px 0px -30px 0px' })
      rvEls.forEach(el => { el.classList.add('rv-hide'); rvObs.observe(el) })
      obsSupported = true
    } catch(e){}
  }
  if(!obsSupported){
    setTimeout(() => rvEls.forEach(el => { el.classList.remove('rv-hide'); el.classList.add('rv-show') }), 100)
  }

  // FAQ accordion
  document.querySelectorAll('#landing .faq-q').forEach(q => {
    q.addEventListener('click', () => {
      const item = q.parentElement!
      const wasOpen = item.classList.contains('open')
      document.querySelectorAll('#landing .faq-item').forEach(fi => fi.classList.remove('open'))
      if(!wasOpen) item.classList.add('open')
    })
  })

  // Smooth scroll for nav links (skip data-action elements)
  document.querySelectorAll('#landing a[href^="#"]').forEach(a => {
    if((a as HTMLElement).getAttribute('data-action')) return
    a.addEventListener('click', (e) => {
      const href = (a as HTMLAnchorElement).getAttribute('href')
      if(href && href.length > 1){
        const target = document.querySelector(href)
        if(target){ e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
      }
    })
  })

  // Hero visual parallax
  const heroVisual = document.querySelector('#landing .hero-visual') as HTMLElement
  if(heroVisual && !window.matchMedia('(prefers-reduced-motion: reduce)').matches){
    window.addEventListener('scroll', ()=>{
      if(window.scrollY < 800) heroVisual.style.transform = 'translateY(' + (window.scrollY * 0.06) + 'px)'
    }, { passive: true })
  }

  // Wire up data-action buttons (event delegation for dynamic elements like plan cards)
  document.getElementById('landing')!.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null
    if(!el) return
    const action = el.getAttribute('data-action')
    if(action === 'register') { e.preventDefault(); showScr('reg', el.getAttribute('data-plan') || undefined) }
    else if(action === 'login') { e.preventDefault(); showScr('login') }
    else if(action === 'quote') {
      e.preventDefault()
      const planId = el.getAttribute('data-plan') || ''
      const planName = el.getAttribute('data-plan-name') || ''
      openQuoteModal(planId, planName)
    }
  })

  // Landing footer legal links
  document.getElementById('lFooterTerms')?.addEventListener('click', (e) => { e.preventDefault(); go('/legal/terms') })
  document.getElementById('lFooterPrivacy')?.addEventListener('click', (e) => { e.preventDefault(); go('/legal/privacy') })

  // CTA button — handled by data-action delegation above
}

// ===== INIT =====
bindLandingEvents()
initDB()
;(async () => {
const st = localStorage.getItem('sp_token') || sessionStorage.getItem('sp_token') || localStorage.getItem('sp_token_local') || sessionStorage.getItem('sp_token_local')
// Migrate old sessionStorage tokens to localStorage
if(st && !localStorage.getItem('sp_token') && !localStorage.getItem('sp_token_local')){
  if(st.startsWith('local_')) localStorage.setItem('sp_token_local', st)
  else { localStorage.setItem('sp_token', st); const rt = sessionStorage.getItem('sp_refresh'); if(rt) localStorage.setItem('sp_refresh', rt) }
  sessionStorage.removeItem('sp_token'); sessionStorage.removeItem('sp_refresh'); sessionStorage.removeItem('sp_token_local')
}
// Restore CU from localStorage for instant session restore
const savedUser = localStorage.getItem('sp_user')
if(st && savedUser){
  try { CU = JSON.parse(savedUser) } catch {}
}
// Public legal routes (no login required)
const pathname = window.location.pathname
if(pathname === '/terms') { go('/legal/terms'); return }
if(pathname === '/privacy') { go('/legal/privacy'); return }
// Check for public alert route (works even when logged in)
const alertaMatch = pathname.match(/^\/alerta\/([a-zA-Z0-9]+)$/)
if(alertaMatch) { showPublicAlert(alertaMatch[1]); return }
// Check for confirmed page (external contact post-opt-in)
if(pathname === '/confirmed' && new URLSearchParams(window.location.search).get('token')) {
  showConfirmedPage()
  return
}
// Check for force-reset route (token in URL)
const forceResetMatch = pathname === '/force-reset'
if(forceResetMatch && new URLSearchParams(window.location.search).get('token')){
  showScr('forceReset')
  const tokenEl=document.getElementById('frToken') as HTMLInputElement
  if(tokenEl) tokenEl.value=new URLSearchParams(window.location.search).get('token')||''
  return
}

if(st){
  if(API_MODE && !st.startsWith('local_')){
    setToken(st)
    // Token vencido: renovarlo YA, en paralelo. Las peticiones del dashboard
    // esperan este refresh (ver req) en vez de recibir 401 cada una.
    if(tokenExpired(st)) refreshAccessToken()
    // Botón de pánico: la sesión cacheada se usa al instante. me() solo revalida
    // en segundo plano; un fallo de red o 5xx NUNCA cierra la sesión. Solo un
    // rechazo explícito de credenciales (401/403) la invalida.
    const authRejected = (r:any) => r?.status === 401 || r?.status === 403
    const dropSession = () => {
      clearToken(); localStorage.removeItem('sp_user'); CU=null
      disconnectAlertHub()
      document.getElementById('APP')!.style.display='none'
      showLanding()
    }
    api.me().then((r:any)=>{
      if(r.ok&&r.data&&r.data.success){
        CU=r.data.user
        localStorage.setItem('sp_user', JSON.stringify(r.data.user))
        if(r.data.token){
          setToken(r.data.token)
          localStorage.setItem('sp_refresh', r.data.refreshToken)
        }
        if(!document.getElementById('APP')?.style.display || document.getElementById('APP')!.style.display === 'none'){
          enterApp()
        } else {
          // Cached CU was used to enter — re-sync group selector with fresh user
          initGroupSelector().then(() => bindGroupSelector())
          if(API_MODE && currentGroupId()) connectAlertHub(currentGroupId())
        }
      } else if(authRejected(r) || (r?.ok && r.data && !r.data.success)) {
        // Credenciales rechazadas por el servidor — limpiar sesión
        dropSession()
      } else if(!CU) {
        // Sin red y sin usuario cacheado: no hay con qué entrar, pero se
        // conservan los tokens para reintentar en el próximo arranque.
        showLanding()
        toast('Sin conexión. No se pudo cargar tu cuenta.','wa')
      }
      // Fallo de red / 5xx con CU cacheado: se sigue en la app con la sesión local.
    }).catch(()=>{ if(!CU) showLanding() })
    // Entrar de inmediato con el usuario cacheado (sin esperar a la red)
    if(CU){ enterApp() }
  } else if(!API_MODE || st.startsWith('local_')){
    const d=db()!; const uid=st.replace('local_','')
    const su=d.users.find((u:any)=>u.id===uid&&u.active)
    if(su){CU=su;enterApp()}else{clearToken();showLanding()}
  } else {
    clearToken();showLanding()
  }
} else {
  const path = window.location.pathname
  const alertaMatch = path.match(/^\/alerta\/([a-zA-Z0-9]+)$/)
  if(alertaMatch) { showPublicAlert(alertaMatch[1]); }
  else if(path === '/login') showScr('login')
  else if(path === '/register') {
    const params = new URLSearchParams(window.location.search)
    if(params.get('invite')) sessionStorage.setItem('invite_token', params.get('invite')!)
    showScr('reg')
  }
  else if(path === '/forgot-password') showScr('forgot')
  else if(path === '/reset-password') {
    // El correo se recuerda al pedir el OTP: permite refrescar o entrar directo
    // al paso 2 sin volver a escribir el correo.
    const remembered = sessionStorage.getItem('sp_forgot_email') || ''
    if(remembered){ _forgotEmail = remembered; showScr('reset') }
    else { showScr('forgot'); toast('Ingresa tu correo para recibir el codigo','in') }
  }
  else showLanding()
  }
})()

// Escape key closes overlays
document.addEventListener('keydown',(e:KeyboardEvent)=>{
  if(e.key==='Escape'){
    document.getElementById('panicExp')!.classList.remove('show')
    document.querySelectorAll('.mo.show').forEach((m)=>(m as HTMLElement).classList.remove('show'))
  }
})

function renderSettings(){
  const area = document.getElementById('totpSetupArea') as HTMLElement
  const verifyArea = document.getElementById('totpVerifyArea') as HTMLElement
  const backupArea = document.getElementById('totpBackupArea') as HTMLElement

  if(!area) return

  async function loadTOTPStatus(){
    const st = await api.get2faStatus()
    const rp = await api.getRetentionPolicy()

    if(st.ok && st.data?.enabled){
      document.getElementById('totpStatus')!.textContent = '✅ Activo'
      ;(document.getElementById('totpStatus') as HTMLElement).style.color = '#0967dF'
      area.innerHTML = '<p>2FA está habilitado. Usa tu app autenticadora para generar códigos.</p>'
      verifyArea.style.display = 'none'
    } else {
      document.getElementById('totpStatus')!.textContent = 'Inactivo'
      ;(document.getElementById('totpStatus') as HTMLElement).style.color = '#ff6600'
      area.innerHTML = '<button class="btn btn-primary" onclick="setupTOTP()">Habilitar Autenticación de Dos Factores</button>'
      verifyArea.style.display = 'none'
    }

    if(rp.ok && rp.data?.policy){
      const days = rp.data.policy.alertRetentionDays ?? 365
      const pending = rp.data.pendingPurgeCount ?? 0
      document.getElementById('retentionInfo')!.innerHTML =
        '<p>Retención de alertas resueltas/canceladas: <strong>'+days+' días</strong></p>'+
        '<p>Purga automática: <strong>'+(rp.data.policy.autoPurgeEnabled ? 'Activada' : 'Desactivada')+'</strong></p>'+
        (pending ? '<p>Alertas pendientes de purga: <strong>'+pending+'</strong></p>' : '')+
        '<p>Última purga: '+(rp.data.policy.lastPurgeAt || 'Nunca')+'</p>'
    } else {
      document.getElementById('retentionInfo')!.innerHTML = '<p>Configuración de retención no disponible.</p>'
    }
  }

  loadTOTPStatus()

  window.setupTOTP = async function(){
    const r = await api.get2faSetup()
    if(r.ok && r.data?.qrCode){
      area.innerHTML = '<img src="'+r.data.qrCode+'" style="max-width:300px;border-radius:8px;" />' +
        '<p style="font-size:0.9em;color:#888;margin-top:0.5rem">Escanea el QR con tu app de autenticación (Google Authenticator, Authy, etc.)</p>'+
        '<p style="font-size:0.8em;color:#666;word-break:break-all">'+r.data.otpauthUrl+'</p>'
      verifyArea.style.display = 'block'
    } else {
      toast(r.data?.error || 'No se pudo generar QR','er')
    }
  }

  window.verifyTOTP = async function(){
    const code = (document.getElementById('totpCodeInput') as HTMLInputElement).value.trim()
    if(code.length !== 6){ toast('El código debe tener 6 dígitos','er'); return }
    const r = await api.verify2fa(code)
    if(r.ok && r.data?.success){
      toast('2FA habilitado exitosamente','ok')
      document.getElementById('totpSetupArea')!.innerHTML = '<p>✅ Autenticación de dos factores está activa</p>'
      verifyArea.style.display = 'none'

      // Show backup codes
      const bc = await api.get2faBackupCodes()
      if(bc.ok && bc.data?.backupCodes){
        document.getElementById('backupCodesDisplay')!.textContent = bc.data.backupCodes.join(', ')
        backupArea.style.display = 'block'
      }
    } else {
      toast(r.data?.error || 'Código inválido','er')
    }
  }

  window.showPurgeConfirm = async function(){
    if(!confirm('¿Estás seguro? Tu cuenta y TODOS tus datos serán eliminados permanentemente. Esta acción no se puede deshacer.')){ return }
    const r = await api.deleteAccount()
    if(r.ok && r.data?.success){ toast('Cuenta eliminada','ok'); logout() }
    else toast(r.data?.error || 'Error al eliminar cuenta','er')
  }
  window.requestComplianceReport = async function(){
    const r = await api.getComplianceReport()
    if(r.ok && r.data?.compliance){
      const c = r.data.compliance
      const text = 'Datos personales: '+JSON.stringify(c.personalData)+'\n\nRegistros almacenados: '+JSON.stringify(c.storedOn)
      alert(text)
      toast('Reporte de cumplimiento generado','ok')
    } else {
      toast('No se pudo generar el reporte','er')
    }
  }
}

// Prevent double tap zoom
let lt=0;
document.addEventListener('touchend',(e:TouchEvent)=>{const n=Date.now();if(n-lt<=300)e.preventDefault();lt=n},{passive:false})


declare const L: any;

// ===== PWA SW UPDATE PROMPT =====
// Con registerType='prompt' en vite.config.ts, el nuevo SW se instala
// silenciosamente y este handler recarga tras inactividad del usuario.
// Evita recargas sorpresivas que matan peticiones en curso (getDevices, etc).
document.addEventListener('swUpdated', ((e: any) => {
  const { updateSW } = e.detail || {}
  if (!updateSW) return
  let idle: ReturnType<typeof setTimeout> | null = null
  const reload = () => { updateSW().then(() => window.location.reload()) }
  const sched = () => { if(idle) clearTimeout(idle); idle = setTimeout(reload, 15000) }
  ;['click','touchstart','scroll','keydown'].forEach(evt =>
    window.addEventListener(evt, sched, { once: true })
  )
  sched()
}) as EventListener)

// ===== CAPITALIZE-ON-TYPE for name/description fields =====
function cap(val: string): string {
  return val.replace(/\b\w/g, c => c.toUpperCase())
}
;[...document.querySelectorAll<HTMLInputElement>('#rName,#rLast,#pName,#pLast,#ufN,#ufL,#cfN,#cfR,#ecfN,#ecfR,#dfN,#dfLoc,#gfN,#gfD,#pfN,#pfD,#pfF')].forEach(el => {
  el.addEventListener('input', function() {
    const s = this.selectionStart
    this.value = cap(this.value)
    this.setSelectionRange(s, s)
  })
})

declare global {
  interface Window {
    setupTOTP: () => Promise<void>
    verifyTOTP: () => Promise<void>
    showPurgeConfirm: () => Promise<void>
    requestComplianceReport: () => Promise<void>
  }
}
